import { measureMintStage } from '../lib/ordinal-mint-diagnostics'
import axios from 'axios'
import type { OperationTrace } from 'utils/operationDiagnostics'
import md5 from 'md5'
import { cloneDeep, omitBy } from 'lodash'
import { accessTokens } from 'api/accessTokenMgmt'
import { getGon, redirectToLoginScreen } from 'AppRoot'
import useStore, { DEBUG, DEBUG_MODE_CHANGED_EVENT, startingIndex } from 'state/knovStore'
import { createChannelJoinInvite } from 'state/channelJoinInvite'
import { BULK_ANSWERS_MAX_IDS, fetchAnswersInChunks } from 'api/bulkAnswerChunks'
import * as cache from 'state/cache'
import { Answer } from 'types/answers'
import type { PaymentReceipt } from 'types/paymentReceipt'
import * as Sentry from '@sentry/react'
import { QuestId } from 'state/PanelState'
import { QuestPerms } from 'types/quests'
import { getQuestsQueryKey, serializeFilterKey } from 'refactor/hooks/api/useGetQuests'
import queryClient from 'api/queryClient'
import { withDepth } from 'components/filters/useStreamFilters'
import { Quest } from 'types/quests'
import { cacheQuestPreservingPendingOptimisticAnswers } from 'state/pendingOptimisticAnswers'
import {
    createCoalescedGetAnswers,
    hydrateAnswersIntoQuestWithFetcher,
    hydrateQuestForQueryWithFetcher,
    hydrateStreamPreviewIntoQuestWithFetcher,
} from 'api/questHydration'
import {
    getPendingAnswerOutboxItemsForQuest,
    markPendingAnswerOutboxConfirmed,
    removePendingAnswerOutbox,
    shouldRemovePendingAnswerOutboxForStatus,
    upsertPendingAnswerOutbox,
} from 'state/pendingAnswerOutbox'
import {
    apiAgentModelConfigToCamel,
    type AgentModelConfig,
    type AgentModelConfigApi,
} from 'state/agentModelConfig'
import {
    annotateUploadError,
    buildSafeUploadFileMetadata,
    captureUploadFailureWithReporter,
    createUploadCancelledError,
    isUploadCancellationError,
    precheckUploadFile,
    probeFileRead,
    serverUploadRejectionMessage,
    UploadDiagnosticDetails,
    UploadDiagnosticsContext,
    UploadErrorWithDiagnostics,
} from './uploadDiagnostics'
import {
    getClientShare,
    getStoredClientSigningDeviceId,
    type UnsignedTransactionTemplate,
} from '../lib/bsv-signing'
import { recordPostAipIntent } from '../lib/post-aip-signing'
import { isInsertAnswerPlaceholder } from '../lib/insertAnswerPlaceholder'
import { assertSubmittableAnswerContent } from '../lib/submittableAnswer'
import { runInFlightRequest } from './inFlightRequestDedupe'
import { createReplyRequestDiagnostics } from 'utils/replySubmitDiagnosticsCore'
import type { ReplySubmitTrace, ReplyRequestDiagnostics } from 'utils/replySubmitDiagnosticsCore'
import { canonicalizeQuestFilter } from 'lib/questFilterCanonicalization'
import { prependQuestToNaturalFeedSession } from 'lib/naturalFeedSession'
import { AnswerTranslationResponse, translatedAnswerFromResponse } from './answerTranslation'
import { attachmentFieldForUploadedShare, type UploadedSharedFile } from 'utils/uploadedSharedFile'
import { completeMultipartAfterWorkers } from './multipartUploadRecovery'
import { isAndroid, isIOS, isMobile } from 'lib/device'
import type {
    OrdinalTransferManifest,
    OrdinalTransferSignature,
} from '../lib/ordinal-local-signing'
import type { OrdinalMintManifest, OrdinalMintSignature } from '../lib/ordinal-local-mint-signing'
import { prepareOrdinalMintIntent, type OrdinalMintMediaInfo } from '../lib/ordinal-mint-intent'
import {
    ownerAttachmentWriteMaxNetworkFee,
    saveOwnerAttachmentWriteIntent,
} from '../lib/owner-attachment-local-signing'
import {
    ordinalPrevoutScriptFromFramedStream,
    type OrdinalPrevoutScript,
} from '../lib/ordinal-prevout-signing'
import type {
    OrdinalMarketApproval,
    OrdinalMarketManifest,
    OrdinalMarketSignature,
} from '../lib/ordinal-local-market-signing'
import {
    clearProvisionalOrdinalMarketActionIntent,
    clearOrdinalMarketActionIntent,
    saveOrdinalMarketActionIntent,
    saveProvisionalOrdinalMarketActionIntent,
    type OrdinalMarketProvisionalActionIntent,
} from '../lib/ordinal-market-action-intent'
import { marketFeeSats, marketSellerProceedsSats } from '../lib/marketFee'
import {
    currentUserUsesClientSigning,
    requireClientSigningFeature,
    treechatSignsWithoutLocalKey,
} from '../lib/clientSigningFeature'
import { attachUploadedImageInputsToActionRequests } from 'utils/aiActionImageInputs'
import { shareRecentRequest } from 'lib/shareRecentRequest'
import { createIdBatcher } from 'lib/batchById'

/** Shape returned by the quests index endpoint (getQuests / getFeed). */
export interface GetQuestsResponse {
    quests: Quest[]
    count?: number
    following_count?: number
    has_more?: boolean
    pinned_quest?: Quest | null
    pinnedQuest?: Quest | null
}

/** Shape of a single page within the infinite quests query cache. */
interface QuestsInfinitePage {
    pinnedQuest?: Quest | null
    quests: { id: string }[]
    count?: number
    has_more?: boolean
}

/** Shape of the full infinite query data stored in the quests query cache. */
interface QuestsInfiniteData {
    pageParams: number[]
    pages: QuestsInfinitePage[]
}

function formatApiErrorData(data: unknown): string | null {
    if (!data || typeof data !== 'object') return null

    const payload = data as {
        error?: unknown
        message?: unknown
        errors?: unknown
    }

    if (typeof payload.error === 'string' && payload.error.trim()) return payload.error
    if (typeof payload.message === 'string' && payload.message.trim()) return payload.message

    const errors = payload.errors
    if (Array.isArray(errors)) {
        const messages = errors.filter(error => typeof error === 'string' && error.trim())
        return messages.length ? messages.join(' ') : null
    }

    if (errors && typeof errors === 'object') {
        const errorMap = errors as Record<string, unknown>
        const messages = Object.values(errorMap).flatMap(value => {
            if (Array.isArray(value)) return value.filter(item => typeof item === 'string')
            return typeof value === 'string' ? [value] : []
        })
        return messages.length ? messages.join(' ') : null
    }

    return null
}

function isResponseError(error: unknown): error is {
    response?: { data?: unknown; status?: number }
    message?: string
} {
    return Boolean(error && typeof error === 'object')
}

export function getReadableApiErrorMessage(error: unknown, fallback = 'Request failed'): string {
    if (isResponseError(error)) {
        return formatApiErrorData(error.response?.data) || error.message || fallback
    }

    if (error instanceof Error && error.message) return error.message
    if (typeof error === 'string' && error.trim()) return error

    return fallback
}

function buildApiError(message: string, status?: number, data?: unknown): Error {
    return Object.assign(new Error(message), { status, data })
}

// Names this browser's client-signing device, so device-signed work a request
// starts (e.g. a post's AIP author signature) goes to the device that acted.
const clientSigningDeviceHeaders = (): Record<string, string> => {
    const currentUserId = gon.currentUser?.id
    if (!currentUserId || !currentUserUsesClientSigning()) return {}

    const deviceId = getStoredClientSigningDeviceId(currentUserId)
    return deviceId ? { 'X-BSV-DEVICE-ID': deviceId } : {}
}

// Remembers a post this device wrote, so it will later sign that post's on-chain
// AIP author tuple (lib/post-aip-signing.ts) and nothing else.
const recordClientSigningPostIntent = (answerId: unknown, content: unknown) => {
    const currentUserId = gon.currentUser?.id
    if (!currentUserId || !currentUserUsesClientSigning()) return
    if (answerId == null || typeof content !== 'string') return

    recordPostAipIntent(String(currentUserId), String(answerId), content).catch(() => {})
}

const commonHeaders = () => {
    const headers: Record<string, string> = {
        'X-CSRF-TOKEN': gon.formToken,
        'X-CLIENT-VERSION': gon.clientVersion,
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...clientSigningDeviceHeaders(),
    }

    return headers
}

const QUERY_LEN = 0
const USE_LOCAL_CACHE = true // can be set to false for debugging
//

let requests = []
const pendingCreateQuestById = new Map<string, Promise<Quest | null>>()
const pendingCreateAnswerById = new Map<string, Promise<Answer | undefined>>()
const pendingAnswerDiagnostics = new WeakMap<Promise<Answer | undefined>, ReplyRequestDiagnostics>()
const pendingAnswerOutboxReplayIds = new Set<string>()

// TODO: investigate if this actually does anything. nothing appears to push
// into the requests aray
function abortQuestApiCall(controller) {
    const len = requests.length
    let ix = 0
    if (controller) requests.push(controller)
    while (ix < len) {
        let inProgController = requests.shift()
        inProgController.abort()
        ix = ix + 1
    }
}

function abort401(res) {
    // TODO: do something inside react when not signed in rather than redirecting
    //console.log('CHECK 401', res.status, res)
    if (res.status === 401) {
        //$('#auth-modal').modal('show')
        // Push current URL to history stack before redirecting
        // This allows users to use the back button to return here after login
        window.history.pushState(null, '', window.location.href)
        location.href = '/login'
        return true
    }
    return false
}

async function getAgentModelConfig(): Promise<AgentModelConfig | null> {
    try {
        const response = await fetch(`${window.knovApiUrl}/api/v1/agent_models`, {
            method: 'GET',
            redirect: 'follow',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
            },
        })

        if (!response.ok) {
            throw new Error(`agent_models returned ${response.status}`)
        }

        const payload = (await response.json()) as AgentModelConfigApi
        return apiAgentModelConfigToCamel(payload)
    } catch (error) {
        console.error('Failed to load agent model config:', error)
        return null
    }
}

function getQueryString(params) {
    const { activeSpaceId } = useStore.getState()
    let qs = []
    if (gon.currentUser && activeSpaceId) qs.push(`space_id=${activeSpaceId}`)

    if (params) {
        for (const key in params) {
            if (Array.isArray(params[key])) {
                params[key].forEach(value => {
                    if (key) qs.push(`${key}[]=${value}`)
                })
            } else {
                if (params[key]) qs.push(`${key}=${encodeURIComponent(params[key])}`)
            }
        }
    }

    return qs.length > 0 ? '?' + qs.join('&') : ''
}

const IDS_ONLY_ANSWER_PAYLOAD_PARAM = { answer_payload: 'ids' }
// `feed_card=v1` is the default shared-web stream contract. Older servers safely
// ignore the version marker while continuing to honor the ids-only payload.
const STREAM_FEED_CARD_PAYLOAD_PARAM = {
    ...IDS_ONLY_ANSWER_PAYLOAD_PARAM,
    feed_card: 'v1',
}

function userViewsQuest(questId) {
    userQuestAction(questId, 'view')
}

function userViewsBranch(quest) {
    userQuestAction(quest.id, 'view_branch')
}

// Active Storage Direct Upload helpers
const MULTIPART_MIN_BYTES_DEFAULT = 50 * 1024 * 1024 // 50MB default threshold for multipart (desktop)

function isMobileEnv(): boolean {
    if (isMobile) return true

    try {
        const ua = navigator.userAgent || ''
        const hasTouch =
            typeof window !== 'undefined' &&
            ('ontouchstart' in window || navigator.maxTouchPoints > 0)
        const isIOS = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && hasTouch)
        const isAndroid = /Android/.test(ua)
        return isIOS || isAndroid
    } catch {
        return false
    }
}

function getMultipartThresholdBytes(file?: File): number {
    // On mobile browsers, avoid MD5 entirely to prevent OOM crashes from arrayBuffer
    if (isMobileEnv()) return 0
    return MULTIPART_MIN_BYTES_DEFAULT
}

function getUploadNetworkContext(): UploadDiagnosticDetails {
    try {
        const nav =
            typeof navigator !== 'undefined'
                ? (navigator as Navigator & {
                      connection?: {
                          effectiveType?: string
                          downlink?: number
                          rtt?: number
                          saveData?: boolean
                      }
                  })
                : undefined
        const connection = nav?.connection

        return {
            online: nav?.onLine,
            user_agent: nav?.userAgent,
            effective_type: connection?.effectiveType,
            downlink_mbps: connection?.downlink,
            rtt_ms: connection?.rtt,
            save_data: connection?.saveData,
            is_mobile_env: isMobileEnv(),
        }
    } catch {
        return { is_mobile_env: isMobileEnv() }
    }
}

function sanitizeUploadUrl(url?: string): UploadDiagnosticDetails {
    if (!url) return {}
    try {
        const parsed = new URL(url)
        return {
            request_host: parsed.host,
            request_protocol: parsed.protocol.replace(':', ''),
        }
    } catch {
        return {}
    }
}

function buildUploadContext(
    file: File,
    diagnostics: UploadDiagnosticsContext = {},
): UploadDiagnosticDetails {
    return {
        answer_id: diagnostics.answerId ?? undefined,
        upload_kind: diagnostics.uploadKind ?? 'file',
        upload_source: diagnostics.source,
        file_index: diagnostics.fileIndex,
        total_files: diagnostics.totalFiles,
        ...buildSafeUploadFileMetadata(file),
        multipart_threshold_bytes: getMultipartThresholdBytes(file),
        ...getUploadNetworkContext(),
    }
}

export function addUploadBreadcrumb(message: string, data: UploadDiagnosticDetails = {}) {
    Sentry.addBreadcrumb({
        category: 'answer-upload',
        level: 'info',
        message,
        data,
    })
}

// Some phones never reach Sentry (an ad-blocking DNS, for one); our own API
// always does, and its log reaches Graylog as an [upload_failure] line.
export function reportUploadDiagnostics(
    kind: 'client_failure' | 'page_unloaded',
    details: UploadDiagnosticDetails = {},
) {
    try {
        const webCommit = (window as unknown as { Knov?: { version?: { gitCommitHash?: string } } })
            .Knov?.version?.gitCommitHash
        void fetch(`${window.knovApiUrl}/api/v1/upload_diagnostics`, {
            method: 'POST',
            headers: { ...accessTokens(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
                ...details,
                kind,
                platform: isAndroid ? 'android' : isIOS ? 'ios' : undefined,
                native_shell: !!(window as unknown as { ReactNativeWebView?: unknown })
                    .ReactNativeWebView,
                web_commit: webCommit,
            }),
            keepalive: true,
        }).catch(() => {})
    } catch {
        // Reporting must never affect uploading.
    }
}

export function captureUploadFailure(
    error: unknown,
    details: UploadDiagnosticDetails = {},
): UploadErrorWithDiagnostics {
    const err = captureUploadFailureWithReporter(
        {
            addBreadcrumb: (message, breadcrumbDetails) => {
                addUploadBreadcrumb(message, breadcrumbDetails)
            },
            captureException: failure => {
                const diagnostics = (failure as UploadErrorWithDiagnostics).uploadDiagnostics || {}
                reportUploadDiagnostics('client_failure', {
                    ...diagnostics,
                    error_name: failure.name,
                    error_message: failure.message,
                })
                // A refused file (empty, over the limit) is the user's to fix, not an app error.
                if (diagnostics.stage === 'precheck') return

                Sentry.withScope(scope => {
                    scope.setTag('feature', 'answer-upload')
                    if (diagnostics.upload_mode)
                        scope.setTag('upload_mode', String(diagnostics.upload_mode))
                    if (diagnostics.stage) scope.setTag('upload_stage', String(diagnostics.stage))
                    scope.setExtras(diagnostics)
                    Sentry.captureException(failure)
                })
            },
        },
        error,
        details,
    )

    return err
}

export { buildSafeUploadFileMetadata, isUploadCancellationError }

async function md5Base64(file: File): Promise<string> {
    const t0 = performance.now()
    const buf = await file.arrayBuffer()
    const hex = md5(new Uint8Array(buf)) // hex string
    const bytes =
        hex
            .match(/.{1,2}/g)
            ?.map(h => String.fromCharCode(parseInt(h, 16)))
            .join('') || ''
    const out = btoa(bytes)
    return out
}

type DirectUploadResult = {
    signed_id: string
    direct_upload: { url: string; headers: Record<string, string> }
}

async function requestDirectUpload(
    file: File,
    diagnostics: UploadDiagnosticsContext = {},
): Promise<DirectUploadResult> {
    const context = buildUploadContext(file, diagnostics)
    addUploadBreadcrumb('direct_upload_init_start', {
        ...context,
        upload_mode: 'direct',
        stage: 'init',
    })
    let checksum: string
    try {
        checksum = await md5Base64(file)
    } catch (error) {
        throw annotateUploadError(error, {
            ...context,
            upload_mode: 'direct',
            stage: 'checksum',
        })
    }
    const startedAt = performance.now()
    const resp = await fetch(`${window.knovApiUrl}/rails/active_storage/direct_uploads`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': gon.formToken,
        },
        body: JSON.stringify({
            blob: {
                filename: file.name,
                content_type: file.type || 'application/octet-stream',
                byte_size: file.size,
                checksum: checksum,
            },
        }),
    })

    if (!resp.ok) {
        const text = await resp.text().catch(() => '')
        throw annotateUploadError(
            new Error(`direct upload init failed (${resp.status}): ${text}`),
            {
                ...context,
                upload_mode: 'direct',
                stage: 'init',
                fetch_status: resp.status,
                elapsed_ms: Math.round(performance.now() - startedAt),
            },
        )
    }
    const json = (await resp.json()) as DirectUploadResult
    addUploadBreadcrumb('direct_upload_init_success', {
        ...context,
        upload_mode: 'direct',
        stage: 'init',
        fetch_status: resp.status,
        elapsed_ms: Math.round(performance.now() - startedAt),
        ...sanitizeUploadUrl(json?.direct_upload?.url),
    })
    return json
}

async function putFileToService(
    url: string,
    headers: Record<string, string>,
    file: File,
    onProgress?: (pct: number) => void,
    registerCancel?: (fn: () => void) => void,
    diagnostics: UploadDiagnosticsContext = {},
) {
    const context = {
        ...buildUploadContext(file, diagnostics),
        upload_mode: 'direct',
        stage: 'put',
        ...sanitizeUploadUrl(url),
    }
    addUploadBreadcrumb('direct_upload_put_start', context)
    // Use XHR to get upload progress
    await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        let lastLogged = 0
        let cancelled = false
        xhr.open('PUT', url, true)
        try {
            registerCancel &&
                registerCancel(() => {
                    try {
                        cancelled = true
                        xhr.abort()
                    } catch {}
                })
        } catch {}
        Object.entries(headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v as string))
        xhr.upload.onprogress = e => {
            if (e.lengthComputable) {
                const pct = Math.floor((e.loaded / e.total) * 100)
                if (pct >= lastLogged + 5 || pct === 100) {
                    lastLogged = pct
                    try {
                        onProgress && onProgress(pct)
                    } catch {}
                }
            } else {
            }
        }
        xhr.onerror = () => {
            if (cancelled) {
                reject(createUploadCancelledError(context))
                return
            }
            reject(annotateUploadError(new Error('direct upload PUT network error'), context))
        }
        xhr.onabort = () => {
            reject(createUploadCancelledError(context))
        }
        xhr.onload = () => {
            if (cancelled) {
                reject(createUploadCancelledError(context))
                return
            }
            if (xhr.status >= 200 && xhr.status < 300) {
                addUploadBreadcrumb('direct_upload_put_success', {
                    ...context,
                    xhr_status: xhr.status,
                })
                resolve()
            } else {
                reject(
                    annotateUploadError(
                        new Error(`direct upload PUT failed (${xhr.status}): ${xhr.responseText}`),
                        {
                            ...context,
                            xhr_status: xhr.status,
                        },
                    ),
                )
            }
        }
        xhr.send(file)
    })
}

async function directUploadFile(
    file: File,
    onProgress?: (pct: number) => void,
    registerCancel?: (fn: () => void) => void,
    diagnostics: UploadDiagnosticsContext = {},
): Promise<string> {
    const probe = file.size === 0 ? await probeFileRead(file) : undefined
    const rejected = precheckUploadFile(
        file,
        typeof gon !== 'undefined' ? gon.uploadMaxBytes : undefined,
        probe,
    )
    if (rejected) throw annotateUploadError(rejected, buildUploadContext(file, diagnostics))
    const threshold = getMultipartThresholdBytes(file)
    if (file.size >= threshold) {
        addUploadBreadcrumb('upload_mode_selected', {
            ...buildUploadContext(file, diagnostics),
            upload_mode: 'multipart',
            stage: 'start',
        })
        return await multipartUploadFile(file, onProgress, registerCancel, diagnostics)
    }
    const t0 = performance.now()
    addUploadBreadcrumb('upload_mode_selected', {
        ...buildUploadContext(file, diagnostics),
        upload_mode: 'direct',
        stage: 'start',
    })
    const init = await requestDirectUpload(file, diagnostics)
    const t1 = performance.now()
    // Build a canceler that aborts the PUT and purges the precreated blob
    let aborter: (() => void) | null = null
    const registerComposite = (fn: () => void) => {
        aborter = fn
        if (registerCancel) {
            registerCancel(async () => {
                try {
                    aborter && aborter()
                } catch {}
                try {
                    await purgeBlob(init.signed_id)
                } catch {}
            })
        }
    }
    await putFileToService(
        init.direct_upload.url,
        init.direct_upload.headers,
        file,
        onProgress,
        registerComposite,
        diagnostics,
    )
    const t2 = performance.now()
    addUploadBreadcrumb('direct_upload_complete', {
        ...buildUploadContext(file, diagnostics),
        upload_mode: 'direct',
        stage: 'complete',
        init_elapsed_ms: Math.round(t1 - t0),
        put_elapsed_ms: Math.round(t2 - t1),
        total_elapsed_ms: Math.round(t2 - t0),
    })
    return init.signed_id
}

async function directUploadFiles(
    files: (File | string)[] | null | undefined,
    onProgress?: (pct: number) => void,
    registerCancel?: (fn: () => void) => void,
    diagnostics: UploadDiagnosticsContext = {},
): Promise<string[]> {
    if (!files || files.length === 0) return []
    // If already signed IDs were provided, return as-is
    if (typeof files[0] === 'string') return files as string[]
    return await Promise.all(
        (files as File[]).map((f, index) =>
            directUploadFile(f, onProgress, registerCancel, {
                ...diagnostics,
                fileIndex: index,
                totalFiles: files.length,
            }),
        ),
    )
}

// Multipart upload helpers
type MultipartStart = {
    upload_id: string
    key: string
    bucket: string
    region: string
    part_size: number
}

type MultipartPart = { part_number: number; etag: string }

async function multipartStart(
    file: File,
    diagnostics: UploadDiagnosticsContext = {},
): Promise<MultipartStart> {
    const context = buildUploadContext(file, diagnostics)
    addUploadBreadcrumb('multipart_start_request', {
        ...context,
        upload_mode: 'multipart',
        stage: 'start',
    })
    const startedAt = performance.now()
    const resp = await fetch(`${window.knovApiUrl}/api/v1/uploads/multipart/start`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': gon.formToken,
        },
        body: JSON.stringify({
            filename: file.name,
            content_type: file.type || 'application/octet-stream',
            byte_size: file.size,
        }),
    })
    if (!resp.ok) {
        const text = await resp.text().catch(() => '')
        const error = annotateUploadError(new Error(`multipart start failed (${resp.status})`), {
            ...context,
            upload_mode: 'multipart',
            stage: 'start',
            fetch_status: resp.status,
            elapsed_ms: Math.round(performance.now() - startedAt),
            response_text_length: text.length,
        })
        error.userMessage = serverUploadRejectionMessage(resp.status, text)
        throw error
    }
    const json = (await resp.json()) as MultipartStart
    addUploadBreadcrumb('multipart_start_success', {
        ...context,
        upload_mode: 'multipart',
        stage: 'start',
        fetch_status: resp.status,
        elapsed_ms: Math.round(performance.now() - startedAt),
        part_size: json.part_size,
    })
    return json
}

async function multipartPartUrl(
    uploadId: string,
    key: string,
    partNumber: number,
    file: File,
    diagnostics: UploadDiagnosticsContext = {},
): Promise<{ url: string; headers: Record<string, string> }> {
    const context = {
        ...buildUploadContext(file, diagnostics),
        upload_mode: 'multipart',
        stage: 'part_url',
        part_number: partNumber,
    }
    addUploadBreadcrumb('multipart_part_url_request', context)
    const qs = new URLSearchParams({
        upload_id: uploadId,
        key: key,
        part_number: String(partNumber),
    })
    const resp = await fetch(`${window.knovApiUrl}/api/v1/uploads/multipart/part?${qs}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { ...accessTokens(), Accept: 'application/json' },
    })
    if (!resp.ok) {
        const text = await resp.text().catch(() => '')
        throw annotateUploadError(new Error(`part url failed (${resp.status})`), {
            ...context,
            fetch_status: resp.status,
            response_text_length: text.length,
        })
    }
    const out = (await resp.json()) as { url: string; headers: Record<string, string> }
    addUploadBreadcrumb('multipart_part_url_success', {
        ...context,
        fetch_status: resp.status,
        ...sanitizeUploadUrl(out?.url),
    })
    return out
}

async function multipartComplete(
    uploadId: string,
    key: string,
    parts: MultipartPart[],
    expectedParts: number,
    file: File,
    diagnostics: UploadDiagnosticsContext = {},
): Promise<string> {
    const context = {
        ...buildUploadContext(file, diagnostics),
        upload_mode: 'multipart',
        stage: 'complete',
        uploaded_parts: parts.length,
    }
    addUploadBreadcrumb('multipart_complete_request', context)
    const startedAt = performance.now()
    const resp = await fetch(`${window.knovApiUrl}/api/v1/uploads/multipart/complete`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': gon.formToken,
        },
        body: JSON.stringify({
            upload_id: uploadId,
            key,
            parts,
            filename: file.name,
            content_type: file.type || 'application/octet-stream',
            byte_size: file.size,
            expected_parts: expectedParts,
        }),
    })
    if (!resp.ok) {
        const text = await resp.text().catch(() => '')
        throw annotateUploadError(new Error(`multipart complete failed (${resp.status})`), {
            ...context,
            fetch_status: resp.status,
            elapsed_ms: Math.round(performance.now() - startedAt),
            response_text_length: text.length,
        })
    }
    const json = (await resp.json()) as { signed_id: string }
    addUploadBreadcrumb('multipart_complete_success', {
        ...context,
        fetch_status: resp.status,
        elapsed_ms: Math.round(performance.now() - startedAt),
    })
    return json.signed_id
}

async function multipartCompleteWithRetry(
    uploadId: string,
    key: string,
    parts: MultipartPart[],
    expectedParts: number,
    file: File,
    diagnostics: UploadDiagnosticsContext = {},
): Promise<string> {
    const MAX_ATTEMPTS = 3
    let lastError: unknown

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
            return await multipartComplete(uploadId, key, parts, expectedParts, file, diagnostics)
        } catch (error) {
            lastError = error
            if (attempt === MAX_ATTEMPTS) break

            const backoff = 300 * attempt
            addUploadBreadcrumb('multipart_complete_retry', {
                ...buildUploadContext(file, diagnostics),
                upload_mode: 'multipart',
                stage: 'complete',
                attempt,
                next_backoff_ms: backoff,
            })
            await new Promise(resolve => setTimeout(resolve, backoff))
        }
    }

    throw lastError
}

async function multipartAbort(uploadId: string, key: string): Promise<void> {
    const resp = await fetch(`${window.knovApiUrl}/api/v1/uploads/multipart/abort`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': gon.formToken,
        },
        body: JSON.stringify({ upload_id: uploadId, key }),
    })

    if (!resp.ok) throw new Error(`multipart abort failed (${resp.status})`)
}

async function multipartUploadFile(
    file: File,
    onProgress?: (pct: number) => void,
    registerCancel?: (fn: () => void) => void,
    diagnostics: UploadDiagnosticsContext = {},
): Promise<string> {
    const start = await multipartStart(file, diagnostics)
    // Adjust part size on mobile to minimize memory spikes
    const suggested = start.part_size || 10 * 1024 * 1024
    const partSize = isMobileEnv()
        ? Math.max(5 * 1024 * 1024, Math.min(suggested, 6 * 1024 * 1024))
        : suggested
    const totalParts = Math.ceil(file.size / partSize)
    const CONCURRENCY = getUploadConcurrency()

    // Build part descriptors
    const partDescs = Array.from({ length: totalParts }, (_, i) => {
        const partNumber = i + 1
        const begin = i * partSize
        const end = Math.min(begin + partSize, file.size)
        return { partNumber, begin, end }
    })

    // Track aggregated progress
    const loadedByPart: Record<number, number> = {}
    let lastLoggedPct = -1
    const totalBytes = file.size
    const logAggregated = () => {
        const loaded = Object.values(loadedByPart).reduce((a, b) => a + b, 0)
        const pct = Math.floor((loaded / totalBytes) * 100)
        if (pct >= lastLoggedPct + 5 || pct === 100) {
            lastLoggedPct = pct
            try {
                onProgress && onProgress(pct)
            } catch {}
        }
    }

    const parts: MultipartPart[] = []

    async function uploadSinglePart(desc: { partNumber: number; begin: number; end: number }) {
        const { partNumber, begin, end } = desc
        const blob = file.slice(begin, end)
        // Retry up to 3 times for transient errors
        const MAX_ATTEMPTS = 3
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
            try {
                const { url, headers } = await multipartPartUrl(
                    start.upload_id,
                    start.key,
                    partNumber,
                    file,
                    diagnostics,
                )
                addUploadBreadcrumb('multipart_part_put_start', {
                    ...buildUploadContext(file, diagnostics),
                    upload_mode: 'multipart',
                    stage: 'part_put',
                    part_number: partNumber,
                    total_parts: totalParts,
                    attempt,
                    ...sanitizeUploadUrl(url),
                })
                await new Promise<void>((resolve, reject) => {
                    const xhr = new XMLHttpRequest()
                    let cancelled = false
                    xhr.open('PUT', url, true)

                    try {
                        registerCancel &&
                            registerCancel(() => {
                                try {
                                    cancelled = true
                                    xhr.abort()
                                } catch {}
                            })
                    } catch {}
                    Object.entries(headers || {}).forEach(([k, v]) =>
                        xhr.setRequestHeader(k, v as string),
                    )
                    xhr.upload.onprogress = e => {
                        if (e.lengthComputable) {
                            loadedByPart[partNumber] = e.loaded
                            logAggregated()
                        }
                    }
                    xhr.onerror = () => {
                        if (cancelled) {
                            reject(
                                createUploadCancelledError({
                                    ...buildUploadContext(file, diagnostics),
                                    upload_mode: 'multipart',
                                    stage: 'part_put',
                                    part_number: partNumber,
                                    total_parts: totalParts,
                                    attempt,
                                    ...sanitizeUploadUrl(url),
                                }),
                            )
                            return
                        }
                        reject(
                            annotateUploadError(
                                new Error(`multipart PUT network error (part ${partNumber})`),
                                {
                                    ...buildUploadContext(file, diagnostics),
                                    upload_mode: 'multipart',
                                    stage: 'part_put',
                                    part_number: partNumber,
                                    total_parts: totalParts,
                                    attempt,
                                    ...sanitizeUploadUrl(url),
                                },
                            ),
                        )
                    }
                    xhr.onabort = () => {
                        reject(
                            createUploadCancelledError({
                                ...buildUploadContext(file, diagnostics),
                                upload_mode: 'multipart',
                                stage: 'part_put',
                                part_number: partNumber,
                                total_parts: totalParts,
                                attempt,
                                ...sanitizeUploadUrl(url),
                            }),
                        )
                    }
                    xhr.onload = () => {
                        if (cancelled) {
                            reject(
                                createUploadCancelledError({
                                    ...buildUploadContext(file, diagnostics),
                                    upload_mode: 'multipart',
                                    stage: 'part_put',
                                    part_number: partNumber,
                                    total_parts: totalParts,
                                    attempt,
                                    ...sanitizeUploadUrl(url),
                                }),
                            )
                            return
                        }
                        if (xhr.status >= 200 && xhr.status < 300) {
                            loadedByPart[partNumber] = end - begin
                            parts.push({ part_number: partNumber, etag: '' })
                            addUploadBreadcrumb('multipart_part_put_success', {
                                ...buildUploadContext(file, diagnostics),
                                upload_mode: 'multipart',
                                stage: 'part_put',
                                part_number: partNumber,
                                total_parts: totalParts,
                                attempt,
                                xhr_status: xhr.status,
                            })
                            resolve()
                        } else {
                            reject(
                                annotateUploadError(
                                    new Error(`multipart PUT failed (${xhr.status})`),
                                    {
                                        ...buildUploadContext(file, diagnostics),
                                        upload_mode: 'multipart',
                                        stage: 'part_put',
                                        part_number: partNumber,
                                        total_parts: totalParts,
                                        attempt,
                                        xhr_status: xhr.status,
                                        ...sanitizeUploadUrl(url),
                                    },
                                ),
                            )
                        }
                    }
                    xhr.send(blob)
                })
                return // success
            } catch (err) {
                if (isUploadCancellationError(err)) {
                    throw err
                }
                const backoff = Math.min(2000 * attempt, 5000)

                if (attempt === MAX_ATTEMPTS) {
                    throw err
                }
                addUploadBreadcrumb('multipart_part_retry', {
                    ...buildUploadContext(file, diagnostics),
                    upload_mode: 'multipart',
                    stage: 'part_put',
                    part_number: partNumber,
                    total_parts: totalParts,
                    attempt,
                    next_backoff_ms: backoff,
                })
                await new Promise(res => setTimeout(res, backoff))
            }
        }
    }

    // Simple promise pool
    let idx = 0
    const workers = Array.from({ length: Math.min(CONCURRENCY, partDescs.length) }, async () => {
        while (idx < partDescs.length) {
            const cur = partDescs[idx++]
            await uploadSinglePart(cur)
        }
    })
    return await completeMultipartAfterWorkers({
        workers,
        complete: async recovering => {
            if (recovering) {
                addUploadBreadcrumb('multipart_recovery_probe', {
                    ...buildUploadContext(file, diagnostics),
                    upload_mode: 'multipart',
                    stage: 'complete',
                    expected_parts: totalParts,
                    acknowledged_parts: parts.length,
                })
            }
            return multipartCompleteWithRetry(
                start.upload_id,
                start.key,
                parts,
                totalParts,
                file,
                diagnostics,
            )
        },
        abort: () => multipartAbort(start.upload_id, start.key),
        isRecoverableFailure: error => !isUploadCancellationError(error),
    })
}

// Detect a sensible concurrency for the current device/browser.
// - iOS Safari/WebView: 2 (aggressive parallelism can stall)
// - Android Chrome/WebView: 3
// - Desktop: 4
function getUploadConcurrency(): number {
    try {
        const ua = navigator.userAgent || ''
        const hasTouch =
            typeof window !== 'undefined' &&
            ('ontouchstart' in window || navigator.maxTouchPoints > 0)
        const isIOS = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && hasTouch)
        const isAndroid = /Android/.test(ua)
        const isWebView =
            /; wv\)/.test(ua) || !!window.ReactNativeWebView || (isIOS && !/Safari/.test(ua))
        if (isIOS) return 1 // keep memory pressure low on iOS
        if (isAndroid || isMobile) return 2
        return 4
    } catch {
        return 4
    }
}

async function userSearchAction(query, filter) {
    if (gon.currentUser) {
        const resp = await fetch(`${gon.api.quests}/search_actions`, {
            method: 'post',
            credentials: 'same-origin',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-Token': gon.formToken,
            },
            body: JSON.stringify({
                query,
                filter,
            }),
        })
        const data = await resp.json()
        // HACK We pretend searches are quests.
        return data?.quest
    }
}

function updateUserOptions(options) {
    if (gon.currentUser) {
        return fetch(`${gon.api.options}`, {
            method: 'post',
            credentials: 'same-origin',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-Token': gon.formToken,
            },
            body: JSON.stringify(options),
        })
    }
}

export async function updateUserSpaceOptions(options) {
    if (gon.currentUser) {
        const userId = gon.currentUser.id
        // activeSpaceId can be null during early boot / route loads; fall back to user's current space_id
        const spaceId = useStore.getState().activeSpaceId || gon.currentUser.space_id

        // Keep gon in sync optimistically so newly-opened panels/components
        // can read from `gon.currentUser.space_options` immediately. Store a
        // copy: callers may hand in store state, which immer freezes or revokes.
        try {
            gon.currentUser.space_options = {
                ...(gon.currentUser.space_options || {}),
                ...JSON.parse(JSON.stringify(options || {})),
            }
        } catch {}

        return fetch(`${gon.api.options}/space`, {
            method: 'PATCH',
            credentials: 'same-origin',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-Token': gon.formToken,
            },
            body: JSON.stringify({
                user_id: userId,
                space_id: spaceId,
                options: options,
            }),
        })
    }
}

// Pre-upload attachments (files and/or recording) for an Answer-like object.
// Returns an object with signed IDs that can be passed to createAnswer/createQuest
// without re-uploading.
export async function preuploadAnswerMedia(
    input: {
        files?: (File | string)[] | null
        recording?: File | string | null
    },
    onProgress?: (pct: number) => void,
    registerCancel?: (fn: () => void) => void,
    diagnostics: UploadDiagnosticsContext = {},
): Promise<{ files?: string[]; recording?: string }> {
    const out: { files?: string[]; recording?: string } = {}
    if (input.files && input.files.length) {
        out.files = await directUploadFiles(input.files, onProgress, registerCancel, diagnostics)
    }
    if (input.recording) {
        out.recording =
            typeof input.recording === 'string'
                ? input.recording
                : await directUploadFile(input.recording as File, onProgress, registerCancel, {
                      ...diagnostics,
                      uploadKind: diagnostics.uploadKind || 'recording',
                  })
    }
    return out
}

export async function preuploadAnswerImage(
    answer: Pick<
        Partial<Answer>,
        | 'pending_images'
        | 'local_image_urls'
        | 'answer_image'
        | 'answer_image_url'
        | 'local_answer_image_url'
    >,
    diagnostics: UploadDiagnosticsContext = {},
): Promise<string | null> {
    const images = await preuploadAnswerImages(answer, diagnostics)
    return images[0] || null
}

export async function preuploadAnswerImages(
    answer: Pick<
        Partial<Answer>,
        | 'pending_images'
        | 'local_image_urls'
        | 'answer_image'
        | 'answer_image_url'
        | 'local_answer_image_url'
    >,
    diagnostics: UploadDiagnosticsContext = {},
): Promise<string[]> {
    const images = await resolveAnswerImagesForUpload(answer)

    return Promise.all(
        images.map((image, index) => {
            if (typeof image === 'string') return image
            return directUploadFile(image, undefined, undefined, {
                ...diagnostics,
                uploadKind: 'image',
                fileIndex: index,
                totalFiles: images.length,
            })
        }),
    )
}

async function purgeBlob(signedId: string) {
    try {
        const resp = await fetch(`${window.knovApiUrl}/api/v1/uploads/purge`, {
            method: 'POST',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-Token': gon.formToken,
            },
            body: JSON.stringify({ signed_id: signedId }),
        })
        if (!resp.ok) {
            const text = await resp.text().catch(() => '')
        }
    } catch (e) {}
}

// Exported helper for UI to purge a pre-uploaded ActiveStorage blob by signed_id
export async function purgePreuploadedBlob(signedId: string) {
    return purgeBlob(signedId)
}

// Home preference: which stream the Home button opens.
export async function setDefaultHomeStream(streamType: 'public' | 'following') {
    if (gon?.currentUser) {
        gon.currentUser.space_options = {
            ...(gon.currentUser.space_options || {}),
            defaultHomeStream: streamType,
        }
    }
    return updateUserSpaceOptions({ defaultHomeStream: streamType })
}

// Click-time prompt crafting for the composer's Illustrate/Film actions. Bills the current
// user for the LLM call; the caller falls back to its template prompt on any rejection.
export async function craftPostMediaPrompt(payload: {
    kind: 'illustrate' | 'film'
    post_text: string
    action_tag?: string | null
    duration_seconds?: number | null
    has_media_inputs?: boolean
    media_inputs?: Array<{
        kind: 'image' | 'video' | 'audio'
        label?: string
        content_type?: string
    }>
}): Promise<{
    prompt: string
    core_message?: string
    human_dimension?: string
    visual_concept?: string
    beats?: Array<{ timing: string; action: string }>
    source_fidelity_mode?: 'semantic' | 'visual'
    visual_prompt?: string
    must_preserve?: string[]
    negative_prompt?: string | null
    kind?: string
    action_tag?: string
    craft_model?: string
    ai_run_id?: string
}> {
    const response = await fetch(`${window.knovApiUrl}/api/v1/ai/prompt_crafts`, {
        method: 'post',
        credentials: 'same-origin',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': gon.formToken,
        },
        body: JSON.stringify(payload),
    })

    const data = await response.json().catch(() => null)
    if (!response.ok || !data?.prompt) {
        const error = new Error(data?.error || `Prompt crafting failed (${response.status})`)
        ;(error as Error & { status?: number }).status = response.status
        throw error
    }
    return data
}

// Fire-and-forget record of a media action (media_play / add_to_playlist)
// against an answer, so plays and playlist adds are indexable server-side.
export function recordAnswerAction(answerId, name, meta = {}) {
    if (!gon.currentUser || !answerId) return

    try {
        fetch(`${gon.api.answers}/${answerId}/actions`, {
            method: 'post',
            credentials: 'same-origin',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-Token': gon.formToken,
            },
            body: JSON.stringify({ name, meta }),
        }).catch(() => {})
    } catch {}
}

function userQuestAction(questId, action) {
    if (gon.currentUser) {
        fetch(`${gon.api.quests}/${questId}/actions`, {
            method: 'post',
            credentials: 'same-origin',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-Token': gon.formToken,
            },
            body: JSON.stringify({
                name: action,
            }),
        })
    }
}

async function actOnNoti(questId, answerId) {
    if (gon.currentUser) {
        return fetch(`${gon.api.notifications}/actions`, {
            method: 'post',
            credentials: 'same-origin',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-Token': gon.formToken,
            },
            body: JSON.stringify({
                quest_id: questId,
                answer_id: answerId,
            }),
        })
    }
    return null
}

// Shows the server's channel join prompt (state/channelJoinInvite), if any.
export const offerChannelJoinInvite = createChannelJoinInvite({
    isMember: teamId =>
        (gon.currentUser?.space_teams || []).some(team => String(team.id) === teamId) ||
        (useStore.getState().userStreams || []).some(team => String(team.id) === teamId),
    show: invite => useStore.getState().set({ channelJoinInvite: invite }),
})

async function createQuest(quest, diagnostic?: ReplySubmitTrace | null) {
    diagnostic?.apiBegin()
    try {
        return await performCreateQuest(quest, diagnostic)
    } finally {
        diagnostic?.finish()
    }
}

async function performCreateQuest(quest, diagnostic?: ReplySubmitTrace | null) {
    diagnostic?.crumb('create_quest_form_begin')
    const { activeSpaceId, getSpaceId } = useStore.getState()
    const resolvedSpaceId = activeSpaceId || getSpaceId?.()
    let rootQuest

    if (quest && quest.parent && resolvedSpaceId) {
        const form = new FormData()
        form.set('id', quest.id)
        form.set('space_id', resolvedSpaceId)

        if (quest.team_id) form.set('team_id', quest.team_id)
        if (quest.private) form.set('private', quest.private)
        if (quest.public) form.set('public', quest.public)
        if (quest.blog) form.set('blog', quest.blog)
        if (quest.replies_enabled != null)
            form.set('replies_enabled', String(quest.replies_enabled))
        if (quest.members_only_replies != null)
            form.set('members_only_replies', String(quest.members_only_replies))
        if (quest.default_sort_col) form.set('default_sort_col', quest.default_sort_col)
        if (quest.default_sort_dir) form.set('default_sort_dir', quest.default_sort_dir)
        if (quest.thread_type) form.set('thread_type', quest.thread_type)
        if (quest.created_at) form.set('created_at', quest.updated_at)
        if (quest.updated_at) form.set('updated_at', quest.updated_at)

        const parent = quest.parent
        if (parent.payment_ids?.length)
            form.set('parent_attributes[payment_ids]', JSON.stringify(parent.payment_ids))
        const parentActionRequests = parent.action_requests
        form.set('parent_attributes[id]', parent.id)
        form.set('parent_attributes[content]', parent.content)
        recordClientSigningPostIntent(parent.id, parent.content)
        if (parent.delta_json)
            form.set('parent_attributes[delta_json]', JSON.stringify(parent.delta_json))
        if (Array.isArray(parentActionRequests))
            form.set('parent_attributes[action_requests]', JSON.stringify(parentActionRequests))
        if (parent.is_music === true) {
            form.set('parent_attributes[is_music]', 'true')
            if (parent.music_title) form.set('parent_attributes[music_title]', parent.music_title)
        }
        if (parent.embeds?.length)
            form.set('parent_attributes[embeds]', JSON.stringify(parent.embeds))
        // Files may already be pre-uploaded (array of signed_ids)
        let parentFileSignedIds: string[] = []
        if (parent.files?.length) {
            if (typeof parent.files[0] === 'string') {
                parentFileSignedIds = (parent.files as string[]).filter(
                    id => typeof id === 'string' && id.trim(),
                )
            } else {
                parentFileSignedIds = await directUploadFiles(parent.files as File[])
            }
            parentFileSignedIds.forEach(id => form.append('parent_attributes[files][]', id))
        }
        // Attach client-generated previews for files (aligned by index)
        if (parent.file_previews?.length)
            [parent.file_previews[0]].forEach(preview => {
                if (preview) form.append('parent_attributes[file_previews][]', preview)
            })
        const parentAnswerImageUploads = await resolveAnswerImagesForUpload(parent, {
            filename: 'thread-image',
        })
        const parentAnswerImageSignedIds = await Promise.all(
            parentAnswerImageUploads.map((image, index) =>
                typeof image === 'string'
                    ? image
                    : directUploadFile(image, undefined, undefined, {
                          source: 'createQuestParent',
                          uploadKind: 'image',
                          fileIndex: index,
                          totalFiles: parentAnswerImageUploads.length,
                      }),
            ),
        )
        parentAnswerImageSignedIds.forEach(signedId =>
            form.append('parent_attributes[images][]', signedId),
        )
        const parentAnswerImageSignedId = parentAnswerImageSignedIds[0] || null
        if (Array.isArray(parentActionRequests)) {
            form.set(
                'parent_attributes[action_requests]',
                JSON.stringify(
                    attachUploadedImageInputsToActionRequests(
                        parentActionRequests,
                        parentAnswerImageSignedIds,
                    ),
                ),
            )
        }
        // Direct upload large recording and attach by signed_id (or accept pre-uploaded signed_id)
        let parentRecordingSignedId: string | null = null
        if (parent.recording) {
            if (typeof parent.recording === 'string') {
                parentRecordingSignedId = parent.recording as string
            } else {
                const rec = parent.recording as File
                parentRecordingSignedId = await directUploadFile(rec)
            }
            form.set('parent_attributes[recording]', parentRecordingSignedId)
        }
        assertSubmittableAnswerContent(parent, {
            imageSignedId: parentAnswerImageSignedId,
            fileSignedIds: parentFileSignedIds,
            recordingSignedId: parentRecordingSignedId,
        })
        if (parent.created_at) form.set('parent_attributes[created_at]', parent.created_at)
        if (parent.updated_at) form.set('parent_attributes[updated_at]', parent.updated_at)
        if (parent.message_type) {
            form.set('parent_attributes[message_type]', parent.message_type)
        }
        if (parent.poll_options?.length) {
            parent.poll_options.forEach(option => {
                form.append('parent_attributes[poll_options][]', option)
            })
        }
        if (typeof parent.poll_duration_minutes === 'number') {
            form.set(
                'parent_attributes[poll_duration_minutes]',
                String(parent.poll_duration_minutes),
            )
        }
        if (parent.talk_to_agent) {
            form.set('parent_attributes[talk_to_agent]', parent.talk_to_agent)
            if (parent.agent_model) form.set('parent_attributes[agent_model]', parent.agent_model)
        }

        const questId = String(quest.id)
        const createQuestPromise = (async () => {
            try {
                const request = axios.post(gon.api.quests, form, {
                    headers: { ...accessTokens(), ...clientSigningDeviceHeaders() },
                })
                diagnostic?.requestSent({ request: 'create_quest' })
                const res = await request
                // The new-wallet intro skips the "Join this channel" prompt.
                if (!quest.no_join_invite_after_publish)
                    offerChannelJoinInvite(res.data?.join_invite)
                if (res.data.quest) {
                    if (res.data.duplicate_reason === 'content') {
                        useStore
                            .getState()
                            .showGlobalNotice(
                                "Identical content already exists, so we didn't post it again.",
                                {
                                    kind: 'duplicate-content',
                                    testId: 'toast-duplicate-content',
                                },
                            )
                    }
                    return cacheQuestPreservingPendingOptimisticAnswers({
                        ...res.data.quest,
                        isLocalRootQuest: false,
                    })
                }
                return null
            } catch (error: unknown) {
                const status = isResponseError(error) ? error.response?.status : undefined
                const data = isResponseError(error) ? error.response?.data : undefined
                throw buildApiError(
                    getReadableApiErrorMessage(error, 'Failed to create thread.'),
                    status,
                    data,
                )
            }
        })()

        pendingCreateQuestById.set(questId, createQuestPromise)
        try {
            rootQuest = await createQuestPromise
        } finally {
            if (pendingCreateQuestById.get(questId) === createQuestPromise) {
                pendingCreateQuestById.delete(questId)
            }
        }
        //console.log('POSTED QUEST', rootQuest, rootQuest.parent, rootQuest.parent.embeds)
        //console.log('api post quest caching', rootQuest)
    }

    return rootQuest
}

async function duplicateQuest(questId) {
    const url = `${gon.api.quests}/${questId}/duplicate`
    return fetch(url, {
        method: 'put',
        headers: { ...accessTokens() },
    })
        .then(res => res.json())
        .catch(err => {
            console.log(err)
        })
}

async function deleteQuest(questId, params = {}) {
    const url = `${gon.api.quests}/${questId}`

    await fetch(url, {
        method: 'delete',
        credentials: 'same-origin',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': gon.formToken,
        },
        body: JSON.stringify(params),
    }).catch(err => {
        console.log(err)
    })
}

async function postThread(content, contentJson, parentId, parentType) {
    const res = await fetch(gon.api.threads, {
        method: 'post',
        credentials: 'same-origin',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': gon.formToken,
        },
        body: JSON.stringify({
            content: content,
            content_json: contentJson,
            parent_id: parentId,
            parent_type: parentType,
        }),
    })
    const data = await res.json()
    return data.quest
}

async function pinQuest(questId, teamId = null) {
    const url = `${gon.api.quests}/${questId}/pin`
    const body = teamId ? { team_id: teamId } : {}
    const res = await fetch(url, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': gon.formToken,
        },
        body: JSON.stringify(body),
    })
    return res
}

async function unpinQuest(questId, teamId = null) {
    const url = `${gon.api.quests}/${questId}/unpin`
    const body = teamId ? { team_id: teamId } : {}
    const res = await fetch(url, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': gon.formToken,
        },
        body: JSON.stringify(body),
    })
    return res
}

function normalizeAnswerImageForUpload(
    image: string | Blob | null | undefined,
): File | string | null {
    if (!image) return null
    if (typeof image === 'string') return image
    if (image instanceof File) return image

    return new File([image], 'answer-image', {
        type: image.type || 'application/octet-stream',
        lastModified: Date.now(),
    })
}

function isLocalPreviewUrl(url: unknown): url is string {
    return typeof url === 'string' && /^(blob|data):/i.test(url)
}

function extensionForContentType(contentType: string | null | undefined): string {
    const subtype = contentType?.split('/')?.[1]?.split(';')?.[0]?.trim()
    if (!subtype) return 'png'
    if (subtype === 'jpeg') return 'jpg'
    return subtype.replace(/[^a-z0-9]+/gi, '') || 'png'
}

async function localPreviewUrlToFile(url: string, filename: string): Promise<File> {
    const response = await fetch(url)
    if (!response.ok) throw new Error(`Failed to read local image preview (${response.status})`)

    const blob = await response.blob()
    const contentType = blob.type || 'image/png'
    return new File([blob], `${filename}.${extensionForContentType(contentType)}`, {
        type: contentType,
        lastModified: Date.now(),
    })
}

async function resolveAnswerImagesForUpload(
    answer: Pick<
        Partial<Answer>,
        | 'pending_images'
        | 'local_image_urls'
        | 'answer_image'
        | 'answer_image_url'
        | 'local_answer_image_url'
    >,
    options: { filename?: string } = {},
): Promise<Array<File | string>> {
    const explicitImages =
        Array.isArray(answer.pending_images) && answer.pending_images.length > 0
            ? answer.pending_images
            : answer.answer_image
            ? [answer.answer_image]
            : []
    const localPreviewUrls =
        Array.isArray(answer.local_image_urls) && answer.local_image_urls.length > 0
            ? answer.local_image_urls
            : [answer.local_answer_image_url, answer.answer_image_url].filter(isLocalPreviewUrl)
    const imageValues = explicitImages.length > 0 ? explicitImages : localPreviewUrls

    return Promise.all(
        imageValues.map(async (image, index) => {
            const filename = `${options.filename || 'answer-image'}-${index + 1}`
            if (isLocalPreviewUrl(image)) {
                return localPreviewUrlToFile(image, filename)
            }

            const explicitUpload = normalizeAnswerImageForUpload(image)
            if (explicitUpload) return explicitUpload

            return null
        }),
    ).then(images => images.filter((image): image is File | string => !!image))
}

async function setAnswerForm(
    answer: Partial<Answer>,
    options: { requireContent?: boolean } = {},
    diagnostic?: ReplyRequestDiagnostics,
): Promise<FormData> {
    const form = new FormData()
    if (answer.space_id) form.set('space_id', answer.space_id)
    else {
        // TODO Bug. Why do we have to do this?
        const { activeSpaceId } = useStore.getState()
        Sentry.withScope(scope => {
            scope.setExtras({
                answer_id: answer?.id,
                user_id: gon?.currentUser?.id,
                user_name: gon?.currentUser?.name,
                backup_space_id: activeSpaceId,
            })
            Sentry.captureMessage('Missing space id in js api.')
        })
        form.set('space_id', activeSpaceId)
    }
    if (answer.id) form.set('id', answer.id)
    if (answer.quest_id) form.set('quest_id', answer.quest_id)
    if (answer.user_id) form.set('user_id', answer.user_id)
    if (answer.url_id !== undefined && answer.url_id !== null) {
        form.set('url_id', String(answer.url_id))
    }
    if (answer.content) form.set('content', answer.content)
    if (answer.delta_json) form.set('delta_json', JSON.stringify(answer.delta_json))
    if ('archive' in answer && answer.archive !== null) form.set('archive', String(answer.archive))
    if (answer.position) form.set('position', answer.position.toString())
    if (answer.embeds?.length) answer.embeds.forEach(e => form.append('embeds[]', e.id))
    if (answer.reply_to_answer_id) form.set('reply_to_answer_id', String(answer.reply_to_answer_id))
    let fileSignedIds: string[] = []
    if (answer.files?.length) {
        if (typeof answer.files[0] === 'string') {
            fileSignedIds = (answer.files as string[]).filter(
                id => typeof id === 'string' && id.trim(),
            )
        } else {
            diagnostic?.crumb('form_files_begin')
            fileSignedIds = await directUploadFiles(answer.files as File[])
        }
        fileSignedIds.forEach(id => form.append('files[]', id))
    }
    if (answer.file_previews?.length)
        [answer.file_previews[0]].forEach(preview => {
            if (preview) form.append('file_previews[]', preview)
        })
    diagnostic?.crumb('form_image_read_begin')
    const answerImageUploads = await resolveAnswerImagesForUpload(answer)
    diagnostic?.crumb('form_images_upload_begin')
    const answerImageSignedIds = await Promise.all(
        answerImageUploads.map((image, index) =>
            typeof image === 'string'
                ? image
                : directUploadFile(image, undefined, undefined, {
                      source: 'setAnswerForm',
                      uploadKind: 'image',
                      fileIndex: index,
                      totalFiles: answerImageUploads.length,
                  }),
        ),
    )
    answerImageSignedIds.forEach(signedId => form.append('images[]', signedId))
    const answerImageSignedId = answerImageSignedIds[0] || null
    if (answer.payment_ids?.length) form.set('payment_ids', JSON.stringify(answer.payment_ids))
    const answerActionRequests = answer.action_requests
    if (Array.isArray(answerActionRequests)) {
        form.set(
            'action_requests',
            JSON.stringify(
                attachUploadedImageInputsToActionRequests(
                    answerActionRequests,
                    answerImageSignedIds,
                ),
            ),
        )
    }
    let recordingSignedId: string | null = null
    if (answer.recording) {
        if (typeof answer.recording === 'string') {
            recordingSignedId = answer.recording
        } else {
            const rec = answer.recording as Blob
            diagnostic?.crumb('form_recording_begin')
            recordingSignedId = await directUploadFile(rec as File)
        }
        form.set('recording', recordingSignedId)
    }
    if (answer.created_at) form.set('created_at', answer.created_at.toString())
    if (answer.updated_at) form.set('updated_at', answer.updated_at.toString())
    if (answer.message_type) form.set('message_type', answer.message_type)
    if (answer.skip_notifications) form.set('skip_notifications', 'true')
    // Moderation reason for an archive by a thread/stream owner.
    if (typeof answer.reason === 'string') form.set('reason', answer.reason)
    if (answer.clear_embeds) form.set('clear_embeds', 'true')
    if (answer.clear_files) form.set('clear_files', 'true')
    if (answer.clear_image) form.set('clear_image', 'true')
    if (answer.clear_recording) form.set('clear_recording', 'true')
    if (answer.clear_url) form.set('clear_url', 'true')

    if (answer.child_quests?.[0]?.id) form.set('child_quest_id', answer.child_quests?.[0]?.id)
    if ('talk_to_agent' in answer && answer.talk_to_agent) {
        form.set('talk_to_agent', answer.talk_to_agent.toString())
    }
    if (answer.agent_model) form.set('agent_model', answer.agent_model)
    if (answer.poll_options?.length) {
        form.set('poll_options', JSON.stringify(answer.poll_options))
    }
    if (typeof answer.poll_duration_minutes === 'number') {
        form.set('poll_duration_minutes', String(answer.poll_duration_minutes))
    }

    // Add dice field if present
    if (answer.dice) {
        const dice = { ...answer.dice, status: 'ready' }
        form.set('dice', JSON.stringify(dice))
    }

    if (options.requireContent) {
        assertSubmittableAnswerContent(answer, {
            imageSignedId: answerImageSignedId,
            fileSignedIds,
            recordingSignedId,
        })
    }

    return form
}

async function createAnswer(answer, trace?: ReplySubmitTrace | null) {
    trace?.apiBegin()
    const existing = answer?.id != null ? pendingCreateAnswerById.get(String(answer.id)) : null
    const diagnostic =
        (existing && pendingAnswerDiagnostics.get(existing)) || createReplyRequestDiagnostics()
    trace?.crumb('create_answer', { reused_in_flight: !!existing })
    diagnostic.attach(trace)
    const request = runInFlightRequest(pendingCreateAnswerById, answer?.id, () =>
        performCreateAnswer(answer, diagnostic),
    )
    pendingAnswerDiagnostics.set(request, diagnostic)
    try {
        return await request
    } finally {
        diagnostic.finish()
    }
}

async function performCreateAnswer(answer, diagnostic: ReplyRequestDiagnostics) {
    const pendingQuestId = answer?.quest_id != null ? String(answer.quest_id) : null
    const pendingAnswerId = answer?.id != null ? String(answer.id) : null
    if (pendingQuestId && pendingAnswerId) {
        useStore.getState().trackPendingOptimisticAnswer(pendingQuestId, pendingAnswerId)
        upsertPendingAnswerOutbox(answer)
    }

    let preparedForm
    try {
        diagnostic.crumb('create_form_begin')
        preparedForm = await setAnswerForm(
            answer,
            {
                requireContent: !isInsertAnswerPlaceholder(answer),
            },
            diagnostic,
        )
        diagnostic.crumb('create_form_end')
        recordClientSigningPostIntent(answer?.id, answer?.content)
    } catch (error) {
        diagnostic.crumb('create_form_failed')
        if (pendingQuestId && pendingAnswerId) {
            useStore.getState().resolvePendingOptimisticAnswer(pendingQuestId, pendingAnswerId)
        }
        throw error
    }

    const ANSWER_CREATE_RETRY_DELAYS_MS = [250, 600, 1200, 2500]
    const CHILD_QUEST_CREATE_RETRY_DELAYS_MS = [250, 600, 1200, 2500, 5000, 8000, 13000]
    const wait = async (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
    const getPostingTargetLocalQuest = () => {
        if (!pendingQuestId) return false
        const quest = cache.getCachedQuest(pendingQuestId)
        return quest?.isLocalChildQuest || quest?.isLocalRootQuest ? quest : null
    }
    const waitForPendingQuestCreation = async () => {
        if (!pendingQuestId) return null
        const pendingCreateQuest = pendingCreateQuestById.get(pendingQuestId)
        if (!pendingCreateQuest) {
            diagnostic.crumb('pending_quest_absent', { has_pending_creation: false })
            return null
        }
        diagnostic.crumb('pending_quest_begin', { has_pending_creation: true })
        const confirmedQuest = await pendingCreateQuest
        diagnostic.crumb('pending_quest_end')
        if (confirmedQuest?.id && confirmedQuest.id !== '__PRIVATE__') {
            cache.cacheQuest({
                ...confirmedQuest,
                isLocalChildQuest: false,
                isLocalRootQuest: false,
            })
        }
        return confirmedQuest
    }
    const freshFormData = () => {
        const form = new FormData()
        preparedForm.forEach((value, key) => {
            form.append(key, value)
        })
        return form
    }

    for (let attempt = 0; ; attempt += 1) {
        const retryDelays = answer?.child_quests?.[0]?.id
            ? CHILD_QUEST_CREATE_RETRY_DELAYS_MS
            : ANSWER_CREATE_RETRY_DELAYS_MS
        const target = getPostingTargetLocalQuest()
        if (target) {
            diagnostic.crumb('create_wait_local_quest', {
                attempt,
                local_child: !!target?.isLocalChildQuest,
                local_root: !!target?.isLocalRootQuest,
            })
            let confirmedQuest
            try {
                confirmedQuest = await waitForPendingQuestCreation()
            } catch (error) {
                if (pendingQuestId && pendingAnswerId) {
                    useStore
                        .getState()
                        .resolvePendingOptimisticAnswer(pendingQuestId, pendingAnswerId)
                }
                throw error
            }

            if (!confirmedQuest) {
                diagnostic.crumb('quest_lookup_begin')
                confirmedQuest = pendingQuestId ? await getQuestQuery(pendingQuestId) : null
                diagnostic.crumb('quest_lookup_end')
            }
            if (confirmedQuest?.id && confirmedQuest.id !== '__PRIVATE__') {
                cache.cacheQuest({
                    ...confirmedQuest,
                    isLocalChildQuest: false,
                    isLocalRootQuest: false,
                })
            } else if (attempt < retryDelays.length) {
                diagnostic.crumb('quest_retry_wait', { attempt, delay_ms: retryDelays[attempt] })
                await wait(retryDelays[attempt])
                continue
            } else {
                if (pendingQuestId && pendingAnswerId) {
                    useStore
                        .getState()
                        .resolvePendingOptimisticAnswer(pendingQuestId, pendingAnswerId)
                }
                throw buildApiError('Thread is still being created. Please try again.', 404, {
                    status: 'answer_not_ready',
                })
            }
        }

        const form = freshFormData()
        const request = fetch(gon.api.answers, {
            headers: { ...accessTokens(), ...clientSigningDeviceHeaders() },
            method: 'POST',
            body: form,
        })
        diagnostic.requestSent({ request: 'create_answer', attempt })
        const res = await request
        // Parse body once upfront to avoid "body already read" errors from calling
        // both res.text() and res.json() on the same response
        const bodyText = await res.text().catch(() => '')
        let data = null
        if (bodyText) {
            try {
                data = JSON.parse(bodyText)
            } catch (e) {
                data = null
            }
        }

        if (!res.ok) {
            const status = `${res.status} ${res.statusText}`.trim()
            const message = formatApiErrorData(data) || bodyText || 'Request failed'
            const apiStatus = data?.status

            // During root-thread creation, replies may race with quest creation.
            // Retry briefly when backend reports the quest is not ready yet.
            const questNotReady =
                apiStatus === 'answer_not_ready' ||
                (res.status === 404 && typeof message === 'string' && message.match(/quest/i))
            if (questNotReady && attempt < retryDelays.length) {
                diagnostic.crumb('quest_retry_wait', { attempt, delay_ms: retryDelays[attempt] })
                await wait(retryDelays[attempt])
                continue
            }

            // Handle duplicate ID errors gracefully for idempotency.
            // The client generates UUIDs for answers before sending. If a request
            // succeeds on the server but the client doesn't receive the response
            // (network hiccup, timeout, etc.), a retry will fail with a duplicate
            // key error. Rather than showing an error to the user, we fetch the
            // existing answer and return it - the answer was created successfully.
            // These strings cover: Rails RecordNotUnique, Postgres error text, and
            // the specific constraint name for the answers table primary key.
            const isDuplicateId =
                typeof message === 'string' &&
                (message.includes('RecordNotUnique') ||
                    message.includes('duplicate key value') ||
                    message.includes('answers_pkey'))
            if (isDuplicateId && answer?.id) {
                try {
                    const existing = await getAnswer(answer.id)
                    if (existing) {
                        markPendingAnswerOutboxConfirmed(answer.id)
                        return existing
                    }
                } catch (e) {
                    // Fall through to throw the original error
                }
            }
            console.error('createAnswer failed:', status, message)
            if (data?.answer) return data.answer
            if (pendingQuestId && pendingAnswerId) {
                useStore.getState().resolvePendingOptimisticAnswer(pendingQuestId, pendingAnswerId)
                if (shouldRemovePendingAnswerOutboxForStatus(res.status)) {
                    removePendingAnswerOutbox(pendingAnswerId)
                }
            }
            throw buildApiError(message, res.status, data)
        }
        offerChannelJoinInvite(data?.join_invite)
        if (data?.answer) {
            // Record the successful write before cache reconciliation, which may still fail on a
            // malformed projection. This keeps a saved post from being replayed as unconfirmed.
            markPendingAnswerOutboxConfirmed(data.answer.id)
        }
        if (data?.quest) {
            cacheQuestPreservingPendingOptimisticAnswers(data.quest)
        }
        if (data?.side_quest) {
            cacheQuestPreservingPendingOptimisticAnswers({
                ...data.side_quest,
                isLocalChildQuest: false,
            })
        } else if (answer?.child_quests?.[0]?.id) {
            const childQuestId = String(answer.child_quests[0].id)
            const localChildQuest = cache.getCachedQuest(childQuestId)
            if (localChildQuest?.isLocalChildQuest) {
                cache.cacheQuest({
                    ...localChildQuest,
                    isLocalChildQuest: false,
                })
            }
        }
        // Quest payloads can carry a lean projection of the newly created post. Finish the
        // reconciliation with the direct Answer response so that projection cannot replace the
        // optimistic embed relationship for a render before the caller receives the result.
        if (data?.answer) {
            cache.cacheAnswer(data.answer)
        }
        return data?.answer
    }
}

function schedulePendingAnswerOutboxReplayForQuest(questId: QuestId) {
    const items = getPendingAnswerOutboxItemsForQuest(questId).filter(item => !item.confirmedAtMs)
    if (!items.length) return

    items.forEach(item => {
        if (pendingAnswerOutboxReplayIds.has(item.answerId)) return
        pendingAnswerOutboxReplayIds.add(item.answerId)

        window.setTimeout(async () => {
            try {
                await createAnswer(item.answer)
            } catch (error) {
                const status = isResponseError(error) ? error.response?.status : undefined
                const errorStatus =
                    status || (error && typeof error === 'object' ? (error as any).status : null)
                if (shouldRemovePendingAnswerOutboxForStatus(errorStatus)) {
                    removePendingAnswerOutbox(item.answerId)
                }
            } finally {
                pendingAnswerOutboxReplayIds.delete(item.answerId)
            }
        }, 1500)
    })
}

async function deleteAnswer(answerId, params = {}) {
    const url = `${gon.api.answers}/${answerId}`

    await fetch(url, {
        method: 'delete',
        credentials: 'same-origin',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': gon.formToken,
        },
        body: JSON.stringify(params),
    }).catch(err => {
        console.log(err)
    })
}

async function updateAnswer(
    answerId: number | string,
    answer: Partial<Answer>,
    options: { optimistic?: boolean; skip_cache?: boolean; override?: boolean } = null,
) {
    const { optimistic = false, skip_cache = false } = options || {}
    const formData = await setAnswerForm(answer)
    recordClientSigningPostIntent(answerId, answer.content)

    async function getter() {
        const res = await axios
            .put(`${gon.api.answers}/${answerId}`, formData, {
                headers: { ...accessTokens() },
            })
            .catch(error => {
                const status = isResponseError(error) ? error.response?.status : undefined
                const data = isResponseError(error) ? error.response?.data : undefined
                throw buildApiError(
                    getReadableApiErrorMessage(error, 'Failed to update answer.'),
                    status,
                    data,
                )
            })
        const answer = res.data.answer
        if (skip_cache) return answer

        let cachedQuest, cachedSideQuest
        const cachedAnswer = cache.cacheAnswer(answer)
        //console.log('api update answer caching', res.data.answer, res.data.quest)
        if (res.data.quest)
            cachedQuest = cacheQuestPreservingPendingOptimisticAnswers(res.data.quest)
        if (res.data.side_quest)
            cachedSideQuest = cacheQuestPreservingPendingOptimisticAnswers(res.data.side_quest)
        return [cachedAnswer, cachedQuest, cachedSideQuest]
    }

    if (optimistic) {
        getter()
        return cache.updateCachedAnswer(answerId, answer)
    } else {
        return await getter()
    }
}

type BulkAnswersResult = {
    answers: Answer[]
    ok: boolean
    status: number | null
    requestedIds: string[]
}

type BulkAnswersOptions = {
    signal?: AbortSignal | null
    skipCache?: boolean
}

function throwIfRequestAborted(signal?: AbortSignal | null) {
    if (!signal?.aborted) return
    if (signal.reason instanceof Error) throw signal.reason

    const error = new Error('Request aborted')
    error.name = 'AbortError'
    throw error
}

async function getAnswersWithMeta(
    answerIds: string[],
    options: BulkAnswersOptions = { skipCache: false },
): Promise<BulkAnswersResult> {
    const requestedIds = (answerIds || []).filter(Boolean).map(String)
    if (requestedIds.length <= BULK_ANSWERS_MAX_IDS)
        return getAnswersChunkWithMeta(answerIds, options)

    const merged = await fetchAnswersInChunks(requestedIds, chunk =>
        getAnswersChunkWithMeta(chunk, options),
    )
    return { ...merged, requestedIds }
}

async function getAnswersChunkWithMeta(
    answerIds: string[],
    options: BulkAnswersOptions = { skipCache: false },
): Promise<BulkAnswersResult> {
    const requestedIds = (answerIds || []).filter(Boolean).map(String)
    if (!answerIds || answerIds.length === 0) {
        return { answers: [], ok: true, status: 200, requestedIds: [] }
    }

    const url = `${gon.api.answers}/bulk`

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                ...accessTokens(),
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify({ ids: answerIds }),
            signal: options.signal,
        })

        if (!response.ok) {
            const err = new Error(`HTTP error! status: ${response.status}`)
            ;(err as Error & { status?: number }).status = response.status
            throw err
        }

        const data = await response.json()
        throwIfRequestAborted(options.signal)

        // Normalize users separately from answers (fire-and-forget, non-blocking):
        // fetch fresh user records for all authors referenced by these answers.
        // This avoids relying on embedded `answer.user` objects (which can be stale if Answer JSON is cached).
        // NOTE: We intentionally don't await this - answers should render immediately while
        // user cache populates in background. UserIcon will re-render when cache updates.
        try {
            const userIds = Array.from(
                new Set(
                    (data?.answers || [])
                        .flatMap(a => {
                            const out: string[] = []
                            if (a?.user_id) out.push(a.user_id)
                            if (a?.user?.id) out.push(a.user.id)
                            return out
                        })
                        .filter(Boolean)
                        .map(String),
                ),
            )
            if (userIds.length) {
                // Fire-and-forget: don't block answer rendering on user fetch
                fetch(`${gon.api.users}/bulk`, {
                    method: 'POST',
                    headers: {
                        ...accessTokens(),
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                    body: JSON.stringify({ ids: userIds }),
                    signal: options.signal,
                })
                    .then(res => (res.ok ? res.json() : null))
                    .then(usersData => {
                        if (usersData?.users?.length) {
                            cache.cacheUsers(usersData.users)
                        }
                    })
                    .catch(() => {})
            }
        } catch (e) {}

        if (!options.skipCache) {
            cache.cacheAnswers(data.answers)
        }
        return {
            answers: data.answers || [],
            ok: true,
            status: response.status,
            requestedIds,
        }
    } catch (error) {
        throwIfRequestAborted(options.signal)
        console.error('error fetching answers:', error)
        return {
            answers: [],
            ok: false,
            status: (error as Error & { status?: number })?.status || null,
            requestedIds,
        }
    }
}

async function getAnswers(answerIds: string[], options: BulkAnswersOptions = { skipCache: false }) {
    const result = await getAnswersWithMeta(answerIds, options)
    return result.answers
}

/**
 * hydrates a quest object with its associated answers
 * fetches all answers for the quest, including sorted answers and parent answer, then
 * replaces the answer ids in the quest object with fully hydrated answer objects
 *
 * this is to match the json structure that the rest of the frontend expects
 *
 * @param quest - the quest object to hydrate
 * @returns the hydrated quest object, or null if the input quest is null
 */
export async function hydrateAnswersIntoQuest(quest, signal: AbortSignal | null = null) {
    return hydrateAnswersIntoQuestWithFetcher(
        quest,
        answerIds => getAnswers(answerIds, { signal, skipCache: true }),
        { signal },
    )
}

export async function hydrateViewerNeutralAnswersIntoQuest(
    quest,
    signal: AbortSignal | null = null,
) {
    return hydrateAnswersIntoQuestWithFetcher(
        quest,
        answerIds => getAnswers(answerIds, { signal, skipCache: true }),
        { signal, viewerNeutral: true },
    )
}

export async function hydrateStreamPreviewIntoQuest(quest, signal: AbortSignal | null = null) {
    return hydrateStreamPreviewIntoQuestWithFetcher(
        quest,
        answerIds => getAnswers(answerIds, { signal, skipCache: true }),
        { signal },
    )
}

async function getQuestAiInputMediaTree(id: QuestId): Promise<any> {
    const res = await fetch(`${gon.api.quests}/${id}/ai_input_media_tree`, {
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
        },
    })

    if (!res.ok) return { answers: [] }
    return await res.json()
}

async function getQuests(params, signal = null) {
    //console.log('>>> getQuests', { params })
    const questParams = canonicalizeQuestFilter(params)

    // If following filter is set, use the feed endpoint instead
    if (questParams.following) {
        const { following, ...feedParams } = questParams
        return getFeed(feedParams, signal)
    }

    const queryString = getQueryString({ ...questParams, ...STREAM_FEED_CARD_PAYLOAD_PARAM })
    const questsPath = gon.currentUser ? gon.api.quests : gon.api.publicQuests
    const res = await fetch(`${questsPath}` + queryString, {
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
        },
        cache: 'no-store',
        signal,
    }).catch(err => {
        if (!signal?.aborted) {
            console.log(">>> Couldn't retrieve quests for query + filter: ", queryString)
        }
    })
    // Don't need to set anything since next search or post req is out there.
    if (abort401(res) || (signal && signal.aborted)) return

    if (res) {
        const data: GetQuestsResponse | undefined = await res.json().catch(err => {})
        throwIfRequestAborted(signal)
        // One shared /answers/bulk request per page: every per-quest hydration
        // below joins it via the coalesced fetcher instead of racing its own
        // round trip (the page used to wait on the slowest of ~20).
        const getPageAnswers = createCoalescedGetAnswers(answerIds =>
            getAnswers(answerIds, { signal, skipCache: true }),
        )
        const hydratePageQuest = quest =>
            hydrateStreamPreviewIntoQuestWithFetcher(quest, getPageAnswers, { signal })
        // Kick off the pinned card (same bounded stream projection) alongside
        // the page quests so it shares their coalesced answers request.
        const pinned = data ? data.pinned_quest || data.pinnedQuest : null
        const pinnedPromise = pinned ? hydratePageQuest(pinned) : null
        // The page quests' rejection wins below; keep an abandoned pinned
        // rejection from surfacing as unhandled.
        pinnedPromise?.catch(() => {})
        if (data && data.quests) {
            data.quests = await Promise.all(data.quests.map(hydratePageQuest))
        }
        if (data && pinnedPromise) {
            try {
                const hydratedPinned = await pinnedPromise
                if (data.pinned_quest) data.pinned_quest = hydratedPinned
                else data.pinnedQuest = hydratedPinned
            } catch (error) {
                throwIfRequestAborted(signal)
            }
        }
        throwIfRequestAborted(signal)
        return data
    }
}

async function getFeed(params, signal = null) {
    const queryString = getQueryString({ ...params, ...STREAM_FEED_CARD_PAYLOAD_PARAM })
    const res = await fetch(`${gon.api.feed}${queryString}`, {
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
        },
        signal,
    }).catch(err => {
        if (!signal?.aborted) console.log(">>> Couldn't retrieve feed: ", queryString)
    })

    if (abort401(res) || (signal && signal.aborted)) return

    if (res) {
        const data = await res.json().catch(err => {})
        throwIfRequestAborted(signal)
        if (data && data.quests) {
            // Same page-level coalescing as getQuests: one shared bulk answers
            // request for the whole feed page.
            const getPageAnswers = createCoalescedGetAnswers(answerIds =>
                getAnswers(answerIds, { signal, skipCache: true }),
            )
            data.quests = await Promise.all(
                data.quests.map(quest =>
                    hydrateStreamPreviewIntoQuestWithFetcher(quest, getPageAnswers, { signal }),
                ),
            )
        }
        throwIfRequestAborted(signal)
        return data
    }
}

export interface FeedNewItemsAuthor {
    id: string
    name?: string | null
    // null = definitively no uploaded avatar (render initials, no network);
    // absent would mean stale/unknown, but this endpoint always resolves it.
    avatar_urls?: Record<string, string> | null
}

export interface FeedNewItemsItem {
    id: string
    // nil when the thread's author row is gone or unset — the item still
    // counts as new, it just contributes no avatar.
    user?: FeedNewItemsAuthor | null
}

export interface FeedNewItemsResponse {
    quest_items?: FeedNewItemsItem[]
    capped?: boolean
    watermark?: number
}

// Head/new-items probe for the feed pill (docs/feed-new-items-pill.md).
// Mirrors getQuests routing: following filters hit the feed endpoint (scope is
// fixed server-side, only `after` matters), everything else hits the quests
// endpoint with the same flat filter params the feed list itself uses.
// `after` absent = head probe returning { watermark }.
export async function fetchFeedNewItems(
    filter,
    after: number | null = null,
): Promise<FeedNewItemsResponse | undefined> {
    const questParams = canonicalizeQuestFilter(filter || {})
    const isFollowing = Boolean(questParams.following)

    const { following, page, ...filterParams } = questParams
    const params: Record<string, unknown> = isFollowing ? {} : filterParams
    if (after != null) params.after = after

    const base = isFollowing ? `${gon.api.feed}/new_items` : `${gon.api.quests}/new_items`
    const res = await fetch(`${base}${getQueryString(params)}`, {
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
        },
        cache: 'no-store',
    }).catch(() => undefined)

    if (!res || !res.ok || abort401(res)) return undefined
    return await res.json().catch(() => undefined)
}

async function getQuestQuery(
    id: QuestId,
    cacheOptions: Parameters<typeof cacheQuestPreservingPendingOptimisticAnswers>[1] & {
        streamPreview?: boolean
        deferAnswerHydration?: boolean
    } = {},
    signal: AbortSignal | null = null,
): Promise<any> {
    //console.log('getQuest', id)
    const res = await fetch(
        `${gon.api.quests}/${id}${getQueryString(IDS_ONLY_ANSWER_PAYLOAD_PARAM)}`,
        {
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
            },
            signal,
        },
    )

    if (res.status === 401) {
        console.error('401 error in getQuestQuery', id)
        return { id: '__PRIVATE__' } as Quest
    } else if (res.status === 404) {
        return null
    } else {
        const data = await res.json()
        throwIfRequestAborted(signal)
        const {
            streamPreview = false,
            deferAnswerHydration = false,
            ...questCacheOptions
        } = cacheOptions
        const quest = await hydrateQuestForQueryWithFetcher(
            data?.quest,
            answerIds => getAnswers(answerIds, { signal, skipCache: true }),
            { signal, streamPreview, deferAnswerHydration },
        )
        throwIfRequestAborted(signal)
        const cachedQuest = cacheQuestPreservingPendingOptimisticAnswers(quest, questCacheOptions)
        schedulePendingAnswerOutboxReplayForQuest(id)
        return cachedQuest
    }
}

async function getQuest(
    id: string | number,
    opts: { obscure?: boolean; break_cache?: boolean } = { obscure: false },
) {
    // console.log('GET QUEST', id, opts?.obscure ? 'obscure' : '')
    const { activeSpaceId } = useStore.getState()
    async function questGetter() {
        /* retrieve the quest and update knovStore w/new data */
        let params: {
            space_id?: string
            obscure?: boolean
            break_cache?: boolean
            answer_payload?: string
        } = {
            space_id: activeSpaceId,
            ...IDS_ONLY_ANSWER_PAYLOAD_PARAM,
        }
        if (opts.obscure) params.obscure = true
        if (opts.break_cache) params.break_cache = true

        const queryStringParams = new URLSearchParams(params as Record<string, string>).toString()
        const url = `${gon.api.quests}/${id}?${queryStringParams}`
        const res = await fetch(url, {
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
            },
        })
        if (res.status === 401) {
            return null
        } else {
            const data = await res.json()
            const quest = await hydrateAnswersIntoQuest(data.quest)
            return cacheQuestPreservingPendingOptimisticAnswers(quest)
        }
    }
    return await questGetter()
}

// AKA getSideQuest() AKA discuss_quest
async function getCommentQuest(answerId) {
    const res = await fetch(`${gon.api.answers}/${answerId}/discuss`, {
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })

    if (res.status === 401) {
        return null
    } else {
        const data = await res.json()
        const quest = await hydrateAnswersIntoQuest(data.quest)
        return cacheQuestPreservingPendingOptimisticAnswers(quest)
    }
}

// quote_main: main-thread answers quoting a branch quest's head — the union
// projection's guest half, served pre-sorted by created_at.
async function getQuestQuotes(questId) {
    const res = await fetch(`${gon.api.quests}/${questId}/quotes`, {
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })
    if (!res.ok) return []
    const data = await res.json()
    const answers = (data?.answers || []).filter(Boolean)
    answers.forEach(a => cache.cacheAnswer(a))
    return answers
}

async function updateQuestPerms(questId: string, perms: QuestPerms) {
    const url = `${window.knovApiUrl}/api/v1/quests/${questId}/perms`
    const res = await fetch(url, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
        body: JSON.stringify(perms),
    })

    const data = await res.json()
    const quest = data.quest
    if (quest) cacheQuestPreservingPendingOptimisticAnswers(quest)
    return quest
}

async function updateQuest(questId, params, options) {
    const { optimistic = false } = options || {}

    async function mutater() {
        const url = `${gon.api.quests}/${questId}`
        const res = await fetch(url, {
            method: 'PUT',
            credentials: 'same-origin',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-Token': gon.formToken,
            },
            body: JSON.stringify(params),
        })
        const { quest } = await res.json()
        return cacheQuestPreservingPendingOptimisticAnswers(quest)
    }

    if (optimistic) {
        mutater()
        return cache.updateCachedQuest(questId, params)
    }
    return await mutater()
}

async function getAnswerResult(
    id,
    params = {},
): Promise<{ answer: Answer | null; status: number }> {
    const res = await fetch(`${gon.api.answers}/${id}${getQueryString(params)}`, {
        method: 'GET',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })
    if (res.status === 401 || res.status === 404) {
        return { answer: null, status: res.status }
    }

    const { answer } = await res.json()
    return { answer: answer || null, status: res.status }
}

async function getAnswer(id, params = {}): Promise<Answer> {
    const paramsWithSignalContext = { ...params } as Record<string, unknown>
    if (!paramsWithSignalContext.quest_id) {
        const cachedSignalQuestId = cache.getCachedAnswer(String(id))?.signal_quest_id
        if (cachedSignalQuestId) paramsWithSignalContext.quest_id = cachedSignalQuestId
    }

    const { answer } = await getAnswerResult(id, paramsWithSignalContext)
    return answer
}

async function refreshAnswerPayloadMedia(id): Promise<Answer | null> {
    const res = await fetch(`${gon.api.answers}/${id}/refresh_payload_media`, {
        method: 'POST',
        headers: { ...commonHeaders(), ...accessTokens() },
    })

    if (abort401(res)) return null

    if (!res.ok) {
        throw buildApiError(
            `Failed to refresh payload media for answer ${id}`,
            res.status,
            await res.json().catch(() => null),
        )
    }

    const { answer } = await res.json()
    if (answer) cache.cacheAnswer(answer)
    return answer || null
}

async function transcribeAnswer(id, identifySpeakers: boolean = false) {
    let url = `${gon.api.answers}/${id}/transcribe`
    if (!!identifySpeakers) {
        url += '?identify_speakers=true'
    }
    console.log('Sending transcribe request to url: ', url)
    const res = await axios.post(url, null, { headers: { ...accessTokens() } })
    return res.data
}

async function getHistory(params) {
    const queryString = getQueryString(params)
    const res = await fetch(`${gon.api.history}${queryString}`, {
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
        },
    })
    const data = await res.json()
    return {
        actions: Array.isArray(data?.actions) ? data.actions : [],
        has_more: !!data?.has_more,
    }
}

// Latest thread shared with each other person, spanning public and private threads.
type PeopleChatsParams = {
    page?: number
    per_page?: number
}

async function getPeopleChats(spaceId = null, params: PeopleChatsParams = {}) {
    const resolvedSpaceId = spaceId || useStore.getState().activeSpaceId
    const queryParams = new URLSearchParams()
    if (gon.currentUser && resolvedSpaceId) queryParams.set('space_id', String(resolvedSpaceId))
    if (params?.page) queryParams.set('page', String(params.page))
    if (params?.per_page) queryParams.set('per_page', String(params.per_page))
    const queryString = queryParams.toString()
    const query = queryString ? `?${queryString}` : ''
    const res = await fetch(`${window.knovApiUrl}/api/v1/people${query}`, {
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
        },
    })
    const data = await res.json()
    return {
        people: Array.isArray(data?.people) ? data.people : [],
        has_more: !!data?.has_more,
    }
}

// Slim starred-thread previews for the sidebar Starred tab (id, content,
// flags, root post author) — NOT full quest payloads; do not seed the quest
// cache with them.
async function getStarredSidebarQuests(spaceId = null, params: PeopleChatsParams = {}) {
    const resolvedSpaceId = spaceId || useStore.getState().activeSpaceId
    const queryParams = new URLSearchParams({
        starred: 'true',
        sidebar_preview: 'true',
        order: 'created_at',
    })
    if (resolvedSpaceId) queryParams.set('space_id', String(resolvedSpaceId))
    if (params?.page) queryParams.set('page', String(params.page))
    if (params?.per_page) queryParams.set('per_page', String(params.per_page))
    const res = await fetch(`${gon.api.quests}?${queryParams.toString()}`, {
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
        },
        cache: 'no-store',
    })
    if (!res.ok) throw new Error(`Starred threads request failed: ${res.status}`)
    const data = await res.json()
    return {
        quests: Array.isArray(data?.quests) ? data.quests : [],
        has_more: !!data?.has_more,
    }
}

async function starQuest(questId) {
    const res = await fetch(`${gon.api.quests}/${questId}/star`, {
        method: 'PUT',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })
    return res
}

async function unstarQuest(questId) {
    const res = await fetch(`${gon.api.quests}/${questId}/unstar`, {
        method: 'PUT',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })
    return res
}

async function getNotificationsCount(spaceId) {
    if (gon.currentUser) {
        const res = await fetch(`${gon.api.notificationsCount}?space_id=${spaceId}`, {
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
            },
            cache: 'no-store',
        })
        const data = await res.json()
        return data.num_noti
    } else {
        return null
    }
}

// Unread counts for several spaces in one request: { [spaceId]: count }.
// Returns null when signed out or when the server predates the batch endpoint.
async function getNotificationsCounts(
    spaceIds: Array<string | number>,
): Promise<Record<string, number> | null> {
    if (!gon.currentUser || !gon.api.notificationsCounts) return null
    const query = new URLSearchParams()
    spaceIds.forEach(id => query.append('space_ids[]', String(id)))
    const res = await fetch(`${gon.api.notificationsCounts}?${query}`, {
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
        },
        cache: 'no-store',
    })
    if (!res.ok) return null
    const data = await res.json()
    return data?.counts && typeof data.counts === 'object' ? data.counts : null
}

async function deleteEmbed(answerId, embedId) {
    const res = await fetch(`${gon.api.answers}/${answerId}/delete-embed`, {
        method: 'delete',
        credentials: 'same-origin',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': gon.formToken,
        },
        body: JSON.stringify({
            embedded_id: embedId,
        }),
    }).catch(err => {
        console.log(err)
    })
}

async function mergeAnswers(containerQuestId, embeddedQuestId) {
    if (containerQuestId && embeddedQuestId) {
        const res = await fetch(`${gon.api.quests}/${containerQuestId}/merge`, {
            method: 'post',
            credentials: 'same-origin',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-Token': gon.formToken,
            },
            body: JSON.stringify({
                embedded_quest_id: embeddedQuestId,
            }),
        })
        const data = await res.json()
        return data.quest
    } else {
        return null
    }
}

/* Move an answer to a new location, leaving any child quests in place */
async function swapAnswer(srcAnswerId, tgtAnswerId, options) {
    const { optimistic = false } = options || {}

    async function mutater() {
        let result: { updated_quests?: Quest[] } = {}
        const fetchRes = await fetch(`${gon.api.answers}/${srcAnswerId}/swap_with`, {
            method: 'put',
            credentials: 'same-origin',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-Token': gon.formToken,
            },
            body: JSON.stringify({ tgt_answer_id: tgtAnswerId }),
        })
        const resOK = handleErrors(fetchRes)
        result = await resOK.json()
        // NOTE: cacheQuest() takes care of updating cached answers as well
        result?.updated_quests?.forEach(q => {
            cacheQuestPreservingPendingOptimisticAnswers(q)
        })
        return result
    }

    if (optimistic) {
        const { quests, answers } = useStore.getState()

        const srcAnswer = cloneDeep(answers[srcAnswerId])
        const srcQuest = cloneDeep(
            quests[srcAnswer?.quest_id] ||
                Object.values(quests).find(q => q?.parent?.id == srcAnswer?.id),
        )
        const tgtAnswer = cloneDeep(answers[tgtAnswerId])
        const tgtQuest = cloneDeep(
            quests[tgtAnswer?.quest_id] ||
                Object.values(quests).find(q => q?.parent?.id == tgtAnswer?.id),
        )

        cache.updateCachedAnswer(srcAnswerId, {
            quest_id: tgtAnswer?.quest_id,
            position: tgtAnswer?.position,
            side_quest_id: tgtAnswer?.child_quests?.[0]?.id || null,
        })
        cache.updateCachedAnswer(tgtAnswerId, {
            quest_id: srcAnswer?.quest_id,
            position: srcAnswer?.position,
            side_quest_id: srcAnswer?.child_quests?.[0]?.id || null,
        })

        mutater()
    } else {
        return await mutater()
    }
}

async function attachVideo(answerId, blob) {
    let form = new FormData()
    form.append('recording', blob)
    form.append('id', answerId)

    const res = await fetch(`${gon.api.answers}/${answerId}/video`, {
        method: 'post',
        credentials: 'same-origin',
        headers: {
            ...accessTokens(),
            'X-CSRF-Token': gon.formToken,
        },
        body: form,
    })

    const data = await res.json()
    return data.url
}

async function getUsers(params = {}) {
    const { activeSpaceId } = useStore.getState()
    // TODO don't get the space from currentUser
    if (gon?.currentUser && !Object.keys(params).includes('space_id')) {
        params = { ...params, space_id: activeSpaceId }
    }
    const res = await fetch(`${gon.api.users}?${new URLSearchParams(params)}`, {
        headers: { ...accessTokens() },
    })
    const { users } = await res.json()
    return users
}

function handleErrors(response) {
    if (!response.ok) {
        throw Error(response.statusText)
    }
    return response
}

async function updateUser(id, params) {
    try {
        let form
        if (params instanceof FormData) {
            form = params
        } else {
            form = new FormData()
            for (let key in params) {
                form.set(key, params[key])
            }
        }

        const res = await fetch(`${gon.api.users}/${id}`, {
            method: 'PATCH',
            credentials: 'same-origin',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'X-CSRF-Token': gon.formToken,
            },
            body: form,
        })

        const data = await res.json()
        return data
    } catch (e) {
        return e
    }
}

// Soft-deletes the signed-in user's own account (server refuses any other id).
// currentPassword: required by the server for accounts with a password they know.
async function deleteAccount(currentPassword?: string) {
    const res = await fetch(`${gon.api.users}/${gon.currentUser.id}`, {
        method: 'DELETE',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
        body: JSON.stringify({ current_password: currentPassword || undefined }),
    })
    if (abort401(res)) return
    return await res.json()
}

async function toggleUserFeature(feature: string) {
    try {
        const res = await fetch(`${gon.api.users}/${gon.currentUser.id}/toggle_feature`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                ...accessTokens(),
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-CSRF-Token': gon.formToken,
            },
            body: JSON.stringify({ feature }),
        })

        const data = await res.json()
        return data
    } catch (e) {
        return e
    }
}

async function getUserFeatures() {
    try {
        const res = await fetch(`${gon.api.users}/${gon.currentUser.id}/features`, {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'X-CSRF-Token': gon.formToken,
            },
        })

        const data = await res.json()
        if (!res.ok) throw new Error(data.error || 'Failed to load feature flags')
        return data
    } catch (e) {
        return { error: e instanceof Error ? e.message : 'Failed to load feature flags' }
    }
}

async function getBsvAddressForUser(
    username: string,
): Promise<{ address?: string; error?: string }> {
    // encodeURIComponent so usernames with spaces / special chars resolve correctly.
    const url = `${gon.api.bsv}/address-for-user?username=${encodeURIComponent(username)}`
    try {
        const res = await fetch(url, {
            method: 'GET',
            headers: { ...accessTokens() },
        })
        if (!res.ok) {
            return { error: `Could not resolve @${username} (${res.status})` }
        }
        const body = await res.json()
        return { address: body?.address, error: body?.error }
    } catch (err) {
        // Never throw: a thrown lookup used to leave the composer permanently stuck.
        console.error('Error getting bsv address info:', err)
        return { error: err instanceof Error ? err.message : 'Address lookup failed' }
    }
}

export interface ILinkPreviewInfo {
    title?: string
    description?: string
    image?: string
    url: string
    status?: 'pending'
}

export async function getBsvLocks(answerId: string) {
    try {
        const res = await fetch(`${gon.api.answers}/${answerId}/bsv_locks`, {
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
            },
        })
        if (!res.ok) {
            throw Error(res.statusText)
        }
        const data = await res.json()
        return data
    } catch (e) {
        return e
    }
}

export async function getLinkPreview(url, answerId?: string): Promise<ILinkPreviewInfo> {
    const query = new URLSearchParams({ url })
    if (answerId) query.set('answer_id', answerId)
    const res = await fetch(`${gon.api.linkpreview}?${query.toString()}`, {
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
        },
    })
        .then(r => r.json())
        .catch(err => {
            // console.log('linkpreviewcontroller failed to retrieve preview for url', url, 'error: ', err)
        })
    return await res
}

interface DispatchUrlPreviewArgs {
    answerId: string
    address: string
    screenshotOptions?: Record<string, unknown>
    ensureUrl?: boolean
}

export async function dispatchUrlPreview({
    answerId,
    address,
    screenshotOptions,
    ensureUrl,
}: DispatchUrlPreviewArgs): Promise<{ ok: boolean; status: string; request_id?: string } | null> {
    try {
        const res = await fetch(`${window.knovApiUrl}/api/v1/urls/preview`, {
            method: 'POST',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-Token': gon.formToken,
            },
            body: JSON.stringify({
                answer_id: answerId,
                address,
                options: screenshotOptions,
                ensure_url: ensureUrl,
            }),
        })
        if (!res.ok) return null
        return await res.json()
    } catch (e) {
        console.warn('Failed to dispatch URL preview:', e)
        return null
    }
}

export async function fetchUrlPreviewByAddress(
    address: string,
): Promise<{ ok: boolean; url_id?: number; preview_image_url?: string } | null> {
    try {
        const query = new URLSearchParams({ address })
        const res = await fetch(`${window.knovApiUrl}/api/v1/urls/preview?${query.toString()}`, {
            method: 'GET',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
            },
        })
        if (!res.ok) return null
        return await res.json()
    } catch (e) {
        console.warn('Failed to fetch URL preview:', e)
        return null
    }
}

export async function requestUrlPreviewGeneration(
    address: string,
): Promise<{ ok: boolean; url_id?: number; request_id?: string } | null> {
    try {
        const res = await fetch(`${window.knovApiUrl}/api/v1/urls/preview`, {
            method: 'POST',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-Token': gon.formToken,
            },
            body: JSON.stringify({ address }),
        })
        if (!res.ok) return null
        return await res.json()
    } catch (e) {
        console.warn('Failed to queue URL preview generation:', e)
        return null
    }
}

export async function resolveUrl(address: string): Promise<{
    ok: boolean
    status?: string
    url_id?: number
    url?: Record<string, unknown>
} | null> {
    try {
        const res = await fetch(`${window.knovApiUrl}/api/v1/urls/resolve`, {
            method: 'POST',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-Token': gon.formToken,
            },
            body: JSON.stringify({ address }),
        })
        if (!res.ok) return null
        return await res.json()
    } catch (e) {
        console.warn('Failed to resolve URL:', e)
        return null
    }
}

export async function uploadUrlPreviewImage(
    address: string,
    blob: Blob,
): Promise<{ ok: boolean; url_id?: number; preview_image_url?: string } | null> {
    try {
        const form = new FormData()
        form.append('address', address)
        form.append('preview_image', blob, 'preview.jpg')

        const res = await fetch(`${window.knovApiUrl}/api/v1/urls/preview`, {
            method: 'POST',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'X-CSRF-Token': gon.formToken,
            },
            body: form,
        })
        if (!res.ok) return null
        return await res.json()
    } catch (e) {
        console.warn('Failed to upload URL preview image:', e)
        return null
    }
}

export async function refreshTweetEmbedMedia(
    urlId: string | number,
): Promise<{ ok: boolean; url?: Record<string, unknown> } | null> {
    try {
        const res = await fetch(`${window.knovApiUrl}/api/v1/urls/${urlId}/refresh_tweet_media`, {
            method: 'POST',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'X-CSRF-Token': gon.formToken,
            },
        })
        if (!res.ok) return null
        return await res.json()
    } catch (e) {
        console.warn('Failed to refresh tweet media:', e)
        return null
    }
}

export async function fetchBsvOrdinalPreview(ordinalId: string): Promise<{
    ok: boolean
    bsv_ordinal_id?: string
    origin_outpoint?: string
    preview_url?: string
} | null> {
    try {
        const res = await fetch(`${window.knovApiUrl}/api/v1/bsv/ordinals/${ordinalId}/preview`, {
            method: 'GET',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
            },
        })
        if (!res.ok) return null
        return await res.json()
    } catch (e) {
        console.warn('Failed to fetch ordinal preview:', e)
        return null
    }
}

export async function uploadBsvOrdinalPreviewImage(
    ordinalId: string,
    blob: Blob,
): Promise<{ ok: boolean; bsv_ordinal_id?: string; preview_url?: string } | null> {
    try {
        const form = new FormData()
        form.append('preview_image', blob, 'preview.jpg')

        const res = await fetch(
            `${window.knovApiUrl}/api/v1/bsv/ordinals/${ordinalId}/preview_image`,
            {
                method: 'POST',
                headers: {
                    ...accessTokens(),
                    Accept: 'application/json',
                    'X-CSRF-Token': gon.formToken,
                },
                body: form,
            },
        )
        if (!res.ok) return null
        return await res.json()
    } catch (e) {
        console.warn('Failed to upload ordinal preview image:', e)
        return null
    }
}

export async function getSpace(spaceId, params) {
    const res = await fetch(`${gon.api.spaces}/${spaceId}?${new URLSearchParams(params)}`, {
        headers: { ...accessTokens() },
    })
    const data = await res.json()
    return data?.space
}

export async function getSpaces(params) {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    const res = await fetch(`${gon.api.spaces}${query}`, {
        headers: { ...accessTokens() },
    })
    const data = await res.json()
    return data ? data.spaces : []
}

export async function getTeams(params) {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    const headers = gon.currentUser ? { ...accessTokens() } : {}

    try {
        const res = await fetch(`${gon.api.teams}${query}`, {
            headers,
        })

        if (!res.ok) {
            console.error('getTeams API error:', res.status, res.statusText)
            return []
        }

        const data = await res.json()
        return data ? data.teams : []
    } catch (error) {
        console.error('getTeams fetch error:', error)
        return []
    }
}

export async function getPublicTeams(params) {
    const query = params ? `?${new URLSearchParams(params)}` : ''

    try {
        const res = await fetch(`${gon.api.publicTeams}${query}`, {
            // No auth headers needed for public endpoint
        })

        if (!res.ok) {
            console.error('getPublicTeams API error:', res.status, res.statusText)
            return []
        }

        const data = await res.json()
        return data ? data.teams : []
    } catch (error) {
        console.error('getPublicTeams fetch error:', error)
        return []
    }
}

export async function getPublicTeam(teamId, params = null) {
    const teams = await getPublicTeams(params)
    return teams.find(team => String(team.id) === String(teamId)) || null
}

export async function getTeamCardStats(teamId: string): Promise<{
    members_count: number | null
    host_name: string | null
    upvalue_sats_30d: number | null
    host_earnings_sats_30d: number | null
}> {
    const res = await fetch(`${gon.api.teams}/${teamId}/card_stats`, {
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
        },
    })

    if (!res.ok)
        return {
            members_count: null,
            host_name: null,
            upvalue_sats_30d: null,
            host_earnings_sats_30d: null,
        }
    return res.json()
}

export async function getTeam(teamId) {
    const res = await fetch(`${gon.api.teams}/${teamId}`, {
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
        },
    })

    if (abort401(res)) return null

    if (!res.ok) {
        const data = await res.json().catch(() => null)
        throw buildApiError(
            formatApiErrorData(data) || res.statusText || `Failed to load team ${teamId}`,
            res.status,
            data,
        )
    }

    const text = await res.text()
    if (!text) return null

    try {
        return JSON.parse(text)
    } catch (error) {
        throw buildApiError(`Invalid JSON response while loading team ${teamId}`, res.status, text)
    }
}

async function updateTeam(teamId, params) {
    try {
        let form
        if (params instanceof FormData) {
            form = params
        } else {
            form = new FormData()
            for (let key in params) {
                form.set(key, params[key])
            }
        }

        const res = await fetch(`${gon.api.teams}/${teamId}`, {
            method: 'put',
            credentials: 'same-origin',
            headers: {
                ...accessTokens(),
            },
            body: form,
        })

        const resOK = handleErrors(res)
        const data = await resOK.json()
        return data.team
    } catch (e) {
        return e
    }
}

async function deleteInvite(email, teamId) {
    const queryParams = new URLSearchParams({
        email,
    })
    const res = await fetch(`${gon.api.teams}/${teamId}/delete-invite?${queryParams}`, {
        method: 'DELETE',
        headers: {
            ...accessTokens(),
            'Content-Type': 'application/json',
        },
    })

    const resOK = handleErrors(res)
    const data = await resOK.json()
    return data.team
}

async function addToTeam(
    {
        emails = [],
        ids = [],
        names = [],
    }: { emails?: string[]; ids?: Array<number | string>; names?: string[] },
    teamId,
) {
    const body = {
        by_email: emails,
        by_id: ids,
        by_name: names,
    }
    const res = await fetch(`${gon.api.teams}/${teamId}/invite`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': gon.formToken,
        },
        body: JSON.stringify(body),
    })
    return res.json()
}

async function acceptTeamInvite(teamId: string) {
    const res = await fetch(`${gon.api.teams}/${teamId}/accept_invite`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            ...commonHeaders(),
            ...accessTokens(),
        },
    })
    const resOK = handleErrors(res)
    return await resOK.json()
}

async function rejectTeamInvite(teamId: string) {
    const res = await fetch(`${gon.api.teams}/${teamId}/reject_invite`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            ...commonHeaders(),
            ...accessTokens(),
        },
    })
    const resOK = handleErrors(res)
    return await resOK.json()
}

async function removeFromTeam(teamId, userId) {
    const queryParams = new URLSearchParams({
        user_id: userId,
    })
    const res = await fetch(`${gon.api.teams}/${teamId}/remove-user?${queryParams}`, {
        method: 'DELETE',
        headers: {
            ...commonHeaders(),
            ...accessTokens(),
        },
    })
    const data = await res.json()
    return data.team
}

async function leaveTeam(teamId: string) {
    const res = await fetch(`${gon.api.teams}/${teamId}/leave`, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: {
            ...commonHeaders(),
            ...accessTokens(),
        },
    })
    const resOK = handleErrors(res)
    return await resOK.json()
}

async function hideTeam(teamId: string) {
    const res = await fetch(`${gon.api.teams}/${teamId}/hide`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            ...commonHeaders(),
            ...accessTokens(),
        },
    })

    if (!res.ok) {
        throw new Error('Failed to hide team')
    }

    return res
}

async function unhideTeam(teamId: string) {
    const res = await fetch(`${gon.api.teams}/${teamId}/unhide`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            ...commonHeaders(),
            ...accessTokens(),
        },
    })

    if (!res.ok) {
        throw new Error('Failed to unhide team')
    }

    return res
}

async function pinTeam(teamId: string) {
    const res = await fetch(`${gon.api.teams}/${teamId}/pin`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            ...commonHeaders(),
            ...accessTokens(),
        },
    })

    if (!res.ok) {
        throw new Error('Failed to pin team')
    }

    return res
}

async function unpinTeam(teamId: string) {
    const res = await fetch(`${gon.api.teams}/${teamId}/unpin`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            ...commonHeaders(),
            ...accessTokens(),
        },
    })

    if (!res.ok) {
        throw new Error('Failed to unpin team')
    }

    return res
}

async function updateTeamSeedSelection(teamId: string, seedSelected: boolean) {
    const res = await fetch(`${gon.api.teams}/${teamId}/seed_selection`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            ...commonHeaders(),
            ...accessTokens(),
        },
        body: JSON.stringify({ seed_selected: seedSelected }),
    })

    const resOK = handleErrors(res)
    return await resOK.json()
}

async function updateTeamBanner(teamId: string, formData: FormData) {
    const endpoint = `${gon.api.teams}/${teamId}/update_banner`
    try {
        const res = await fetch(endpoint, {
            method: 'PATCH',
            credentials: 'same-origin',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'X-CSRF-Token': gon.formToken,
            },
            body: formData,
        })

        if (res.ok) {
            const data = await res.json()
            return data.team
        }

        // Some deployments reject multipart PATCH on this member route.
        // Fall back to the main team update endpoint, which also permits `banner_image`.
        return await updateTeam(teamId, formData)
    } catch (e) {
        console.error('Error updating team banner:', e)
        throw e
    }
}

async function getTeamInvitePreview(teamId: string) {
    const url = `${window.knovApiUrl}/api/v1/teams/${teamId}/invite_preview`
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
    })
    if (res.status === 403) return null
    if (!res.ok) return null
    const data = await res.json()
    return data.team
}

async function joinTeam(teamId: string) {
    const url = `${window.knovApiUrl}/api/v1/teams/${teamId}/join`
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
        },
        credentials: 'same-origin',
    })
    if (res.status === 401) {
        // Preserve attempted path and redirect to login
        redirectToLoginScreen()
        return null
    }
    if (res.status === 403) return { forbidden: true }
    if (!res.ok) return null
    return await res.json()
}

async function createSpaceInvitation(
    space_id,
    {
        emails = [],
        ids = [],
        names = [],
        standing = false,
    }: { emails?: string[]; ids?: string[]; names?: string[]; standing?: boolean },
) {
    const body = {
        space_id,
        by_email: emails,
        by_id: ids,
        by_name: names,
        standing,
    }
    const res = await fetch(`${gon.api.space_invitations}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            ...commonHeaders(),
            ...accessTokens(),
        },
        body: JSON.stringify(body),
    })
    const json = await res.json().catch(() => ({}))
    // Limit responses (too many invitees, daily cap) carry an error and no space.
    if (!res.ok || !json.space) return { error: json.error || 'Could not send invitations' }
    return json.space
}

async function getSpaceInvitationByToken(token) {
    const res = await fetch(`${window.knovApiUrl}/api/v1/space_invitation/${token}`, {
        headers: { ...commonHeaders(), ...accessTokens() },
    })
    if (res.ok) return (await res.json()).space_invitation
    else return null
}

async function acceptSpaceInvitation(invitationId) {
    const res = await fetch(`${gon.api.space_invitations}/${invitationId}/accept`, {
        method: 'POST',
        headers: { ...commonHeaders(), ...accessTokens() },
    })
    const data = await res.json()
    return data
}

async function deleteSpaceInvitation(invitationId) {
    const res = await fetch(`${gon.api.space_invitations}/${invitationId}`, {
        headers: { ...commonHeaders, ...accessTokens() },
        method: 'DELETE',
    })
    return (await res.json()).space
}

async function removeFromSpace(spaceId, userId) {
    const res = await fetch(`${gon.api.spaces}/${spaceId}/remove-user?user_id=${userId}`, {
        headers: { ...commonHeaders, ...accessTokens() },
        method: 'DELETE',
    })
    return (await res.json()).space
}

async function updateSpace(spaceId, params) {
    let form
    if (params instanceof FormData) {
        form = params
    } else {
        form = new FormData()
        for (let key in params) {
            form.set(key, params[key])
        }
    }

    const res = await fetch(`${gon.api.spaces}/${spaceId}`, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: {
            ...accessTokens(),
        },
        body: form,
    })
    const { space } = await res.json()
    return space
}

// Wallet bootstrap asks for these from several boot paths at once; share one
// request per user within WALLET_BOOT_SHARE_MS (lib/shareRecentRequest).
const WALLET_BOOT_SHARE_MS = 1000
const walletRequestKey = (name: string) => `${name}:${gon.currentUser?.id ?? 'guest'}`

async function getBsvBalance() {
    return shareRecentRequest(walletRequestKey('bsv-balance'), WALLET_BOOT_SHARE_MS, async () => {
        const res = await fetch(`${gon.api.bsv}/balance`, {
            method: 'GET',
            headers: { ...commonHeaders(), ...accessTokens() },
        })
        const data = await res.json()
        return data
    })
}

async function getBsvBalanceCached() {
    const res = await fetch(`${gon.api.bsv}/balance_cached`, {
        method: 'GET',
        headers: { ...commonHeaders(), ...accessTokens() },
    })
    return res.json()
}

async function getBsvFeeRate(): Promise<{ fee_rate: number; fallback?: boolean }> {
    const res = await fetch(`${gon.api.bsv}/fee_rate`, {
        method: 'GET',
        headers: { ...commonHeaders(), ...accessTokens() },
    })
    const data = await res.json()
    return data
}

async function getBsvAddress(options?: { enqueue?: boolean }) {
    const enqueue = options?.enqueue !== false
    const key = walletRequestKey(enqueue ? 'bsv-address' : 'bsv-address-no-enqueue')
    return shareRecentRequest(key, WALLET_BOOT_SHARE_MS, async () => {
        const url = new URL(`${gon.api.bsv}/address`, window.location.origin)
        if (!enqueue) {
            url.searchParams.set('enqueue', 'false')
        }
        const res = await fetch(url.toString(), {
            method: 'GET',
            headers: { ...commonHeaders(), ...accessTokens() },
        })
        const data = await res.json()
        return data
    })
}

async function getAnswerPaymentReceipts(answerId: string): Promise<PaymentReceipt[]> {
    const response = await fetch(
        `${gon.api.answers}/${encodeURIComponent(answerId)}/payment_receipts`,
        {
            headers: { ...accessTokens() },
        },
    )
    if (!response.ok) throw new Error('Payment status could not be loaded')
    return (await response.json()).payment_receipts
}

export interface SendBsvResult {
    payment_receipt?: PaymentReceipt
    status: string
    payment_id?: string
    // The payment_id the client actually sent, echoed back so callers can correlate the
    // real-time bsv_payment_update stream even on the legacy path (which omits payment_id).
    paymentId?: string
    workflow_id?: string
    run_id?: string
    access_token?: string
    tx_id?: string
    request_id?: string
    error?: string
    httpStatus?: number
}

async function sendBsv(
    destinationAddress: string,
    amountSats: number,
    additionalPayments?: Array<{ amount: number; addr: string }>,
    toTreechatUsers?: string[],
    paymentId?: string,
    deviceId?: string,
    includeReceipt = false,
): Promise<SendBsvResult> {
    const url = `${gon.api.bsv}/send_bsv`
    // Resolve the payment_id once and echo it back on every result branch so callers can
    // correlate the bsv_payment_update stream and safely retry with the SAME id (idempotent).
    const usedPaymentId = paymentId || crypto.randomUUID()
    const payload: {
        destination_address: string
        amount_sats: number
        additional_payments?: Array<{ amount: number; addr: string }>
        to_treechat_users?: string[]
        payment_id?: string
        device_id?: string
        include_receipt?: boolean
    } = {
        destination_address: destinationAddress,
        amount_sats: amountSats,
        payment_id: usedPaymentId,
    }

    if (additionalPayments) {
        payload.additional_payments = additionalPayments
    }

    if (toTreechatUsers) {
        payload.to_treechat_users = toTreechatUsers
    }

    if (deviceId) {
        payload.device_id = deviceId
    }
    if (includeReceipt) payload.include_receipt = true

    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                ...accessTokens(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        })

        let body: Partial<SendBsvResult> = {}
        try {
            body = await res.json()
        } catch (_) {
            // Non-JSON response (e.g. 502 HTML). Treated as an error below.
        }

        if (!res.ok) {
            return {
                status: body.status && body.status !== 'error' ? body.status : 'error',
                error: body.error || `Request failed (${res.status})`,
                httpStatus: res.status,
                paymentId: usedPaymentId,
            }
        }

        return { ...body, status: body.status || 'ok', paymentId: usedPaymentId }
    } catch (err) {
        console.log(err)
        return {
            status: 'error',
            error: err instanceof Error ? err.message : 'Unknown error',
            paymentId: usedPaymentId,
        }
    }
}

export interface OrdinalWalletActionResult {
    status:
        | 'temporal_pending'
        | 'pending'
        | 'awaiting_unlock'
        | 'completed'
        | 'failed'
        | 'aborted'
        | 'conflict'
        | 'error'
        // The payment may or may not have gone through; treat it as possibly paid.
        | 'unknown'
    payment_id?: string
    tx_id?: string | null
    beef?: string | null
    // On a failed deferred action: its coins are free again.
    released?: boolean
    /** A deferred action's unsigned transaction (Atomic BEEF hex) for the ordinal to unlock. */
    signable_beef?: string
    error?: string
    /** Set when the server answered with an error; absent when the request never got an answer. */
    httpStatus?: number
}

// An HTML ordinal's BRC-100 createAction, after the viewer confirmed it.
async function createOrdinalWalletAction(params: {
    origin: string
    paymentId: string
    outputs: Array<{ lockingScript: string; satoshis: number }>
    deviceId?: string
    /** A deferred action: inputs the ordinal unlocks itself, and the BEEF they come from. */
    inputs?: Array<{ txid: string; vout: number; unlockingScriptLength: number; sequence: number }>
    inputBeef?: string
}): Promise<OrdinalWalletActionResult> {
    try {
        const res = await fetch(`${gon.api.bsv}/ordinal_wallet/actions`, {
            method: 'POST',
            headers: { ...accessTokens(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
                origin: params.origin,
                payment_id: params.paymentId,
                device_id: params.deviceId,
                outputs: params.outputs.map(output => ({
                    locking_script: output.lockingScript,
                    satoshis: output.satoshis,
                })),
                ...(params.inputs
                    ? {
                          deferred: true,
                          input_beef: params.inputBeef,
                          inputs: params.inputs.map(input => ({
                              outpoint: `${input.txid}.${input.vout}`,
                              unlocking_script_length: input.unlockingScriptLength,
                              sequence: input.sequence,
                          })),
                      }
                    : {}),
            }),
        })
        const body = await res.json().catch(() => ({}))
        if (!res.ok) {
            return {
                status: body.status === 'conflict' ? 'conflict' : 'error',
                error: body.error,
                httpStatus: res.status,
            }
        }
        return body
    } catch (err) {
        return { status: 'error', error: err instanceof Error ? err.message : 'Request failed' }
    }
}

async function postOrdinalWalletAction(
    paymentId: string,
    step: 'sign' | 'abort',
    body: Record<string, unknown>,
): Promise<OrdinalWalletActionResult> {
    try {
        const res = await fetch(
            `${gon.api.bsv}/ordinal_wallet/actions/${encodeURIComponent(paymentId)}/${step}`,
            {
                method: 'POST',
                headers: { ...accessTokens(), 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            },
        )
        const json = await res.json().catch(() => ({}))
        if (!res.ok) {
            return {
                status: 'error',
                error: json.error || `Request failed (${res.status})`,
                httpStatus: res.status,
            }
        }
        return json
    } catch (err) {
        return { status: 'error', error: err instanceof Error ? err.message : 'Request failed' }
    }
}

// signAction: the ordinal's unlocking scripts (hex) by input index.
async function signOrdinalWalletAction(
    paymentId: string,
    spends: Record<string, string>,
): Promise<OrdinalWalletActionResult> {
    return postOrdinalWalletAction(paymentId, 'sign', { spends })
}

/** declined: the viewer cancelled the confirm tray; the payment is recorded as cancelled. */
async function abortOrdinalWalletAction(
    paymentId: string,
    options: { declined?: boolean } = {},
): Promise<OrdinalWalletActionResult> {
    return postOrdinalWalletAction(paymentId, 'abort', options.declined ? { declined: true } : {})
}

async function getOrdinalWalletAction(paymentId: string): Promise<OrdinalWalletActionResult> {
    try {
        const res = await fetch(
            `${gon.api.bsv}/ordinal_wallet/actions/${encodeURIComponent(paymentId)}`,
            { headers: { ...accessTokens() } },
        )
        const body = await res.json().catch(() => ({}))
        if (!res.ok)
            return {
                status: 'error',
                error: body.error || `Request failed (${res.status})`,
                httpStatus: res.status,
            }
        return body
    } catch (err) {
        return { status: 'error', error: err instanceof Error ? err.message : 'Request failed' }
    }
}

export interface OrdinalWalletAddressUser {
    address: string
    user: { id: number; username: string | null; avatar_url: string | null } | null
}

/** The Treechat accounts behind payment addresses, for an ordinal that names who took part
 * in it. Throws when the lookup fails. */
async function getOrdinalWalletUsers(addresses: string[]): Promise<OrdinalWalletAddressUser[]> {
    const query = addresses.map(address => `addresses[]=${encodeURIComponent(address)}`).join('&')
    const res = await fetch(`${gon.api.bsv}/ordinal_wallet/users_by_address?${query}`, {
        headers: { ...accessTokens() },
    })
    const body = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`)
    return Array.isArray(body.users) ? body.users : []
}

async function sendTreechatFee(amountSats: number) {
    const url = `${gon.api.bsv}/send_treechat_fee`
    const payload = {
        amount_sats: amountSats,
    }

    return fetch(url, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    })
        .then(res => res.json())
        .catch(err => {
            console.log(err)
        })
}

// In credits mode this is a credit top-up for amountCents; in card billing
// mode it sets up the card.
async function createStripeAiBillingCheckoutSession(amountCents?: number) {
    const url = `${window.knovApiUrl}/api/v1/stripe_ai_billing/checkout`
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(amountCents ? { amount_cents: amountCents } : {}),
    })
    return res.json()
}

async function setStripeAiAutoReload(settings: {
    enabled?: boolean
    reload_cents?: number
    threshold_cents?: number
}) {
    const url = `${window.knovApiUrl}/api/v1/stripe_ai_billing/auto_reload`
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
    })
    return res.json()
}

async function syncStripeAiBillingCheckoutSession(sessionId: string) {
    const url = `${window.knovApiUrl}/api/v1/stripe_ai_billing/sync?session_id=${encodeURIComponent(
        sessionId,
    )}`
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
        },
    })
    const contentType = res.headers.get('content-type') || ''
    if (!contentType.includes('application/json')) {
        const text = await res.text()
        return { status: 'error', error: text || `HTTP ${res.status}` }
    }
    return res.json()
}

async function getStripeAiBillingStatus(refresh: boolean = false) {
    const query = refresh ? '?refresh=1' : ''
    const url = `${window.knovApiUrl}/api/v1/stripe_ai_billing/status${query}`
    const res = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
            'Cache-Control': 'no-store',
            Pragma: 'no-cache',
        },
    })
    return res.json()
}

async function setStripeAiPaymentMode(mode: 'bsv' | 'stripe_metered') {
    const url = `${window.knovApiUrl}/api/v1/stripe_ai_billing/mode`
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mode }),
    })
    return res.json()
}

type GuardarianEstimateParams = {
    fromCurrency: string
    toCurrency: string
    amount: number
}

async function guardarianEstimate(params: GuardarianEstimateParams) {
    const url = `${window.knovApiUrl}/api/v1/guardarian/estimate?from_currency=${encodeURIComponent(
        params.fromCurrency,
    )}&to_currency=${encodeURIComponent(params.toCurrency)}&amount=${encodeURIComponent(
        String(params.amount),
    )}`
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
        },
    })
    return res.json()
}

type GuardarianCreateTransactionParams = {
    fromCurrency: string
    toCurrency: string
    amount: number
    address: string
    email?: string
    redirectUrl?: string
    successUrl?: string
    failureUrl?: string
}

async function parseGuardarianResponse(res: Response, action: string) {
    const body = await res.json().catch(() => ({}))
    if (!res.ok) {
        const err =
            (body && (body.error || body.message)) || `${action} failed with status ${res.status}`
        throw new Error(err)
    }
    return body
}

async function guardarianCreateTransaction(params: GuardarianCreateTransactionParams) {
    const url = `${window.knovApiUrl}/api/v1/guardarian/transactions`
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            from_currency: params.fromCurrency,
            to_currency: params.toCurrency,
            amount: params.amount,
            address: params.address,
            email: params.email,
            redirect_url: params.redirectUrl,
            success_url: params.successUrl,
            failure_url: params.failureUrl,
        }),
    })
    return parseGuardarianResponse(res, 'Guardarian create transaction')
}

async function guardarianGetTransaction(id: string) {
    const url = `${window.knovApiUrl}/api/v1/guardarian/transactions/${encodeURIComponent(id)}`
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
        },
    })
    return parseGuardarianResponse(res, 'Guardarian get transaction')
}

async function guardarianListTransactions(limit: number = 20) {
    const url = `${window.knovApiUrl}/api/v1/guardarian/transactions?limit=${limit}`
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
        },
    })
    return parseGuardarianResponse(res, 'Guardarian list transactions')
}

async function sendUpvalue(
    answerId: string,
    amountSats: number,
    isBoost?: boolean,
    upvalueId?: string,
    reactionEmoji?: string,
    deviceId?: string,
    observedAnswerSnapshotSha256?: string,
    isSeed?: boolean,
    artifactOrdinalId?: string,
    nowPlayingAnswerId?: string,
) {
    type UpvalueRequestError = Error & {
        statusCode?: number
        apiStatus?: string
        retryable?: boolean
        minSeedSats?: number
    }
    const makeUpvalueError = (
        message: string,
        extra: Partial<UpvalueRequestError> = {},
    ): UpvalueRequestError => {
        const err = new Error(message) as UpvalueRequestError
        Object.assign(err, extra)
        return err
    }

    const url = `${gon.api.answers}/${answerId}/bsv_upvalue`
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            ...commonHeaders(),
            ...accessTokens(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(
            omitBy(
                {
                    id: answerId,
                    sats: amountSats,
                    is_boost: isBoost,
                    upvalue_id: upvalueId,
                    reaction_emoji: reactionEmoji,
                    device_id: deviceId,
                    observed_answer_snapshot_sha256: observedAnswerSnapshotSha256,
                    is_seed: isSeed,
                    // Artifact-surface context: routes the payout to the
                    // ordinal's current owner instead of the answer author.
                    bsv_ordinal_id: artifactOrdinalId,
                    // Dynamic playlist: the song playing at press time —
                    // credits the song and posts "@user is playing …".
                    now_playing_answer_id: nowPlayingAnswerId,
                },
                value => value === undefined,
            ),
        ),
    })

    let data = null
    try {
        data = await res.json()
    } catch (e) {}

    if (!res.ok) {
        const message = data?.error || data?.message || `Failed to send upvalue (${res.status})`
        const apiStatus = data?.status
        const retryable =
            apiStatus === 'seed_in_progress' || apiStatus === 'ordinal_owner_refreshing'
                ? true
                : apiStatus === 'temporal_unavailable'
                ? false
                : res.status === 404 ||
                  apiStatus === 'answer_not_ready' ||
                  apiStatus === 'not_found'
        throw makeUpvalueError(message, {
            statusCode: res.status,
            apiStatus,
            retryable,
            minSeedSats: data?.min_seed_sats,
        })
    }

    if (
        data?.status === 'failed' ||
        data?.status === 'not_found' ||
        data?.status === 'answer_not_ready'
    ) {
        const message = data?.error || data?.message || 'Failed to send upvalue'
        throw makeUpvalueError(message, {
            statusCode: res.status,
            apiStatus: data?.status,
            retryable: data?.status === 'not_found' || data?.status === 'answer_not_ready',
        })
    }

    offerChannelJoinInvite(data?.join_invite)
    return data
}

// Free (non-monetary) reaction: the first press of the react button.
async function sendReaction(answerId: string, emoji: string, questId?: string | null) {
    const url = `${gon.api.answers}/${answerId}/reaction`
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            ...commonHeaders(),
            ...accessTokens(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ emoji, quest_id: questId || undefined }),
    })

    let data = null
    try {
        data = await res.json()
    } catch (e) {}

    if (!res.ok) {
        const message = data?.error || data?.message || `Failed to send reaction (${res.status})`
        const err = new Error(message) as Error & { statusCode?: number; apiStatus?: string }
        err.statusCode = res.status
        err.apiStatus = data?.status
        throw err
    }

    return data
}

async function removeReaction(answerId: string) {
    const url = `${gon.api.answers}/${answerId}/reaction`
    const res = await fetch(url, {
        method: 'DELETE',
        headers: {
            ...commonHeaders(),
            ...accessTokens(),
            'Content-Type': 'application/json',
        },
    })

    if (!res.ok) {
        throw new Error(`Failed to remove reaction (${res.status})`)
    }

    return res.json().catch(() => ({}))
}

async function broadcastBsvTx(rawTx: string, fund: boolean = false) {
    const url = `${gon.api.bsv}/broadcast_tx`
    return fetch(url, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(omitBy({ raw_tx: rawTx, fund }, value => !value)),
    })
        .then(res => res.json())
        .catch(err => {
            console.log(err)
        })
}

async function ensureBsvWallet() {
    return shareRecentRequest(walletRequestKey('ensure-wallet'), WALLET_BOOT_SHARE_MS, async () => {
        const res = await fetch(`${gon.api.bsv}/ensure-wallet`, {
            method: 'GET',
            headers: { ...commonHeaders(), ...accessTokens() },
        })
        const data = await res.json()
        return data
    })
}

// purpose 'local_signing_shadow' (LocalSigningShadow) and 'seed_words' fetch the wallet
// into the browser without counting as a backup download.
async function downloadBsvWallet(purpose?: 'local_signing_shadow') {
    const query = purpose ? `?purpose=${purpose}` : ''
    const response = await fetch(`${gon.api.bsv}/download-wallet${query}`, {
        method: 'GET',
        headers: { ...commonHeaders(), ...accessTokens() },
    })
    if (response.ok && response.headers.get('Content-Type')?.includes('application/json')) {
        return await response.blob()
    } else {
        throw new Error('Failed to download wallet')
    }
}

// ============================================================================
// BSV Self-Custody: Server Share API
// ============================================================================
// These methods manage the server's portion of the 2-of-2 Shamir secret shares
// for BSV private keys.

/**
 * Retrieve the server's BSV Shamir share for a user+device.
 *
 * @param userId - The user's ID (not used in request, auth determines user)
 * @param deviceId - The device ID
 * @returns The server share record, or null if not found
 */
async function getBsvServerShare(
    userId: string,
    deviceId: string,
): Promise<{ treechatShare: string; address: string } | null> {
    const url = `${gon.api.bsv}/server_share?device_id=${encodeURIComponent(deviceId)}`

    try {
        const res = await fetch(url, {
            method: 'GET',
            headers: {
                ...commonHeaders(),
                ...accessTokens(),
            },
        })

        if (!res.ok) {
            if (res.status !== 404) {
                console.error(`>>> getBsvServerShare: ${res.status} ${res.statusText}`)
            }
            return null
        }

        const data = await res.json()
        return {
            treechatShare: data.treechat_share,
            address: data.address,
        }
    } catch (error) {
        console.error('>>> getBsvServerShare error:', error)
        return null
    }
}

/**
 * Retrieve spendable wallet UTXOs for the current user through the backend BSV service.
 *
 * This intentionally goes through Treechat plumbing instead of direct browser calls to
 * third-party providers so client signing benefits from the same provider race/fallbacks.
 */
async function getBsvUtxos(): Promise<
    Array<{
        tx_hash: string
        tx_pos: number
        value: number
        height: number
        status?: string
        scriptPubKey: string
    }>
> {
    const url = `${gon.api.bsv}/utxos`

    try {
        const res = await fetch(url, {
            method: 'GET',
            headers: {
                ...commonHeaders(),
                ...accessTokens(),
            },
        })

        if (!res.ok) {
            console.error(`>>> getBsvUtxos: ${res.status} ${res.statusText}`)
            return []
        }

        const data = await res.json()
        return data.utxos || []
    } catch (error) {
        console.error('>>> getBsvUtxos error:', error)
        return []
    }
}

/**
 * Register or update the server's BSV Shamir share for a user+device.
 *
 * @param deviceId - The device ID
 * @param treechatShare - The server-side Shamir share for this device
 * @returns Result with success status and derived address
 */
async function registerBsvServerShare(
    deviceId: string,
    treechatShare: string,
    walletIdentity?: { paymentAddress: string; ordinalsAddress: string },
): Promise<{ success: boolean; address?: string; ordinalsAddress?: string; error?: string }> {
    const url = `${gon.api.bsv}/server_share`

    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                ...commonHeaders(),
                ...accessTokens(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                device_id: deviceId,
                treechat_share: treechatShare,
                payment_address: walletIdentity?.paymentAddress,
                ordinals_address: walletIdentity?.ordinalsAddress,
            }),
        })

        const data = await res.json()
        if (!res.ok) {
            console.error(`>>> registerBsvServerShare: ${res.status}`, data)
            return {
                success: false,
                error: data.error || 'Failed to register server share',
            }
        }

        return {
            success: true,
            address: data.address,
            ordinalsAddress: data.ordinals_address,
        }
    } catch (error) {
        console.error('>>> registerBsvServerShare error:', error)
        return {
            success: false,
            error: error instanceof Error ? error.message : String(error),
        }
    }
}

// ============================================================================
// BSV Self-Custody: Device Transfer Session API
// ============================================================================

export interface BsvDeviceTransferSession {
    id: string
    status: string
    requesting_device_id: string
    approving_device_id: string | null
    requester_ephemeral_public_key: string
    approver_ephemeral_public_key: string | null
    requester_sas_confirmed: boolean
    approver_sas_confirmed: boolean
    requester_sas_proof: string | null
    approver_sas_proof: string | null
    payload_ready: boolean
    expires_at: string
    completed_at: string | null
}

interface BsvDeviceTransferPayload {
    encrypted_wallet_payload: string
    encrypted_wallet_nonce: string
    device_transfer_session: BsvDeviceTransferSession
}

function parseDeviceTransferSessionResponse(data: any): BsvDeviceTransferSession {
    return data.device_transfer_session
}

async function createBsvDeviceTransferSession(
    requestingDeviceId: string,
    requesterEphemeralPublicKey: string,
): Promise<{ session?: BsvDeviceTransferSession; error?: string }> {
    const url = `${gon.api.bsv}/device_transfer_sessions`

    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                ...commonHeaders(),
                ...accessTokens(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                requesting_device_id: requestingDeviceId,
                requester_ephemeral_public_key: requesterEphemeralPublicKey,
            }),
        })

        const data = await res.json()
        if (!res.ok) {
            console.error(`>>> createBsvDeviceTransferSession: ${res.status}`, data)
            return { error: data.error || 'Failed to create device transfer session' }
        }

        return { session: parseDeviceTransferSessionResponse(data) }
    } catch (error) {
        console.error('>>> createBsvDeviceTransferSession error:', error)
        return { error: error instanceof Error ? error.message : String(error) }
    }
}

async function getBsvDeviceTransferSession(
    sessionId: string,
): Promise<{ session?: BsvDeviceTransferSession; error?: string; status?: number }> {
    const url = `${gon.api.bsv}/device_transfer_sessions/${encodeURIComponent(sessionId)}`

    try {
        const res = await fetch(url, {
            method: 'GET',
            headers: {
                ...commonHeaders(),
                ...accessTokens(),
            },
        })

        const data = await res.json()
        if (!res.ok) {
            console.error(`>>> getBsvDeviceTransferSession: ${res.status}`, data)
            return {
                error: data.error || 'Failed to fetch device transfer session',
                status: res.status,
            }
        }

        return { session: parseDeviceTransferSessionResponse(data) }
    } catch (error) {
        console.error('>>> getBsvDeviceTransferSession error:', error)
        return { error: error instanceof Error ? error.message : String(error) }
    }
}

async function acceptBsvDeviceTransferSession(
    sessionId: string,
    approvingDeviceId: string,
    approverEphemeralPublicKey: string,
): Promise<{ session?: BsvDeviceTransferSession; error?: string; status?: number }> {
    const url = `${gon.api.bsv}/device_transfer_sessions/${encodeURIComponent(sessionId)}/accept`

    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                ...commonHeaders(),
                ...accessTokens(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                approving_device_id: approvingDeviceId,
                approver_ephemeral_public_key: approverEphemeralPublicKey,
            }),
        })

        const data = await res.json()
        if (!res.ok) {
            console.error(`>>> acceptBsvDeviceTransferSession: ${res.status}`, data)
            return {
                error: data.error || 'Failed to accept device transfer session',
                session: data.device_transfer_session,
                status: res.status,
            }
        }

        return { session: parseDeviceTransferSessionResponse(data) }
    } catch (error) {
        console.error('>>> acceptBsvDeviceTransferSession error:', error)
        return { error: error instanceof Error ? error.message : String(error) }
    }
}

async function confirmBsvDeviceTransferSessionSas(
    sessionId: string,
    deviceId: string,
    sasProof: string,
): Promise<{ session?: BsvDeviceTransferSession; error?: string; status?: number }> {
    const url = `${gon.api.bsv}/device_transfer_sessions/${encodeURIComponent(
        sessionId,
    )}/confirm_sas`

    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                ...commonHeaders(),
                ...accessTokens(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                device_id: deviceId,
                sas_proof: sasProof,
            }),
        })

        const data = await res.json()
        if (!res.ok) {
            console.error(`>>> confirmBsvDeviceTransferSessionSas: ${res.status}`, data)
            return {
                error: data.error || 'Failed to confirm device transfer SAS',
                session: data.device_transfer_session,
                status: res.status,
            }
        }

        return { session: parseDeviceTransferSessionResponse(data) }
    } catch (error) {
        console.error('>>> confirmBsvDeviceTransferSessionSas error:', error)
        return { error: error instanceof Error ? error.message : String(error) }
    }
}

async function uploadBsvDeviceTransferPayload(
    sessionId: string,
    deviceId: string,
    encryptedWalletPayload: string,
    encryptedWalletNonce: string,
): Promise<{ session?: BsvDeviceTransferSession; error?: string; status?: number }> {
    const url = `${gon.api.bsv}/device_transfer_sessions/${encodeURIComponent(
        sessionId,
    )}/upload_payload`

    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                ...commonHeaders(),
                ...accessTokens(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                device_id: deviceId,
                encrypted_wallet_payload: encryptedWalletPayload,
                encrypted_wallet_nonce: encryptedWalletNonce,
            }),
        })

        const data = await res.json()
        if (!res.ok) {
            console.error(`>>> uploadBsvDeviceTransferPayload: ${res.status}`, data)
            return {
                error: data.error || 'Failed to upload encrypted wallet payload',
                session: data.device_transfer_session,
                status: res.status,
            }
        }

        return { session: parseDeviceTransferSessionResponse(data) }
    } catch (error) {
        console.error('>>> uploadBsvDeviceTransferPayload error:', error)
        return { error: error instanceof Error ? error.message : String(error) }
    }
}

async function getBsvDeviceTransferPayload(
    sessionId: string,
    deviceId: string,
): Promise<{ payload?: BsvDeviceTransferPayload; error?: string; status?: number }> {
    const url = `${gon.api.bsv}/device_transfer_sessions/${encodeURIComponent(
        sessionId,
    )}/payload?device_id=${encodeURIComponent(deviceId)}`

    try {
        const res = await fetch(url, {
            method: 'GET',
            headers: {
                ...commonHeaders(),
                ...accessTokens(),
            },
        })

        const data = await res.json()
        if (!res.ok) {
            console.error(`>>> getBsvDeviceTransferPayload: ${res.status}`, data)
            return {
                error: data.error || 'Failed to fetch encrypted wallet payload',
                status: res.status,
            }
        }

        return {
            payload: {
                encrypted_wallet_payload: data.encrypted_wallet_payload,
                encrypted_wallet_nonce: data.encrypted_wallet_nonce,
                device_transfer_session: data.device_transfer_session,
            },
        }
    } catch (error) {
        console.error('>>> getBsvDeviceTransferPayload error:', error)
        return { error: error instanceof Error ? error.message : String(error) }
    }
}

async function activateBsvDeviceTransferSession(
    sessionId: string,
    deviceId: string,
): Promise<{ session?: BsvDeviceTransferSession; error?: string; status?: number }> {
    const url = `${gon.api.bsv}/device_transfer_sessions/${encodeURIComponent(sessionId)}/activate`

    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                ...commonHeaders(),
                ...accessTokens(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                device_id: deviceId,
            }),
        })

        const data = await res.json()
        if (!res.ok) {
            console.error(`>>> activateBsvDeviceTransferSession: ${res.status}`, data)
            return {
                error: data.error || 'Failed to activate device transfer session',
                session: data.device_transfer_session,
                status: res.status,
            }
        }

        return { session: parseDeviceTransferSessionResponse(data) }
    } catch (error) {
        console.error('>>> activateBsvDeviceTransferSession error:', error)
        return { error: error instanceof Error ? error.message : String(error) }
    }
}

async function releaseBsvDeviceTransferSession(
    sessionId: string,
    deviceId: string,
): Promise<{ session?: BsvDeviceTransferSession; error?: string; status?: number }> {
    const url = `${gon.api.bsv}/device_transfer_sessions/${encodeURIComponent(sessionId)}/release`

    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                ...commonHeaders(),
                ...accessTokens(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                device_id: deviceId,
            }),
        })

        const data = await res.json()
        if (!res.ok) {
            console.error(`>>> releaseBsvDeviceTransferSession: ${res.status}`, data)
            return {
                error: data.error || 'Failed to release device transfer session',
                session: data.device_transfer_session,
                status: res.status,
            }
        }

        return { session: parseDeviceTransferSessionResponse(data) }
    } catch (error) {
        console.error('>>> releaseBsvDeviceTransferSession error:', error)
        return { error: error instanceof Error ? error.message : String(error) }
    }
}

async function cancelBsvDeviceTransferSession(
    sessionId: string,
    deviceId: string,
): Promise<{ session?: BsvDeviceTransferSession; error?: string; status?: number }> {
    const url = `${gon.api.bsv}/device_transfer_sessions/${encodeURIComponent(sessionId)}/cancel`

    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                ...commonHeaders(),
                ...accessTokens(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                device_id: deviceId,
            }),
        })

        const data = await res.json()
        if (!res.ok) {
            console.error(`>>> cancelBsvDeviceTransferSession: ${res.status}`, data)
            return {
                error: data.error || 'Failed to cancel device transfer session',
                session: data.device_transfer_session,
                status: res.status,
            }
        }

        return { session: parseDeviceTransferSessionResponse(data) }
    } catch (error) {
        console.error('>>> cancelBsvDeviceTransferSession error:', error)
        return { error: error instanceof Error ? error.message : String(error) }
    }
}

// ============================================================================
// BSV Self-Custody: Signing Request API
// ============================================================================

/**
 * Signing request as returned by the server
 */
interface SigningRequest {
    signing_request_id: string
    tx_type: string
    device_id: string
    unsigned_tx_template?: UnsignedTransactionTemplate
    ordinal_manifest?: OrdinalTransferManifest | OrdinalMintManifest
    attachment_id?: string
    market_manifest?: OrdinalMarketManifest
    market_approval?: OrdinalMarketApproval
    ordinal_id?: string
    answer_id: string
    author_address: string
    treechat_address: string
    total_amount: number
    author_amount: number
    treechat_amount: number
    is_boost: boolean
    expires_at: string
}

/**
 * Get pending signing requests for a device (polling fallback for missed ActionCable).
 *
 * @param deviceId - The device ID
 * @returns Array of pending signing requests
 */
async function getSigningRequests(deviceId: string): Promise<SigningRequest[]> {
    const url = `${gon.api.bsv}/signing_requests?device_id=${encodeURIComponent(deviceId)}`

    try {
        const res = await fetch(url, {
            method: 'GET',
            headers: {
                ...commonHeaders(),
                ...accessTokens(),
            },
        })

        if (!res.ok) {
            console.error(`>>> getSigningRequests: ${res.status} ${res.statusText}`)
            return []
        }

        const data = await res.json()
        return data.signing_requests || []
    } catch (error) {
        console.error('>>> getSigningRequests error:', error)
        return []
    }
}

/**
 * Submit a signed transaction back to the server.
 *
 * @param signingRequestId - The signing request ID
 * @param deviceId - The device ID
 * @param signedTxHex - The signed transaction in hex format
 * @returns Result with status
 */
/** Why this device couldn't sign one of its signing requests (diagnostics only). */
async function reportSigningFailure(
    signingRequestId: string,
    deviceId: string,
    failure: { error: string; retryable: boolean; visible: boolean },
): Promise<void> {
    await fetch(`${gon.api.bsv}/signing_requests/${signingRequestId}/failure`, {
        method: 'POST',
        headers: {
            ...commonHeaders(),
            ...accessTokens(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ device_id: deviceId, ...failure }),
    })
}

async function submitSignedTx(
    signingRequestId: string,
    deviceId: string,
    signedTxHex: string,
): Promise<{ success: boolean; error?: string }> {
    const url = `${gon.api.bsv}/signing_requests/${signingRequestId}/sign`

    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                ...commonHeaders(),
                ...accessTokens(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                device_id: deviceId,
                signed_tx: signedTxHex,
            }),
        })

        const data = await res.json()

        if (!res.ok) {
            console.error(`>>> submitSignedTx: ${res.status}`, data)
            return { success: false, error: data.error || 'Failed to submit signed tx' }
        }

        return { success: true }
    } catch (error) {
        console.error('>>> submitSignedTx error:', error)
        return { success: false, error: String(error) }
    }
}

/** Streams the spent ordinal's locking script for a pending send or market ask/accept. */
async function openSigningRequestOrdinalPrevoutScript(
    signingRequestId: string,
    deviceId: string,
): Promise<OrdinalPrevoutScript> {
    const url = `${gon.api.bsv}/signing_requests/${encodeURIComponent(
        signingRequestId,
    )}/ordinal_prevout_script?device_id=${encodeURIComponent(deviceId)}`
    const response = await fetch(url, {
        headers: { ...commonHeaders(), ...accessTokens() },
    })
    if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        throw new Error(body.error || 'Failed to load ordinal signing script')
    }
    if (!response.body) throw new Error('Ordinal signing script stream is unavailable')
    return ordinalPrevoutScriptFromFramedStream(response.body)
}

async function getSigningRequestMarketContext(
    signingRequestId: string,
    deviceId: string,
): Promise<{
    ordinal_prevout_script_hex?: string
    parent_transaction_hex?: string
}> {
    const url = `${gon.api.bsv}/signing_requests/${encodeURIComponent(
        signingRequestId,
    )}/market_context?device_id=${encodeURIComponent(deviceId)}`
    const response = await fetch(url, {
        headers: { ...commonHeaders(), ...accessTokens() },
    })
    const body = await response.json()
    if (!response.ok) throw new Error(body.error || 'Failed to load market signing context')
    return body
}

async function submitOrdinalSignatures(
    signingRequestId: string,
    deviceId: string,
    ordinalSignatures: Array<
        OrdinalTransferSignature | OrdinalMintSignature | OrdinalMarketSignature
    >,
): Promise<{ success: boolean; error?: string }> {
    const url = `${gon.api.bsv}/signing_requests/${encodeURIComponent(signingRequestId)}/sign`
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: { ...commonHeaders(), ...accessTokens(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ device_id: deviceId, ordinal_signatures: ordinalSignatures }),
        })
        const body = await response.json()
        return response.ok
            ? { success: true }
            : { success: false, error: body.error || 'Failed to submit ordinal signatures' }
    } catch (error) {
        return { success: false, error: error instanceof Error ? error.message : String(error) }
    }
}

interface IShareDestinationParams {
    type: 'stream' | 'recent_thread'
    id?: string
    name?: string
}

async function clipLink({
    url,
    image,
    video,
    file,
    title,
    content,
    extraContent,
    destination,
    uploadedFile,
}: {
    url?: string
    image?: Uint8Array
    video?: Uint8Array
    file?: Uint8Array
    title?: string
    extraContent?: string
    content?: string
    destination?: IShareDestinationParams
    uploadedFile?: UploadedSharedFile
}) {
    // TODO: refactor backend endpoint and this to use normal json encodings
    // instead of all this form encoded stuff
    let form = new FormData()

    if (url) {
        form.append('quest[answers_attributes][0][url_attributes][address]', url)
        form.append('quest[answers_attributes][0][url_attributes][title]', title ? title : '')
    } else if (title) {
        form.append('quest[answers_attributes][0][url_attributes][title]', title ? title : '')
    }

    if (image) {
        let blob = new Blob([image])
        form.append('quest[answers_attributes][0][images]', blob)
    }
    if (video) {
        let blob = new Blob([video])
        form.append('quest[answers_attributes][0][recording]', blob)
    }
    if (file) {
        let blob = new Blob([file])
        form.append('quest[answers_attributes][0][files]', blob)
    }
    if (uploadedFile) {
        const attachmentField = attachmentFieldForUploadedShare(uploadedFile)
        form.append(`quest[answers_attributes][0][${attachmentField}]`, uploadedFile.signedId)
    }

    if (content) form.append('quest[answers_attributes][0][content]', content)

    if (destination) {
        form.append('destination[type]', destination.type)
        if (destination.id) form.append('destination[id]', destination.id.toString())
    }

    const response = await fetch(`${window.knovApiUrl}/plugin_new/clip`, {
        method: 'POST',
        headers: {
            ...accessTokens(),
        },
        body: form,
    })
    const responseBody = await response.json().catch(() => null)
    if (!response.ok) {
        const errorMessage =
            responseBody?.error || `Treechat could not attach the shared file (${response.status})`
        throw new Error(errorMessage)
    }
    return responseBody
}

// Most recently updated threads owned by the current user, with a text preview.
// Used by the publish modal's thread-history tab to pick a clip destination.
async function fetchRecentThreads(): Promise<Array<{ id: string; content: string }>> {
    const res = await fetch(`${window.knovApiUrl}/plugin_new/threads/recent`, {
        headers: {
            ...accessTokens(),
        },
    })
    if (!res.ok) throw new Error(`Failed to fetch recent threads: ${res.status}`)
    const threads = await res.json()
    return Array.isArray(threads) ? threads : []
}

async function upvoteAnswer(answerId: string) {
    const res = await fetch(`${window.knovApiUrl}/votes/register`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...accessTokens(),
        },
        body: JSON.stringify({
            value: 1,
            votable_id: answerId,
            votable_type: 'Answer',
        }),
    })
    const data = await res.json()
    if (data.success) {
        const answer = data.votable
        if (answer && data.user_vote !== undefined) {
            answer.user_vote = data.user_vote
        }
        cache.cacheAnswer(answer)
        return answer
    } else return null
}

async function votePoll(answerId: string, pollOptionId: string) {
    const res = await fetch(`${gon.api.answers}/${answerId}/poll_votes`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...accessTokens(),
        },
        body: JSON.stringify({
            poll_option_id: pollOptionId,
        }),
    })
    const data = await res.json()
    if (data.success && data.answer) {
        cache.cacheAnswer(data.answer)
        return data.answer
    }

    throw new Error(data?.error || 'Failed to vote on poll')
}

async function getBsvLockAmtForAnswer(answerId) {
    const url = `${gon.api.answers}/${answerId}/get_bsv_lock_amt`
    return fetch(url, {
        method: 'GET',
        headers: { ...accessTokens() },
    })
        .then(res => res.json())
        .catch(err => {
            console.log(err)
        })
}

/**
 * creates and returns a raw lock tx without signing or paying for it
 */
async function createLockTx(
    address: string,
    lockToTxId: string,
    amtToLock: number,
    blocksToLockFor: number,
    privateKeyWIF?: string,
    paymail?: string,
    appName?: string,
    sign?: boolean,
): Promise<string> {
    // validate required parameters
    if (!address || !lockToTxId || !amtToLock || !blocksToLockFor) {
        throw new Error(
            `Missing required parameters: ${[
                !address && 'address',
                !lockToTxId && 'lockToTxId',
                !amtToLock && 'amtToLock',
                !blocksToLockFor && 'blocksToLockFor',
            ]
                .filter(Boolean)
                .join(', ')} must be provided.`,
        )
    }

    const options = {
        ...(paymail && { paymail }),
        appName: appName ?? 'treechat',
        privateKeyWIF: sign ? privateKeyWIF : '',
    }
    const response = await fetch(`${gon.api.bsv}/create_lock_tx/${lockToTxId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            ...accessTokens(),
        },
        body: JSON.stringify({
            address: address,
            amt_to_lock: amtToLock,
            blocks_to_lock_for: blocksToLockFor,
            paymail: options.paymail,
            app_name: options.appName,
            // private_key_wif: options.privateKeyWIF,
            // sign: sign,
        }),
    })
    const result = await response.json()
    if (response.ok) {
        return result?.raw_tx
    } else {
        throw new Error(result.error || 'Failed to create lock transaction')
    }
}

async function lockAnswer(answerId, sats, blocks, lockTxId) {
    const url = `${gon.api.answers}/${answerId}/bsv_lock`
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            sats,
            blocks,
            lock_tx_id: lockTxId,
        }),
    })
    const data = await res.json()
    if (data.status === 'lock created') {
        cache.cacheAnswer(data?.answer)
        return data.answer
    } else {
        throw new Error(data.error)
    }
}

async function postToBsv(answerId) {
    const url = `${gon.api.answers}/${answerId}/post-to-bsv`
    return fetch(url, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            'Content-Type': 'application/json',
        },
    })
        .then(async res => {
            const data = await res.json()
            if (data.status === 'complete') {
                cache.cacheAnswer(data.answer)
            }
            return data
        })
        .catch(err => {
            console.log(err)
        })
}

async function getUser(id: string) {
    try {
        const res = await fetch(`${gon.api.users}/${id}`, {
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
            },
        })

        if (res.status === 404) {
            return null
        } else {
            const data = await res.json()
            return data.user
        }
    } catch (error) {
        console.error('Error fetching user data:', error)
        return null
    }
}

async function getUserByName(username: string) {
    try {
        const res = await fetch(
            `${gon.api.users}/name/${encodeURIComponent(username.toLowerCase())}`,
            {
                headers: {
                    ...accessTokens(),
                    Accept: 'application/json',
                },
            },
        )

        if (res.status === 404) {
            return null
        } else {
            const data = await res.json()
            return data.user
        }
    } catch (error) {
        console.error('Error fetching user data by name:', error)
        return null
    }
}

async function getUserByPandaPubkey(pubKey) {
    const url = `${gon.api.users}/by-panda-pubkey/${pubKey}`
    return fetch(url, {
        method: 'GET',
        headers: {
            ...accessTokens(),
        },
    })
        .then(response => response.json())
        .catch(err => console.log(err))
}

// proof: { pubkey, sig, msg, signature_scheme } from createYoursLoginProof; the server
// requires a fresh signature from the new key before it replaces the wallet login key.
async function setPandaPubkey(userId, proof) {
    const url = `${gon.api.users}/set-panda-pubkey`
    return fetch(url, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            pubkey: proof.pubkey,
            sig: proof.sig,
            msg: proof.msg,
            signature_scheme: proof.signature_scheme,
        }),
    }).catch(err => console.log(err))
}

async function muteUser(userId: string) {
    return fetch(`${gon.api.mutes}`, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
        body: JSON.stringify({ user_id: userId }),
    })
}

async function unmuteUser(userId: string) {
    return fetch(`${gon.api.mutes}/${userId}`, {
        method: 'DELETE',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })
}

async function muteQuest(questId: string) {
    return fetch(`${gon.api.quests}/${questId}/mute`, {
        method: 'PUT',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })
}

async function unmuteQuest(questId: string) {
    return fetch(`${gon.api.quests}/${questId}/unmute`, {
        method: 'PUT',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })
}

async function deleteProvider(providerId: string) {
    const res = await fetch(`${gon.api.providers}/${providerId}`, {
        method: 'DELETE',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })
    if (abort401(res)) return
    return await res.json()
}

async function muteStream(streamId: string) {
    return fetch(`${gon.api.stream_mutes}`, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
        body: JSON.stringify({ stream_id: streamId }),
    })
}

async function unmuteStream(streamId: string) {
    return fetch(`${gon.api.stream_mutes}/${streamId}`, {
        method: 'DELETE',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })
}

async function getMutedStreams() {
    const res = await fetch(`${gon.api.stream_mutes}`, {
        method: 'GET',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })
    if (abort401(res)) return []
    return await res.json()
}

async function createFragmentWalletTx(
    numFragments: number = 10,
    fragmentPercentage: number = 1.0,
    broadcast: boolean = true,
) {
    const url = `${gon.api.bsv}/create_fragment_wallet_tx`
    return fetch(url, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(
            omitBy(
                {
                    num_fragments: numFragments,
                    fragment_percentage: fragmentPercentage,
                    broadcast,
                },
                value => value === undefined,
            ),
        ),
    })
        .then(async res => {
            const data = await res.json()
            if (!res.ok) {
                throw new Error(data?.error || 'Failed to fragment wallet')
            }
            return data
        })
        .catch(err => {
            console.error('Error creating fragment wallet tx:', err)
            throw err
        })
}

async function getBlockHeight() {
    const url = `${gon.api.bsv}/blockheight`
    return fetch(url, {
        method: 'GET',
        headers: { ...accessTokens() },
    })
        .then(res => res.json())
        .catch(err => {
            console.error('Error getting block height:', err)
            throw err
        })
}

async function getBsvExchangeRate() {
    const url = `${gon.api.bsv}/exchangerate`
    return fetch(url, {
        method: 'GET',
        headers: { ...accessTokens() },
    })
        .then(res => res.json())
        .catch(err => {
            console.error('Error getting bsv exchange rate:', err)
            throw err
        })
}

async function getAiModels() {
    try {
        const res = await fetch(`${gon.api.ai_models}`, {
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
            },
        })

        if (!res.ok) {
            throw new Error(`Failed to fetch AI models: ${res.statusText}`)
        }

        const data = await res.json()
        return data
    } catch (error) {
        console.error('Error fetching AI models:', error)
        return []
    }
}

async function getActionDefinitions() {
    if (!gon.api.action_definitions) return []

    try {
        const res = await fetch(`${gon.api.action_definitions}`, {
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
            },
        })

        if (!res.ok) {
            throw new Error(`Failed to fetch action definitions: ${res.statusText}`)
        }

        const data = await res.json()
        return data
    } catch (error) {
        console.error('Error fetching action definitions:', error)
        return []
    }
}

// Add these types near the top with other interfaces
interface BsvUpvalue {
    id: string
    amount: string
    transaction_type: string
    created_at: string
    from_user_id: string
    to_user_id: string
    is_boost?: boolean
    is_agent_payment?: boolean
}

// Add this function with the other API methods
async function getBsvUpvalueHistory(userId?: string): Promise<BsvUpvalue[]> {
    const url = `${gon.api.bsv}/history${userId ? `?user_id=${userId}` : ''}`
    try {
        const response = await fetch(url, {
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
            },
        })
        if (!response.ok) {
            throw new Error('Failed to fetch BSV upvalue history')
        }
        const data = await response.json()
        return data.upvalues
    } catch (error) {
        console.error('Error fetching BSV upvalue history:', error)
        return []
    }
}

interface BsvUpvalueTotals {
    total: number
    received: number
    given: number
    boost: number
}

async function getBsvUpvalueTotal(userId?: string): Promise<BsvUpvalueTotals> {
    const url = `${gon.api.bsv}/total_upvalues${userId ? `?user_id=${userId}` : ''}`
    try {
        const response = await fetch(url, {
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
            },
        })
        if (!response.ok) {
            throw new Error('Failed to fetch BSV upvalue total')
        }
        return await response.json()
    } catch (error) {
        console.error('Error fetching BSV upvalue total:', error)
        return { total: 0, received: 0, given: 0, boost: 0 }
    }
}

export interface DailyEarnings {
    day: string
    sats_earned: number
    upvalue_count: number
    amount_display: string
}

export interface DailyEarningsToday extends DailyEarnings {
    share_id: string | null
}

export interface DailyEarningsShare extends DailyEarnings {
    id: string
    user?: { id: string; name: string | null }
}

async function getDailyEarningsToday(): Promise<DailyEarningsToday | null> {
    try {
        const response = await fetch(`${gon.api.daily_earnings_shares}/today`, {
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
            },
        })
        if (!response.ok) {
            throw new Error('Failed to fetch daily earnings')
        }
        return await response.json()
    } catch (error) {
        console.error('Error fetching daily earnings:', error)
        return null
    }
}

// Mints (or returns) the current user's share record — today's UTC day by
// default, or an explicit ISO day (the daily notification passes its own so
// a share tapped after the UTC rollover mints the announced day). Throws
// with the server's message so the tray can surface "nothing earned yet"
// instead of a generic failure.
async function createDailyEarningsShare(day?: string): Promise<DailyEarningsShare> {
    const response = await fetch(gon.api.daily_earnings_shares, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
        ...(day ? { body: JSON.stringify({ day }) } : {}),
    })
    const body = await response.json().catch(() => null)
    if (!response.ok) {
        throw new Error(body?.error || 'Failed to create earnings share')
    }
    return body
}

export interface DailyEarningsBackingPost {
    id: string
    quest_id: string | null
    answer_id: string | null
    stream_posted: boolean
}

// Ensures the share's hidden public backing post — the thread tracked share
// links point at — and reports whether it has been promoted to the public
// stream. Throws so the tray can fall back to the profile link.
async function ensureDailyEarningsBackingPost(shareId: string): Promise<DailyEarningsBackingPost> {
    const response = await fetch(
        `${gon.api.daily_earnings_shares}/${encodeURIComponent(shareId)}/backing_post`,
        {
            method: 'POST',
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
            },
        },
    )
    const body = await response.json().catch(() => null)
    if (!response.ok) {
        throw new Error(body?.error || 'Failed to prepare the share post')
    }
    return body
}

// "Post to public stream": unhides the backing post and bumps it to the top
// of the feed. Idempotent server-side, so repeat taps and tray reopens can't
// double-post.
async function promoteDailyEarningsPost(shareId: string): Promise<DailyEarningsBackingPost> {
    const response = await fetch(
        `${gon.api.daily_earnings_shares}/${encodeURIComponent(shareId)}/promote`,
        {
            method: 'POST',
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
            },
        },
    )
    const body = await response.json().catch(() => null)
    if (!response.ok) {
        throw new Error(body?.error || 'Failed to post to the public stream')
    }
    return body
}

async function getDailyEarningsShare(shareId: string): Promise<DailyEarningsShare | null> {
    try {
        const response = await fetch(`${gon.api.daily_earnings_shares}/${shareId}`, {
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
            },
        })
        if (!response.ok) {
            throw new Error('Failed to fetch earnings share')
        }
        return await response.json()
    } catch (error) {
        console.error('Error fetching earnings share:', error)
        return null
    }
}

export interface WalletSeedRow {
    answer_id: string
    quest_id: string | null
    author_name: string | null
    text_preview: string | null
    my_sats: number
    position: number | null
    pool_sats: number
    seed_count: number
    earned_back_sats: number
    pending: boolean
    first_seeded_at: string
}

export interface WalletSeedsResponse {
    totals: { staked: number; earned_back: number }
    seeds: WalletSeedRow[]
}

async function getWalletSeeds(): Promise<WalletSeedsResponse> {
    const url = `${gon.api.bsv}/seeds`
    try {
        const response = await fetch(url, {
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
            },
        })
        if (!response.ok) {
            throw new Error('Failed to fetch wallet seeds')
        }
        return await response.json()
    } catch (error) {
        console.error('Error fetching wallet seeds:', error)
        return { totals: { staked: 0, earned_back: 0 }, seeds: [] }
    }
}

interface BsvTdpResponse {
    total_bsv: number
    computed_on: string
    breakdown?: {
        upvalues_bsv?: number
        upvalues_sats?: number
        ai_fee_sats?: number
        ordinal_sales_sats?: number
    }
}

async function getBsvTdp(params?: { _t?: number }): Promise<BsvTdpResponse | null> {
    const qs = params?._t ? `?_t=${params._t}` : ''
    const url = `${gon.api.bsv}/tdp${qs}`
    try {
        const response = await fetch(url, {
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
            },
        })
        if (!response.ok) {
            throw new Error('Failed to fetch BSV TDP')
        }
        return await response.json()
    } catch (error) {
        console.error('Error fetching BSV TDP:', error)
        return null
    }
}

export interface BsvTdpStatsResponse {
    total_all_time_sats: number
    total_all_time_bsv: number
    range_added_sats: number
    range_added_bsv: number
    breakdown: {
        all_time: {
            upvalues_sats: number
            ai_fee_sats: number
            ordinal_sales_sats: number
        }
        range: {
            upvalues_sats: number
            ai_fee_sats: number
            ordinal_sales_sats: number
        }
    }
    range: {
        start_date?: string
        end_date?: string
        end_date_is_exclusive_utc_midnight: boolean
    }
    top_reaction_upvalues: Array<{
        emoji: string
        total_sats: number
        total_bsv: number
    }>
    computed_at_utc: string
    cache_version: number
    error?: string
}

async function getBsvTdpStats(params?: {
    start_date?: string
    end_date?: string
}): Promise<BsvTdpStatsResponse | null> {
    const qs = new URLSearchParams()
    if (params?.start_date) qs.set('start_date', params.start_date)
    if (params?.end_date) qs.set('end_date', params.end_date)
    const url = `${gon.api.bsv}/tdp_stats${qs.toString() ? `?${qs.toString()}` : ''}`

    try {
        const response = await fetch(url, {
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
            },
        })
        if (!response.ok) {
            throw new Error('Failed to fetch BSV TDP stats')
        }
        return await response.json()
    } catch (error) {
        console.error('Error fetching BSV TDP stats:', error)
        return null
    }
}

async function getBsvTdpShareImage(params: {
    range: 'today' | '1d' | '7d' | '1mo' | '1y' | 'all'
    theme: 'light' | 'dark'
}): Promise<Blob | null> {
    const qs = new URLSearchParams({
        range: params.range,
        theme: params.theme,
    })

    try {
        const response = await fetch(`${gon.api.bsv}/tdp_share_image?${qs.toString()}`, {
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
            },
        })

        if (!response.ok) {
            throw new Error('Failed to fetch TDP share image')
        }

        return await response.blob()
    } catch (error) {
        console.error('Error fetching TDP share image:', error)
        return null
    }
}

async function postHelloMessage(analyticsContext: Record<string, unknown> = {}) {
    let responseStatus: number | undefined
    let loggedClientError = false
    const logClientError = (props: Record<string, unknown> = {}) => {
        loggedClientError = true
        window.logEv('ERROR_NEW_USER_HELLO_MESSAGE_CLIENT', {
            ...analyticsContext,
            event_origin: 'client',
            status: responseStatus,
            ...props,
        })
    }

    try {
        const response = await fetch(`${window.knovApiUrl}/api/v1/users/hello-message`, {
            method: 'POST',
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
            },
            body: JSON.stringify({
                auth_surface: analyticsContext.auth_surface,
                signup_method: analyticsContext.signup_method,
                trigger_source: analyticsContext.trigger_source,
            }),
        })
        responseStatus = response.status
        const data = await response.json()

        if (!(data && data.success && data.quest)) {
            logClientError({
                response_success: data?.success,
                error_stage: 'api_response',
            })
            return
        }

        // Ensure quest is fully cached (including answers) for immediate render
        const hydratedQuest = await hydrateAnswersIntoQuest(data.quest)
        if (hydratedQuest) data.quest = hydratedQuest
        cacheQuestPreservingPendingOptimisticAnswers(data.quest)

        // Helper to inject the quest id into a given filter key
        const injectedFilterKeys = new Set<string>()
        const injectIntoFilterKey = (filterKey: string) => {
            if (injectedFilterKeys.has(filterKey)) return
            injectedFilterKeys.add(filterKey)
            queryClient.setQueryData(
                ['quests', filterKey],
                (oldData: QuestsInfiniteData | undefined) => {
                    if (!oldData || !oldData.pages) {
                        return {
                            pageParams: [1],
                            pages: [
                                {
                                    pinnedQuest: null,
                                    quests: [{ id: data.quest.id }],
                                    count: 1,
                                    has_more: true,
                                },
                            ],
                        }
                    }
                    const currentFirstPage = oldData.pages[0] || { quests: [] }
                    if (currentFirstPage.quests.some(q => q.id === data.quest.id)) return oldData

                    const firstPage = {
                        ...currentFirstPage,
                        count:
                            typeof currentFirstPage.count === 'number'
                                ? currentFirstPage.count + 1
                                : currentFirstPage.count,
                        quests: [{ id: data.quest.id }, ...currentFirstPage.quests],
                    }
                    return {
                        ...oldData,
                        pages: [firstPage, ...oldData.pages.slice(1)],
                    }
                },
            )
            prependQuestToNaturalFeedSession(filterKey, data.quest)
        }

        // 1) Center panel filter (if present)
        const centerPanelFilter = useStore.getState().panels?.state?.[startingIndex]?.filter
        if (centerPanelFilter)
            injectIntoFilterKey(getQuestsQueryKey(withDepth(centerPanelFilter))[1])

        // 2) Canonical Public stream filter
        injectIntoFilterKey(getQuestsQueryKey(withDepth({ public: true }))[1])

        // 3) Any active cached queries for public streams (with any depth/order variants)
        const allQueries = queryClient.getQueryCache().getAll()
        allQueries.forEach(q => {
            const key = q.queryKey as readonly unknown[]
            if (!Array.isArray(key) || key[0] !== 'quests' || typeof key[1] !== 'string') return
            try {
                const parsed = JSON.parse(key[1])
                if (parsed && parsed.public === true) injectIntoFilterKey(key[1])
            } catch (_) {
                // ignore parse errors for non-JSON keys
            }
        })

        window.logEv('CREATE_NEW_USER_HELLO_MESSAGE_CLIENT', {
            ...analyticsContext,
            event_origin: 'client',
            status: response.status,
            quest_id: data.quest.id,
        })
    } catch (error) {
        console.error('Error posting hello message:', error)
        if (!loggedClientError) {
            logClientError({
                error_stage: 'request_or_cache',
                error_name: error?.name,
                error_message: error?.message,
            })
        }
        throw error
    }
}

// The ordinals stream mints a fresh upload from a hidden, owner-only post
// instead of publishing it as the user's own thread (see
// BsvController#create_mint_source).
async function createMintSource(
    signedId: string,
): Promise<{ answer_id: string; quest_id: string }> {
    const response = await fetch(`${gon.api.bsv}/ordinals/mint_source`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...accessTokens(),
            ...commonHeaders(),
        },
        body: JSON.stringify({ signed_id: signedId }),
    })
    const body = await response.json().catch(() => null)
    if (!response.ok || !body?.answer_id) {
        throw new Error(body?.error || `Could not prepare the mint (${response.status})`)
    }
    return body
}

async function mintOrdinalTemporal(
    answer_id: string,
    options?: {
        treechat_fee_amount_sats?: number
        service_fee_quote?: string
        broadcast?: boolean
        media_type?: 'auto' | 'image' | 'video' | 'audio' | 'html'
        attachment_id?: string
        source_file?: File
    },
) {
    const url = `${gon.api.bsv}/ordinals/mint_temporal`
    try {
        const requestOptions = { ...(options || {}) }
        delete requestOptions.source_file

        const userId = gon.currentUser?.id
        const clientShare =
            userId && currentUserUsesClientSigning() ? await getClientShare(userId) : null
        if (userId && currentUserUsesClientSigning() && !clientShare) {
            if (!treechatSignsWithoutLocalKey()) {
                throw new Error('Set up local signing on this device before minting')
            }
        }
        if (userId && clientShare) {
            const media = (await measureMintStage(
                { answer_id, attachment_id: options?.attachment_id },
                'media_info',
                () =>
                    getMintMediaInfo(
                        answer_id,
                        options?.media_type,
                        options?.attachment_id,
                        options?.service_fee_quote,
                    ),
            )) as OrdinalMintMediaInfo
            const feeRate = await getBsvFeeRate()
            const feeRateSatsPerByte = Math.max(Number(feeRate?.fee_rate) || 0.1, 0.1)
            const maxNetworkFeeSatoshis = Math.max(
                1_000,
                Math.ceil((media.byte_size + 2_000) * feeRateSatsPerByte * 2),
            )
            const quotedServiceFeeSatoshis = Number(media.service_fee_satoshis)
            const approvedServiceFeeSatoshis = Number(options?.treechat_fee_amount_sats)
            const serviceFeeSatoshis = options?.service_fee_quote
                ? approvedServiceFeeSatoshis
                : quotedServiceFeeSatoshis
            if (!Number.isSafeInteger(serviceFeeSatoshis) || serviceFeeSatoshis <= 0) {
                throw new Error('Mint service fee is unavailable')
            }
            if (
                !options?.service_fee_quote &&
                (!Number.isSafeInteger(approvedServiceFeeSatoshis) ||
                    approvedServiceFeeSatoshis < quotedServiceFeeSatoshis)
            ) {
                throw new Error('Mint service fee changed; review the mint price again')
            }
            const serviceFeeQuote = options?.service_fee_quote || media.service_fee_quote
            if (!serviceFeeQuote) throw new Error('Mint service fee quote is unavailable')
            const intent = await prepareOrdinalMintIntent({
                userId: String(userId),
                deviceId: clientShare.deviceId,
                media,
                serviceFeeSatoshis,
                maxNetworkFeeSatoshis,
                fetchChunk: getMintMediaContentChunk,
                originalFile: options?.source_file,
            })
            Object.assign(requestOptions, {
                treechat_fee_amount_sats: serviceFeeSatoshis,
                service_fee_quote: serviceFeeQuote,
                device_id: clientShare.deviceId,
                attachment_id: intent.attachmentId,
                content_sha256: intent.contentSha256,
                content_bytes: intent.contentBytes,
                content_type: intent.contentType,
                filename: intent.filename,
                max_network_fee_sats: intent.maxNetworkFeeSatoshis,
            })
        }

        const request = () =>
            fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...accessTokens(),
                    ...commonHeaders(),
                },
                body: JSON.stringify({
                    answer_id,
                    ...requestOptions,
                }),
            })

        const response =
            userId && currentUserUsesClientSigning()
                ? await measureMintStage(
                      { answer_id, attachment_id: requestOptions.attachment_id },
                      'mint_request',
                      request,
                      result => result.ok,
                  )
                : await request()

        if (!response.ok) {
            let errorMessage = 'Failed to mint ordinal'

            try {
                const data = await response.json()
                errorMessage = data?.error || errorMessage
            } catch (_error) {
                const errorText = await response.text()
                if (errorText) {
                    errorMessage = errorText
                }
            }

            throw buildApiError(errorMessage, response.status)
        }

        return await response.json()
    } catch (error) {
        console.error('Error minting ordinal:', error)
        throw error
    }
}

// Owner-paid "Write Onchain": the post's owner pays from their own wallet to write one media
// file onchain as a B:// attachment record (bsv#write_attachment_onchain).
// A self-custody wallet signs the write on this device: the tap authorizes the miner fee, and
// the signing request that follows is signed only against that authorization.
async function writeAttachmentOnchain(
    answer_id: string,
    options: {
        media_type: 'image' | 'video' | 'audio'
        attachment_id?: string
        byte_size?: number
        fee_rate_sats_per_byte?: number
    },
): Promise<{
    status: 'started' | 'already_started'
    workflow_id?: string | null
    run_id?: string | null
    access_token?: string | null
    bsv_tx_id?: string | null
}> {
    const { byte_size, fee_rate_sats_per_byte, ...requestOptions } = options
    const body: Record<string, unknown> = { answer_id, ...requestOptions }
    const userId = gon.currentUser?.id
    const clientShare =
        userId && currentUserUsesClientSigning() ? await getClientShare(userId) : null
    if (userId && currentUserUsesClientSigning() && !clientShare) {
        if (!treechatSignsWithoutLocalKey()) {
            throw new Error('Set up local signing on this device before writing onchain')
        }
    }
    if (userId && clientShare) {
        const maxNetworkFeeSatoshis = ownerAttachmentWriteMaxNetworkFee(
            Number(byte_size),
            Number(fee_rate_sats_per_byte),
        )
        saveOwnerAttachmentWriteIntent({
            version: 1,
            userId: String(userId),
            deviceId: clientShare.deviceId,
            answerId: String(answer_id),
            ...(requestOptions.attachment_id
                ? { attachmentId: String(requestOptions.attachment_id) }
                : {}),
            maxNetworkFeeSatoshis,
            createdAt: Date.now(),
        })
        body.device_id = clientShare.deviceId
        body.max_network_fee_sats = maxNetworkFeeSatoshis
    }
    const response = await fetch(`${gon.api.bsv}/attachments/write_onchain`, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
        body: JSON.stringify(body),
    })
    const data = await response.json().catch(() => null)
    if (!response.ok) {
        throw buildApiError(data?.error || 'Failed to write media onchain', response.status, data)
    }
    return data
}

async function getOrdinalMintFeeQuote(): Promise<{
    service_fee_address: string
    service_fee_satoshis: number
    service_fee_quote: string
    app: string
    local_signing_device_available: boolean
}> {
    const response = await fetch(`${gon.api.bsv}/ordinals/mint_fee_quote`, {
        method: 'GET',
        headers: { ...accessTokens(), ...commonHeaders() },
    })
    if (!response.ok) {
        const body = await response.json().catch(() => null)
        throw new Error(body?.error || 'Unable to quote the Treechat mint fee')
    }
    const quote = await response.json()
    const userId = gon.currentUser?.id
    const localSigningDeviceAvailable =
        userId && currentUserUsesClientSigning()
            ? Boolean(await getClientShare(userId).catch(() => null))
            : false
    return { ...quote, local_signing_device_available: localSigningDeviceAvailable }
}

async function getMintMediaInfo(
    answer_id: string,
    media_type: 'auto' | 'image' | 'video' | 'audio' | 'html' = 'auto',
    attachment_id?: string,
    service_fee_quote?: string,
) {
    const params = new URLSearchParams({ answer_id, media_type })
    if (attachment_id) params.set('attachment_id', attachment_id)
    if (service_fee_quote) params.set('service_fee_quote', service_fee_quote)
    const url = `${gon.api.bsv}/ordinals/mint_media_info?${params.toString()}`

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })

    if (!response.ok) {
        const errorText = await response.text()
        throw buildApiError(`Failed to get mint media info: ${errorText}`, response.status)
    }

    return await response.json()
}

async function getMintMediaContentChunk(
    answerId: string,
    attachmentId: string,
    offset: number,
    length: number,
): Promise<Uint8Array> {
    requireClientSigningFeature()
    const params = new URLSearchParams({
        answer_id: answerId,
        attachment_id: attachmentId,
        offset: String(offset),
        length: String(length),
    })
    const response = await fetch(
        `${gon.api.bsv}/ordinals/mint_media_content?${params.toString()}`,
        {
            method: 'GET',
            headers: { ...accessTokens(), ...commonHeaders() },
        },
    )
    if (!response.ok) {
        throw new Error('Mint content is no longer available for local signing')
    }
    return new Uint8Array(await response.arrayBuffer())
}

async function sendOrdinalTemporal(
    ordinal_id: string,
    recipient_address: string,
    options?: {
        broadcast?: boolean
        device_id?: string
    },
) {
    const url = `${gon.api.bsv}/ordinals/send_temporal`
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...accessTokens(),
                ...commonHeaders(),
            },
            body: JSON.stringify({
                ordinal_id,
                recipient_address,
                ...options,
            }),
        })

        if (!response.ok) {
            let errorMessage = 'Failed to send ordinal'

            try {
                const data = await response.json()
                errorMessage = data?.error || errorMessage
            } catch (_error) {
                const errorText = await response.text()
                if (errorText) {
                    errorMessage = errorText
                }
            }

            throw buildApiError(errorMessage, response.status)
        }

        return await response.json()
    } catch (error) {
        console.error('Error sending ordinal:', error)
        throw error
    }
}

async function getTemporalWorkflowStatus(
    workflow_id: string,
    run_id: string,
    query_name: string,
    workflow_class: string,
    access_token?: string,
) {
    const url = `${window.knovApiUrl}/api/v1/temporal_workflows/status`
    const paramsObj: Record<string, string> = {
        workflow_id,
        run_id,
        query_name,
        workflow_class,
    }
    if (access_token) {
        paramsObj.access_token = access_token
    }
    const params = new URLSearchParams(paramsObj)

    try {
        const response = await fetch(`${url}?${params}`, {
            method: 'GET',
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
            },
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`Failed to get workflow status: ${errorText}`)
        }

        return await response.json()
    } catch (error) {
        console.error('Error getting workflow status:', error)
        throw error
    }
}

export interface IOrdinal {
    id: string
    origin?: string | null
    current_bsv_vout_id?: string | null
    treechat_owner_id: string
    attachment_id?: string
    attachment_class_name: string
    mint_tx_id?: string | null
    created_at: string
    updated_at: string
    quest_id?: string | null
    answer_id?: string | null
    minting_answer_id?: string | null
    minting_quest_id?: string | null
    system_thread_answer_id?: string | null
    system_thread_id?: string | null
    minted_by_treechat?: boolean
    recent_market_purchase?: boolean
    content_type?: string
    preview_url?: string | null
    users_ordinal?: {
        hidden: boolean
        show_in_profile: boolean
    } | null
}

export interface IOrdinalOrder {
    id: string
    ordinal_id: string
    side: 'ask' | 'bid'
    price_sats: number
    status: string
    expires_at?: string | null
    user_id: string
    counterparty_user_id?: string | null
    /** Bidder display name; present only for the owner reading their offers. */
    user_name?: string | null
    created_at?: string | null
    ordinal?: {
        id?: string | null
        origin?: string | null
        content_type?: string | null
        owner_id?: string | null
    }
    /** my_orders only: a bid whose payment inputs were signed and are still committed. */
    signed_offer?: boolean
    /** my_orders only: whether Cancel can close it (open, nothing signed). */
    cancellable?: boolean
    /** my_orders only: the on-chain withdrawal of a signed offer. */
    withdrawal?: IOrdinalOrderWithdrawal | null
}

export interface IOrdinalOrderWithdrawal {
    state?:
        | 'requested'
        | 'claimed'
        | 'signed'
        | 'broadcast'
        | 'confirmed'
        | 'failed'
        | 'inputs_spent'
        | null
    error?: string | null
    txid?: string | null
    /** Set when a signed attempt stopped; Withdraw resumes it. */
    paused_reason?: string | null
    /** An attempt no run is left to finish; Withdraw resumes it. */
    stalled?: boolean
    authorization_status: string
    withdrawable: boolean
}

/** A server-signed order opens at once. A local-signing order answers
 * `authorizing` with the workflow that waits for this device's signatures. */
export interface IOrdinalOrderCreateResponse {
    status?: 'authorizing'
    order: IOrdinalOrder
    market: IOrdinalMarketSummary
    workflow_id?: string | null
    run_id?: string | null
    access_token?: string | null
}

export interface IOrdinalOrderSettlementResponse {
    status: 'settling'
    order: IOrdinalOrder
    market: IOrdinalMarketSummary
    workflow_id: string
    run_id?: string | null
    access_token?: string | null
}

export interface IOrdinalMarketSummary {
    ordinal_id?: string
    ask?: { order_id: string; price_sats: number; seller_id: string } | null
    top_bid_sats?: number | null
    bid_count?: number
    settlement_pending?: boolean
    // Monotonic per-ordinal counter shared with the media stream and the live
    // state lane, so a socket frame and an HTTP response can be ordered against
    // each other without comparing clocks.
    state_revision?: number | null
    viewer?: { is_owner?: boolean; bid?: IOrdinalOrder | null; bid_state_revision?: number } | null
}

export interface IOrdinalsMediaStreamItem {
    id: string
    ownership_status?: 'confirmed' | 'unconfirmed'
    ownership_state_revision?: number
    state_revision?: number | null
    // Chain confirmation of the ordinal itself, patched in by live state events.
    chain_state?: string | null
    // Set client-side when a live event means this row no longer matches the
    // lens that fetched it (a sold listing on the Market tab). The row stays
    // put with a chip until an explicit refresh prunes it.
    lens_stale?: boolean
    lens_stale_reason?: 'sold' | 'unlisted'
    origin?: string | null
    minting_answer_id?: string | null
    owner_id?: string | null
    content_type?: string | null
    /** Filename the inscription was minted from; titles otherwise-unnamed media. */
    filename?: string | null
    content_url?: string | null
    media_width?: number | null
    media_height?: number | null
    media_duration_ms?: number | null
    media_byte_size?: number | null
    preview_width?: number | null
    preview_height?: number | null
    preview_url?: string | null
    preview_urls?: {
        original?: string | null
        thumb?: string | null
        grid?: string | null
        list?: string | null
    } | null
    created_at?: string | null
    top_new_unseen?: boolean
    content_key?: string | null
    duplicate_count?: number
    market?: IOrdinalMarketSummary | null
    parent?: {
        answer_id?: string | null
        quest_id?: string | null
        child_quest_id?: string | null
        space_id?: string | null
        bsv_upvalue_amt?: number | null
        // Value inherited from the minting post — displayed with the wrapper's
        // own total, never added to it server-side.
        minting_bsv_upvalue_amt?: number | null
        upvalues_enabled?: boolean
        free_reactions_enabled?: boolean
        user?: {
            id: string
            name?: string | null
            username?: string | null
            handle?: string | null
        } | null
    } | null
}

export interface IEnsureOrdinalThreadResponse {
    answer: Answer
    answer_id: string
    child_quest_id: string | null
}

export interface IOrdinalsMediaStreamResponse {
    items: IOrdinalsMediaStreamItem[]
    page?: number
    has_more?: boolean
    next_cursor?: string | null
    wallet_snapshot?: {
        status: 'fresh' | 'stale' | 'partial'
        refreshed_at: string
        ordinal_count: number
        indexed_count: number
    } | null
}

export interface IWalletOrdinalsSnapshotResponse {
    ok: boolean
    status: 'fresh' | 'stale' | 'partial'
    refreshed_at: string
    ordinals_address: string
    outpoints: string[]
    descriptors: IOrdinalDescriptor[]
}

async function listOrdinals(): Promise<{ ordinals: IOrdinal[] }> {
    const url = `${gon.api.bsv}/ordinals/list`
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
            },
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`Failed to list ordinals: ${errorText}`)
        }

        return await response.json()
    } catch (error) {
        console.error('Error listing ordinals:', error)
        throw error
    }
}

export async function getWalletOrdinalsSnapshot(): Promise<IWalletOrdinalsSnapshotResponse> {
    const response = await fetch(`${window.knovApiUrl}/api/v1/bsv/ordinals/wallet_snapshot`, {
        method: 'GET',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
            Accept: 'application/json',
        },
    })

    if (!response.ok) {
        const error = new Error('Unable to refresh wallet ordinals') as Error & { status?: number }
        error.status = response.status
        throw error
    }

    return await response.json()
}

export type OrdinalsStrayFundsErrorCode =
    | 'unconfirmed'
    | 'nothing_to_move'
    | 'sweep_failed'
    | 'timed_out'

export interface IOrdinalsStrayFundsRecovery {
    status: 'in_progress' | 'succeeded' | 'failed'
    started_at: string | null
    finished_at: string | null
    txid: string | null
    // Sats that reached the payment address (after the miner fee).
    satoshis: number | null
    error_code: OrdinalsStrayFundsErrorCode | null
}

// Plain BSV an external wallet paid to the ordinals address, which is not part
// of the balance until it is swept into the payment address.
export interface IOrdinalsStrayFunds {
    ok: boolean
    recoverable: boolean
    // Who signs the move: Treechat's wallet keys, or this user's device.
    signing?: 'server' | 'device' | null
    satoshis: number
    output_count: number
    recovery: IOrdinalsStrayFundsRecovery | null
}

export async function getOrdinalsStrayFunds(): Promise<IOrdinalsStrayFunds> {
    const response = await fetch(`${gon.api.bsv}/ordinals/stray_funds`, {
        method: 'GET',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })

    if (!response.ok) {
        throw buildApiError('Unable to check the ordinals address', response.status)
    }

    return await response.json()
}

// The unsigned sweep a device-signed wallet signs with its ordinals key.
export interface IOrdinalsStrayFundsSigningRequest {
    signing_token: string
    ordinals_address: string
    payment_address: string
    unsigned_tx_template: UnsignedTransactionTemplate
    satoshis: number
    fee: number
    expires_at: string
}

export async function prepareOrdinalsStrayFundsSweep(): Promise<{
    ok: boolean
    signing_request?: IOrdinalsStrayFundsSigningRequest
    recovery?: IOrdinalsStrayFundsRecovery | null
}> {
    const response = await fetch(`${gon.api.bsv}/ordinals/stray_funds/sweep_template`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...accessTokens(),
            ...commonHeaders(),
        },
    })

    if (!response.ok) {
        let errorMessage = 'Unable to move the funds'
        try {
            const data = await response.json()
            errorMessage = data?.error || errorMessage
        } catch (_error) {
            // Keep the generic message.
        }
        throw buildApiError(errorMessage, response.status)
    }

    return await response.json()
}

// Device-signed wallets pass the prepared token and the transaction they signed.
export async function recoverOrdinalsStrayFunds(deviceSigned?: {
    signing_token: string
    signed_tx: string
}): Promise<{
    ok: boolean
    recovery: IOrdinalsStrayFundsRecovery
}> {
    const response = await fetch(`${gon.api.bsv}/ordinals/stray_funds/recover`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...accessTokens(),
            ...commonHeaders(),
        },
        ...(deviceSigned ? { body: JSON.stringify(deviceSigned) } : {}),
    })

    if (!response.ok) {
        let errorMessage = 'Unable to move the funds'
        try {
            const data = await response.json()
            errorMessage = data?.error || errorMessage
        } catch (_error) {
            // Keep the generic message.
        }
        throw buildApiError(errorMessage, response.status)
    }

    return await response.json()
}

// Which of these users have an ordinal on their profile stream. Returns null when the
// request fails (for example a server without this endpoint) so callers can fall back.
async function getOrdinalProfileOwners(userIds: string[]): Promise<string[] | null> {
    const params = new URLSearchParams()
    userIds.forEach(id => params.append('user_ids[]', id))
    try {
        const response = await fetch(`${gon.api.bsv}/ordinals/profile_owners?${params}`, {
            method: 'GET',
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
            },
        })
        if (!response.ok) return null
        const data = await response.json()
        return Array.isArray(data?.user_ids) ? data.user_ids.map(String) : null
    } catch (error) {
        console.warn('Failed to load ordinal profile owners:', error)
        return null
    }
}

async function ordinalsMediaStream(opts: {
    filter: {
        ordinals?: boolean
        user_ordinals?: string
        order?: string
        market?: boolean
        // Ordinals tabs: 'mine' uses the authenticated owner; 'following' is
        // retained for legacy links.
        ordinals_scope?: 'mine' | 'following'
        // Public-wall quality dial: whitelisted min-upvalue stop (sats).
        min_sats?: number
        // Top time window (24h / week / month / year); only meaningful with the
        // plain upvalue Top order. Creation time on the wall, listing time on
        // the Market tab.
        top_window?: string
    }
    page?: number
    per_page?: number
    // Targeted hydration of specific ordinals in the viewer's own list, used to
    // insert a just-purchased ordinal without refetching a ranked page. Honored
    // only for My Ordinals, server-side.
    ids?: string[]
}): Promise<IOrdinalsMediaStreamResponse> {
    const url = `${gon.api.bsv}/ordinals/media_stream`
    const params = new URLSearchParams()
    if (opts?.filter?.ordinals) params.set('filter[ordinals]', 'true')
    if (opts?.filter?.user_ordinals) params.set('filter[user_ordinals]', opts.filter.user_ordinals)
    if (opts?.filter?.order) params.set('filter[order]', opts.filter.order)
    if (opts?.filter?.market) params.set('filter[market]', 'true')
    if (opts?.filter?.ordinals_scope)
        params.set('filter[ordinals_scope]', opts.filter.ordinals_scope)
    if (typeof opts?.filter?.min_sats === 'number')
        params.set('filter[min_sats]', String(opts.filter.min_sats))
    if (opts?.filter?.top_window) params.set('filter[top_window]', opts.filter.top_window)
    if (opts?.page) params.set('page', String(opts.page))
    if (opts?.per_page) params.set('per_page', String(opts.per_page))
    opts?.ids?.forEach(id => params.append('ids[]', id))

    try {
        const response = await fetch(`${url}?${params}`, {
            method: 'GET',
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
            },
        })

        if (!response.ok) {
            const errorText = await response.text()
            const error = new Error(
                `Failed to load ordinals media stream: ${errorText}`,
            ) as Error & { status?: number }
            error.status = response.status
            throw error
        }

        return await response.json()
    } catch (error) {
        console.error('Error loading ordinals media stream:', error)
        throw error
    }
}

// One ensure per ordinal at a time: every surface that can open an ordinal's
// thread (stream tile, payload menu, market card, media dock) shares the
// in-flight request, so a double-click or two cards for the same ordinal
// never send two writes. Keyed the same way the server dedupes.
const ensureOrdinalThreadInFlight = new Map<string, Promise<IEnsureOrdinalThreadResponse>>()

// Find-or-create the system Treechat thread for an ordinal (wrapper answer
// + its single child quest). Caches the returned answer so upvalue/reply flows
// can treat it like any other answer immediately.
function ensureOrdinalThread(ordinalId: string): Promise<IEnsureOrdinalThreadResponse> {
    const pending = ensureOrdinalThreadInFlight.get(ordinalId)
    if (pending) return pending

    const request: Promise<IEnsureOrdinalThreadResponse> = (async () => {
        const url = `${gon.api.bsv}/ordinals/${ordinalId}/ensure_thread`
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    ...accessTokens(),
                    ...commonHeaders(),
                },
            })

            if (!response.ok) {
                const errorText = await response.text()
                throw new Error(`Failed to ensure ordinal thread: ${errorText}`)
            }

            const data = await response.json()
            if (data?.answer) cache.cacheAnswer(data.answer)
            return data
        } catch (error) {
            console.error('Error ensuring ordinal thread:', error)
            throw error
        }
    })().finally(() => {
        if (ensureOrdinalThreadInFlight.get(ordinalId) === request) {
            ensureOrdinalThreadInFlight.delete(ordinalId)
        }
    })

    ensureOrdinalThreadInFlight.set(ordinalId, request)
    return request
}

// --- Ordinals market ---

async function getOrdinalStates(
    ids: string[],
    includePurchases = false,
): Promise<{ states: import('state/ordinalStatePatch').OrdinalStateEvent[] }> {
    const params = new URLSearchParams()
    ids.forEach(id => params.append('ids[]', id))
    if (includePurchases) params.set('include_purchases', 'true')
    const response = await fetch(`${gon.api.bsv}/ordinals/state?${params}`, {
        method: 'GET',
        headers: { ...accessTokens(), ...commonHeaders() },
    })
    if (!response.ok) throw new Error(`Failed to recover ordinal state: ${await response.text()}`)
    return await response.json()
}

async function getOrdinalMarketState(
    ids: string[],
): Promise<{ market: Record<string, IOrdinalMarketSummary> }> {
    const params = new URLSearchParams()
    ids.forEach(id => params.append('ids[]', id))
    const url = `${gon.api.bsv}/ordinals/market_state?${params}`
    const response = await fetch(url, {
        method: 'GET',
        headers: { ...accessTokens(), ...commonHeaders() },
    })
    if (!response.ok) {
        throw new Error(`Failed to load ordinal market state: ${await response.text()}`)
    }
    return await response.json()
}

// One card's market state, batched with every other card asking in the same moment
// (GET market_state takes up to 100 ids).
const loadOrdinalMarketState = createIdBatcher<IOrdinalMarketSummary>({
    fetchMany: async ids => (await getOrdinalMarketState(ids))?.market || {},
})

function getOrdinalMarketStateFor(ordinalId: string): Promise<IOrdinalMarketSummary | null> {
    return loadOrdinalMarketState(String(ordinalId))
}

async function getOrdinalOrders(
    ordinalId: string,
): Promise<{ market: IOrdinalMarketSummary; orders: IOrdinalOrder[] }> {
    const url = `${gon.api.bsv}/ordinals/${ordinalId}/orders`
    const response = await fetch(url, {
        method: 'GET',
        headers: { ...accessTokens(), ...commonHeaders() },
    })
    if (!response.ok) {
        throw new Error(`Failed to load ordinal orders: ${await response.text()}`)
    }
    return await response.json()
}

async function createOrdinalOrder(
    ordinalId: string,
    opts: { side: 'ask' | 'bid'; price_sats: number },
): Promise<IOrdinalOrderCreateResponse> {
    const url = `${gon.api.bsv}/ordinals/${ordinalId}/orders`
    const userId = gon.currentUser?.id
    const deviceId =
        userId && currentUserUsesClientSigning() ? getStoredClientSigningDeviceId(userId) : null
    const provisionalIntent: OrdinalMarketProvisionalActionIntent | null =
        userId && deviceId
            ? {
                  version: 1,
                  operation:
                      opts.side === 'ask'
                          ? 'ordinal_market_listing_parent'
                          : 'ordinal_market_offer',
                  ordinalId: String(ordinalId),
                  priceSats: opts.price_sats,
                  treechatFeeSats: marketFeeSats(opts.price_sats),
                  sellerProceedsSats: marketSellerProceedsSats(opts.price_sats),
                  userId: String(userId),
                  deviceId,
                  createdAt: Date.now(),
              }
            : null
    if (provisionalIntent) saveProvisionalOrdinalMarketActionIntent(provisionalIntent)
    const response = await fetch(url, {
        method: 'POST',
        headers: { ...accessTokens(), ...commonHeaders() },
        body: JSON.stringify(opts),
    })
    if (!response.ok) {
        if (response.status >= 400 && response.status < 500 && provisionalIntent) {
            clearProvisionalOrdinalMarketActionIntent(provisionalIntent)
        }
        let message = `Failed to create order`
        try {
            const data = await response.json()
            if (data?.error) message = data.error
        } catch {
            // keep the generic message
        }
        throw buildApiError(message, response.status)
    }
    const result = await response.json()
    if (provisionalIntent && result?.order?.id) {
        saveOrdinalMarketActionIntent({
            ...provisionalIntent,
            orderId: String(result.order.id),
        })
        clearProvisionalOrdinalMarketActionIntent(provisionalIntent)
    }
    return result
}

// A signed offer can't simply be cancelled: the server starts its on-chain
// withdrawal instead and answers with `withdrawal` (and no market).
async function cancelOrdinalOrder(
    ordinalId: string,
    orderId: string,
): Promise<{
    order: IOrdinalOrder
    market?: IOrdinalMarketSummary
    withdrawal?: IOrdinalOrderWithdrawal | null
}> {
    const url = `${gon.api.bsv}/ordinals/${ordinalId}/orders/${orderId}/cancel`
    const response = await fetch(url, {
        method: 'POST',
        headers: { ...accessTokens(), ...commonHeaders() },
    })
    if (!response.ok) {
        let message = 'Could not cancel the order.'
        try {
            const data = await response.json()
            if (data?.error) message = data.error
        } catch {
            // Keep the user-facing fallback.
        }
        throw buildApiError(message, response.status)
    }
    return await response.json()
}

// Withdraws a signed offer with an on-chain spend of its committed funds. Signed on
// this device (its id rides in commonHeaders) or with the Treechat-held wallet.
async function withdrawOrdinalOrder(
    ordinalId: string,
    orderId: string,
): Promise<{ order: IOrdinalOrder; withdrawal: IOrdinalOrderWithdrawal | null }> {
    const url = `${gon.api.bsv}/ordinals/${ordinalId}/orders/${orderId}/withdraw`
    const response = await fetch(url, {
        method: 'POST',
        headers: { ...accessTokens(), ...commonHeaders() },
    })
    if (!response.ok) {
        let message = 'Could not withdraw the offer.'
        try {
            const data = await response.json()
            if (data?.error) message = data.error
        } catch {
            // Keep the user-facing fallback.
        }
        throw buildApiError(message, response.status)
    }
    return await response.json()
}

async function declineOrdinalOrder(
    ordinalId: string,
    orderId: string,
): Promise<{ order: IOrdinalOrder; market: IOrdinalMarketSummary }> {
    const url = `${gon.api.bsv}/ordinals/${ordinalId}/orders/${orderId}/decline`
    const response = await fetch(url, {
        method: 'POST',
        headers: { ...accessTokens(), ...commonHeaders() },
    })
    if (!response.ok) {
        let message = 'Could not decline the offer.'
        try {
            const data = await response.json()
            if (data?.error) message = data.error
        } catch {
            // Keep the user-facing fallback.
        }
        throw buildApiError(message, response.status)
    }
    return await response.json()
}

async function settleOrdinalOrder(
    ordinalId: string,
    orderId: string,
    expectedOrder: Pick<IOrdinalOrder, 'side' | 'price_sats'>,
): Promise<IOrdinalOrderSettlementResponse> {
    const url = `${gon.api.bsv}/ordinals/${ordinalId}/orders/${orderId}/settle`
    const userId = gon.currentUser?.id
    const deviceId =
        userId && currentUserUsesClientSigning() ? getStoredClientSigningDeviceId(userId) : null
    if (userId && deviceId) {
        const priceSats = Number(expectedOrder.price_sats)
        saveOrdinalMarketActionIntent({
            version: 1,
            operation:
                expectedOrder.side === 'ask'
                    ? 'ordinal_market_listing_purchase'
                    : 'ordinal_market_offer',
            orderId: String(orderId),
            ordinalId: String(ordinalId),
            priceSats,
            treechatFeeSats: marketFeeSats(priceSats),
            sellerProceedsSats: marketSellerProceedsSats(priceSats),
            userId: String(userId),
            deviceId,
            createdAt: Date.now(),
        })
    }
    const response = await fetch(url, {
        method: 'POST',
        headers: { ...accessTokens(), ...commonHeaders() },
    })
    if (!response.ok) {
        if (response.status >= 400 && response.status < 500) {
            clearOrdinalMarketActionIntent(orderId)
        }
        let message = 'Unable to complete the ordinal sale.'
        try {
            const data = await response.json()
            if (data?.error) message = data.error
        } catch {
            // Keep the user-facing fallback.
        }
        throw buildApiError(message, response.status)
    }
    const result = await response.json()
    return result
}

async function getMyOrdinalOrders(): Promise<{ orders: IOrdinalOrder[] }> {
    const url = `${gon.api.bsv}/ordinals/my_orders`
    const response = await fetch(url, {
        method: 'GET',
        headers: { ...accessTokens(), ...commonHeaders() },
    })
    if (!response.ok) {
        throw new Error(`Failed to load your orders: ${await response.text()}`)
    }
    return await response.json()
}

async function toggleOrdinalShowInProfile(
    ordinalId: string,
): Promise<{ id: string; users_ordinal: { hidden: boolean; show_in_profile: boolean } }> {
    const url = `${gon.api.bsv}/ordinals/${ordinalId}/toggle_show_in_profile`
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
            },
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`Failed to toggle show_in_profile: ${errorText}`)
        }

        return await response.json()
    } catch (error) {
        console.error('Error toggling show_in_profile:', error)
        throw error
    }
}

async function toggleOrdinalHidden(
    ordinalId: string,
): Promise<{ id: string; users_ordinal: { hidden: boolean; show_in_profile: boolean } }> {
    const url = `${gon.api.bsv}/ordinals/${ordinalId}/toggle_hidden`
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
            },
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`Failed to toggle hidden: ${errorText}`)
        }

        return await response.json()
    } catch (error) {
        console.error('Error toggling hidden:', error)
        throw error
    }
}

export type OrdinalDescriptorStatus = 'resolved' | 'pending' | 'hydrating' | 'error'

export interface IOrdinalDescriptor {
    ok: boolean
    status: OrdinalDescriptorStatus
    outpoint: string
    bsv_vout_id?: string | null
    origin_outpoint?: string | null
    bsv_ordinal_id?: string | null
    content_url?: string | null
    content_type?: string | null
    /** Filename the inscription was minted from; titles otherwise-unnamed media. */
    filename?: string | null
    preview_url?: string | null
    error?: string
}

export async function resolveOrdinalDescriptor(
    outpoint: string,
    opts?: { assumeOrigin?: boolean; originOutpointHint?: string },
): Promise<IOrdinalDescriptor | null> {
    try {
        const query = new URLSearchParams({ outpoint })
        if (opts?.assumeOrigin) query.set('assume_origin', 'true')
        if (opts?.originOutpointHint) query.set('origin_outpoint_hint', opts.originOutpointHint)
        const res = await fetch(`${window.knovApiUrl}/api/v1/bsv/ordinals/resolve?${query}`, {
            method: 'GET',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
            },
        })
        if (!res.ok) return null
        return await res.json()
    } catch (e) {
        console.warn('Failed to resolve ordinal descriptor:', e)
        return null
    }
}

export interface IOrdinalProvenanceUser {
    id: string
    name?: string | null
    username?: string | null
    handle?: string | null
    avatar_urls?: Record<string, string> | null
    avatar_blob_id?: string | null
    avatar_attachment_id?: string | null
}

export interface IOrdinalProvenanceTenure {
    user: IOrdinalProvenanceUser | null
    source: 'genesis' | 'transfer' | 'backfill'
    acquired_at: string | null
    released_at: string | null
    current: boolean
}

export interface IOrdinalProvenanceActor {
    address?: string | null
    locking_script_hash?: string | null
    user: IOrdinalProvenanceUser | null
    identity_basis?: string | null
}

export interface IOrdinalProvenanceSigner extends IOrdinalProvenanceActor {
    valid: boolean
    protocol: string
    algorithm?: string | null
}

export interface IOrdinalProvenanceHop extends IOrdinalProvenanceActor {
    sequence: number
    outpoint: string
    tx_id?: string | null
    vout?: number | null
    block_height: number | null
    acquired_at: string | null
    spend_tx_id: string | null
    spend_block_height?: number | null
    released_at: string | null
    current: boolean
}

export interface IOrdinalChainProvenance {
    status: 'pending' | 'fresh' | 'stale' | 'error'
    state: 'active' | 'unconfirmed' | 'terminated' | 'indeterminate' | null
    stale: boolean
    provider: string | null
    last_success_at: string | null
    minted_at: string | null
    origin_block_height: number | null
    holder_count: number
    creator: IOrdinalProvenanceActor | null
    origin_holder: IOrdinalProvenanceActor | null
    signers: IOrdinalProvenanceSigner[]
    current_owner: IOrdinalProvenanceActor | null
    hops: IOrdinalProvenanceHop[]
}

export interface IOrdinalProvenanceResponse {
    ok: boolean
    bsv_ordinal_id: string
    origin: string
    minted_by_treechat: boolean
    // Provenance link for the artifact menu. Null for external inscriptions.
    minting_answer_id?: string | null
    minting_quest_id?: string | null
    // The ordinal's own system thread (wrapper answer + its child quest), read
    // live and visible to guests. Null until the thread is materialized.
    system_thread_answer_id?: string | null
    system_thread_id?: string | null
    content_type?: string | null
    // Owner-only affordances; preference flags are false for anonymous readers.
    viewer_is_owner?: boolean
    starred?: boolean
    archived?: boolean
    owner: IOrdinalProvenanceUser | null
    owner_count: number
    ownerships: IOrdinalProvenanceTenure[]
    chain?: IOrdinalChainProvenance
}

async function getOrdinalProvenance(outpoint: string): Promise<IOrdinalProvenanceResponse | null> {
    try {
        const query = new URLSearchParams({ outpoint })
        const res = await fetch(`${window.knovApiUrl}/api/v1/bsv/ordinals/provenance?${query}`, {
            method: 'GET',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
            },
        })
        if (!res.ok) return null
        return await res.json()
    } catch (e) {
        console.warn('Failed to fetch ordinal provenance:', e)
        return null
    }
}

export interface IResolveManyOrdinalDescriptorsResponse {
    ok: boolean
    descriptors: IOrdinalDescriptor[]
    error?: string
}

export async function resolveOrdinalDescriptorsMany(
    outpoints: string[],
    opts?: { assumeOrigin?: boolean },
): Promise<IResolveManyOrdinalDescriptorsResponse | null> {
    try {
        const res = await fetch(`${window.knovApiUrl}/api/v1/bsv/ordinals/resolve_many`, {
            method: 'POST',
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
                Accept: 'application/json',
            },
            body: JSON.stringify({
                outpoints,
                assume_origin: !!opts?.assumeOrigin,
            }),
        })
        if (!res.ok) return null
        return await res.json()
    } catch (e) {
        console.warn('Failed to resolve many ordinal descriptors:', e)
        return null
    }
}

// Define interfaces for metrics data based on the actual controller responses
export interface UsageMetrics {
    // Upvalues metrics
    upvalues?: {
        total_fee: number
        upvalue_count: number
        average_fee: number
        daily?: Array<{
            date: string
            total: number
            count: number
            average: number
        }>
    }
    // AI usage metrics
    ai_usage_by_type?: {
        by_provider: Array<{
            provider: string
            type: string
            usage_count: number
        }>
        by_type: Array<{
            type: string
            usage_count: number
        }>
    }
    // Posts metrics
    posts?: {
        total_count: number
        daily?: Array<{
            date: string
            count: number
        }>
    }
    // Posts by user metrics
    posts_by_user?: {
        users: Array<{
            name: string
            posts: number
        }>
    }
    // Period information
    period?: {
        start_date: string
        end_date: string
    }
}

export type UpvalueLeaderboardCategory = 'given' | 'received' | 'boosted'

export type UpvalueLeaderboardEntry = {
    user: {
        id: string
        name: string
        avatar_url?: string | null
    }
    total_sats: number
    upvalue_count: number
    last_upvalue_at?: string | null
}

export type UpvalueLeaderboardResponse = {
    category: UpvalueLeaderboardCategory
    period: {
        start_date?: string | null
        end_date?: string | null
    }
    page: number
    per_page: number
    users: UpvalueLeaderboardEntry[]
}

export type PostsLeaderboardEntry = {
    user: {
        id: string
        name: string
        avatar_url?: string | null
    }
    posts_count: number
    last_post_at?: string | null
}

export type PostsLeaderboardResponse = {
    period: {
        start_date?: string | null
        end_date?: string | null
    }
    page: number
    per_page: number
    users: PostsLeaderboardEntry[]
}

export type AiFeesLeaderboardEntry = {
    user: {
        id: string
        name: string
        avatar_url?: string | null
    }
    total_sats: number
    usage_count: number
    last_usage_at?: string | null
}

export type AiFeesLeaderboardResponse = {
    period: {
        start_date?: string | null
        end_date?: string | null
    }
    page: number
    per_page: number
    users: AiFeesLeaderboardEntry[]
}

export type MintersLeaderboardEntry = {
    user: {
        id: string
        name: string
        avatar_url?: string | null
    }
    mints_count: number
    last_mint_at?: string | null
}

export type MintersLeaderboardResponse = {
    period: {
        start_date?: string | null
        end_date?: string | null
    }
    page: number
    per_page: number
    users: MintersLeaderboardEntry[]
}

export type HostsLeaderboardEntry = {
    user: {
        id: string
        name: string
        avatar_url?: string | null
    }
    total_sats: number
    payout_count: number
    last_payout_at?: string | null
}

export type HostsLeaderboardResponse = {
    period: {
        start_date?: string | null
        end_date?: string | null
    }
    page: number
    per_page: number
    users: HostsLeaderboardEntry[]
}

export type ShareRewardsLeaderboardEntry = {
    user: {
        id: string
        name: string
        avatar_url?: string | null
    }
    total_sats: number
    total_usd_micros: number
    share_count: number
    last_settled_at?: string | null
}

export type ShareRewardsLeaderboardTotals = {
    total_sats: number
    total_usd_micros: number
    share_count: number
    sharer_count: number
}

export type ShareRewardsLeaderboardResponse = {
    period: {
        start_date?: string | null
        end_date?: string | null
    }
    page: number
    per_page: number
    totals: ShareRewardsLeaderboardTotals
    users: ShareRewardsLeaderboardEntry[]
}

/**
 * Get upvalues metrics data
 * @param {Object} params - Parameters to filter the metrics
 * @param {string} params.start_date - Start date for metrics (YYYY-MM-DD)
 * @param {string} params.end_date - End date for metrics (YYYY-MM-DD)
 * @param {boolean} params.daily - Whether to include daily data
 * @returns {Promise<{ upvalues: { total_count: number; daily?: Array<{ date: string; count: number }> }; period: { start_date: string; end_date: string } }>} The upvalues metrics data
 */
async function getUpvaluesMetrics(params = {}) {
    try {
        const response = await fetch(
            `${window.knovApiUrl}/api/v1/usage_metrics/upvalues?${new URLSearchParams(params)}`,
            {
                method: 'GET',
                headers: {
                    ...accessTokens(),
                    Accept: 'application/json',
                },
            },
        )

        if (!response.ok) {
            throw new Error(`Failed to fetch upvalues metrics: ${response.statusText}`)
        }

        return await response.json()
    } catch (error) {
        console.error('Error fetching upvalues metrics:', error)
        throw error
    }
}

/**
 * Get the upvalue leaderboard.
 * Categories:
 * - given: total sats given (excluding boosts)
 * - received: total sats received (excluding boosts)
 * - boosted: total sats boosted (sent to system user)
 */
async function getUpvalueLeaderboard(
    params: {
        category?: UpvalueLeaderboardCategory
        start_date?: string
        end_date?: string
        page?: number
        per_page?: number
    } = {},
): Promise<UpvalueLeaderboardResponse> {
    try {
        const response = await fetch(
            `${window.knovApiUrl}/api/v1/leaderboards/upvalues?${new URLSearchParams(
                params as Record<string, string>,
            )}`,
            {
                method: 'GET',
                headers: {
                    ...accessTokens(),
                    Accept: 'application/json',
                },
            },
        )

        if (!response.ok) {
            throw new Error(`Failed to fetch upvalue leaderboard: ${response.statusText}`)
        }

        return (await response.json()) as UpvalueLeaderboardResponse
    } catch (error) {
        console.error('Error fetching upvalue leaderboard:', error)
        throw error
    }
}

/**
 * Get the posts leaderboard (top posters by public post count).
 */
async function getPostsLeaderboard(
    params: {
        start_date?: string
        end_date?: string
        page?: number
        per_page?: number
    } = {},
): Promise<PostsLeaderboardResponse> {
    try {
        const response = await fetch(
            `${window.knovApiUrl}/api/v1/leaderboards/posts?${new URLSearchParams(
                params as Record<string, string>,
            )}`,
            {
                method: 'GET',
                headers: {
                    ...accessTokens(),
                    Accept: 'application/json',
                },
            },
        )

        if (!response.ok) {
            throw new Error(`Failed to fetch posts leaderboard: ${response.statusText}`)
        }

        return (await response.json()) as PostsLeaderboardResponse
    } catch (error) {
        console.error('Error fetching posts leaderboard:', error)
        throw error
    }
}

/**
 * Get the AI fees leaderboard (top users by total AI usage costs).
 */
async function getAiFeesLeaderboard(
    params: {
        start_date?: string
        end_date?: string
        page?: number
        per_page?: number
    } = {},
): Promise<AiFeesLeaderboardResponse> {
    try {
        const response = await fetch(
            `${window.knovApiUrl}/api/v1/leaderboards/ai_fees?${new URLSearchParams(
                params as Record<string, string>,
            )}`,
            {
                method: 'GET',
                headers: {
                    ...accessTokens(),
                    Accept: 'application/json',
                },
            },
        )

        if (!response.ok) {
            throw new Error(`Failed to fetch AI fees leaderboard: ${response.statusText}`)
        }

        return (await response.json()) as AiFeesLeaderboardResponse
    } catch (error) {
        console.error('Error fetching AI fees leaderboard:', error)
        throw error
    }
}

/**
 * Get the minters leaderboard (top users by number of BsvOrdinals minted via Treechat).
 */
async function getMintersLeaderboard(
    params: {
        start_date?: string
        end_date?: string
        page?: number
        per_page?: number
    } = {},
): Promise<MintersLeaderboardResponse> {
    try {
        const response = await fetch(
            `${window.knovApiUrl}/api/v1/leaderboards/minters?${new URLSearchParams(
                params as Record<string, string>,
            )}`,
            {
                method: 'GET',
                headers: {
                    ...accessTokens(),
                    Accept: 'application/json',
                },
            },
        )

        if (!response.ok) {
            throw new Error(`Failed to fetch minters leaderboard: ${response.statusText}`)
        }

        return (await response.json()) as MintersLeaderboardResponse
    } catch (error) {
        console.error('Error fetching minters leaderboard:', error)
        throw error
    }
}

/**
 * Get the hosts leaderboard (top stream hosts by revenue-share earned).
 */
async function getHostsLeaderboard(
    params: {
        start_date?: string
        end_date?: string
        page?: number
        per_page?: number
    } = {},
): Promise<HostsLeaderboardResponse> {
    try {
        const response = await fetch(
            `${window.knovApiUrl}/api/v1/leaderboards/hosts?${new URLSearchParams(
                params as Record<string, string>,
            )}`,
            {
                method: 'GET',
                headers: {
                    ...accessTokens(),
                    Accept: 'application/json',
                },
            },
        )

        if (!response.ok) {
            throw new Error(`Failed to fetch hosts leaderboard: ${response.statusText}`)
        }

        return (await response.json()) as HostsLeaderboardResponse
    } catch (error) {
        console.error('Error fetching hosts leaderboard:', error)
        throw error
    }
}

/**
 * Get the share rewards leaderboard (top sharers by anonymous share-reward payouts),
 * including range totals.
 */
async function getShareRewardsLeaderboard(
    params: {
        start_date?: string
        end_date?: string
        page?: number
        per_page?: number
    } = {},
): Promise<ShareRewardsLeaderboardResponse> {
    try {
        const response = await fetch(
            `${window.knovApiUrl}/api/v1/leaderboards/share_rewards?${new URLSearchParams(
                params as Record<string, string>,
            )}`,
            {
                method: 'GET',
                headers: {
                    ...accessTokens(),
                    Accept: 'application/json',
                },
            },
        )

        if (!response.ok) {
            throw new Error(`Failed to fetch share rewards leaderboard: ${response.statusText}`)
        }

        return (await response.json()) as ShareRewardsLeaderboardResponse
    } catch (error) {
        console.error('Error fetching share rewards leaderboard:', error)
        throw error
    }
}

/**
 * Get AI usage metrics by type
 * @param {Object} params - Parameters to filter the metrics
 * @param {string} params.start_date - Start date for metrics (YYYY-MM-DD)
 * @param {string} params.end_date - End date for metrics (YYYY-MM-DD)
 * @returns {Promise<{
 *   metrics: Array<{
 *     date: string,
 *     provider: string,
 *     model: string,
 *     public: boolean,
 *     usage_count: number
 *   }>,
 *   period: { start_date: string, end_date: string }
 * }>} The AI usage metrics
 */
async function getAiUsageByTypeMetrics(params = {}) {
    try {
        const response = await fetch(
            `${window.knovApiUrl}/api/v1/usage_metrics/ai/type?${new URLSearchParams(params)}`,
            {
                method: 'GET',
                headers: {
                    ...accessTokens(),
                    Accept: 'application/json',
                },
            },
        )

        if (!response.ok) {
            throw new Error(`Failed to fetch AI usage metrics: ${response.statusText}`)
        }

        return await response.json()
    } catch (error) {
        console.error('Error fetching AI usage metrics:', error)
        throw error
    }
}

/**
 * Get AI usage metrics by model
 * @param {Object} params - Parameters to filter the metrics
 * @param {string} params.start_date - Start date for metrics (YYYY-MM-DD)
 * @param {string} params.end_date - End date for metrics (YYYY-MM-DD)
 * @param {string} params.excluded_users - Comma-separated list of usernames to exclude
 * @returns {Promise<{
 *   metrics: Array<{date: string, provider: string, model: string, public: boolean, usage_count: number}>,
 *   period: { start_date: string, end_date: string }
 * }>} The AI usage metrics by model
 **/
async function getAiUsageByModelMetrics(params = {}) {
    try {
        const response = await fetch(
            `${window.knovApiUrl}/api/v1/usage_metrics/ai/model?${new URLSearchParams(params)}`,
            {
                method: 'GET',
                headers: {
                    ...accessTokens(),
                    Accept: 'application/json',
                },
            },
        )

        if (!response.ok) {
            throw new Error(`Failed to fetch AI usage by model metrics: ${response.statusText}`)
        }

        return await response.json()
    } catch (error) {
        console.error('Error fetching AI usage by model metrics:', error)
        throw error
    }
}

/**
 * Get posts metrics data
 * @param {Object} params - Parameters to filter the metrics
 * @param {string} params.start_date - Start date for metrics (YYYY-MM-DD)
 * @param {string} params.end_date - End date for metrics (YYYY-MM-DD)
 * @param {boolean} params.daily - Whether to include daily data
 * @returns {Promise<{ total_count: number; daily?: Array<{ date: string; count: number }>; period: { start_date: string; end_date: string } }>} The posts metrics data
 */
async function getPostsMetrics(params = {}) {
    try {
        const response = await fetch(
            `${window.knovApiUrl}/api/v1/usage_metrics/posts?${new URLSearchParams(params)}`,
            {
                method: 'GET',
                headers: {
                    ...accessTokens(),
                    Accept: 'application/json',
                },
            },
        )

        if (!response.ok) {
            throw new Error(`Failed to fetch posts metrics: ${response.statusText}`)
        }

        return await response.json()
    } catch (error) {
        console.error('Error fetching posts metrics:', error)
        throw error
    }
}

/**
 * Get signups metrics data
 * @param {Object} params - Parameters to filter the metrics
 * @param {string} params.start_date - Start date for metrics (YYYY-MM-DD)
 * @param {string} params.end_date - End date for metrics (YYYY-MM-DD)
 * @param {boolean} params.daily - Whether to include daily data
 * @returns {Promise<{ signups: { total_count: number; daily?: Array<{ date: string; count: number }> }; period: { start_date: string; end_date: string } }>} The signups metrics data
 */
async function getSignupsMetrics(params = {}) {
    try {
        const response = await fetch(
            `${window.knovApiUrl}/api/v1/usage_metrics/signups?${new URLSearchParams(params)}`,
            {
                method: 'GET',
                headers: {
                    ...accessTokens(),
                    Accept: 'application/json',
                },
            },
        )

        if (!response.ok) {
            throw new Error(`Failed to fetch signups metrics: ${response.statusText}`)
        }

        return await response.json()
    } catch (error) {
        console.error('Error fetching signups metrics:', error)
        throw error
    }
}

/**
 * Get all metrics data combined
 * @param {Object} params - Parameters to filter the metrics
 * @param {string} params.start_date - Start date for metrics (YYYY-MM-DD)
 * @param {string} params.end_date - End date for metrics (YYYY-MM-DD)
 * @param {boolean} params.daily - Whether to include daily data
 * @returns {Promise<UsageMetrics>} All metrics data
 */
async function getAllMetrics(params = {}) {
    try {
        const response = await fetch(
            `${window.knovApiUrl}/api/v1/usage_metrics?${new URLSearchParams(params)}`,
            {
                method: 'GET',
                headers: {
                    ...accessTokens(),
                    Accept: 'application/json',
                },
            },
        )

        if (!response.ok) {
            throw new Error(`Failed to fetch all metrics: ${response.statusText}`)
        }

        return await response.json()
    } catch (error) {
        console.error('Error fetching all metrics:', error)
        throw error
    }
}

/**
 * Get PnL metrics from BsvFeeCharges
 * @param {Object} params - Parameters to filter the metrics
 * @param {string} params.start_date - Start date for metrics (YYYY-MM-DD)
 * @param {string} params.end_date - End date for metrics (YYYY-MM-DD)
 * @param {boolean} params.daily - Whether to include daily data
 * @param {string} params.excluded_users - Comma-separated list of usernames to exclude
 * @returns {Promise<any>} The PnL metrics data
 */
async function getFeeChargePnlMetrics(params = {}) {
    try {
        const response = await fetch(
            `${window.knovApiUrl}/api/v1/usage_metrics/fee_charge_pnl?${new URLSearchParams(
                params,
            )}`,
            {
                method: 'GET',
                headers: {
                    ...accessTokens(),
                    Accept: 'application/json',
                },
            },
        )

        if (!response.ok) {
            throw new Error(`Failed to fetch PnL metrics: ${response.statusText}`)
        }

        return await response.json()
    } catch (error) {
        console.error('Error fetching PnL metrics:', error)
        throw error
    }
}

async function getBsvFeeCharges(params: { start_date?: string; end_date?: string } = {}) {
    try {
        const queryParams = new URLSearchParams()
        if (params.start_date) queryParams.append('start_date', params.start_date)
        if (params.end_date) queryParams.append('end_date', params.end_date)

        const url = `${window.knovApiUrl}/api/v1/bsv_fee_charges${
            queryParams.toString() ? `?${queryParams.toString()}` : ''
        }`

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
            },
        })

        if (!response.ok) {
            throw new Error(`Failed to fetch BSV fee charges: ${response.statusText}`)
        }

        return await response.json()
    } catch (error) {
        console.error('Error fetching BSV fee charges:', error)
        throw error
    }
}

interface BsvPayment {
    id: string
    tx_id: string | null
    created_at: string
    amount_sats: number
    to_address: string
    status: string
    answer_id: string | null
    confirmed: boolean
    confirmations: number | null
    block_height: number | null
    block_time: number | null
    retries: number
    max_retries: number
    // 'sent' (current user paid) or 'received' (current user was paid).
    direction?: 'sent' | 'received'
    // Display name of the counterparty (payee for sent, payer for received), if known.
    counterparty_name?: string | null
}

async function getBsvPayments(): Promise<BsvPayment[]> {
    try {
        const url = `${window.knovApiUrl}/api/v1/bsv_txs`

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
            },
        })

        if (!response.ok) {
            throw new Error(`Failed to fetch BSV payments: ${response.statusText}`)
        }

        return await response.json()
    } catch (error) {
        console.error('Error fetching BSV payments:', error)
        throw error
    }
}

async function cancelBsvPayment(
    paymentId: string,
): Promise<{ success: boolean; pending?: boolean; message?: string; error?: string }> {
    try {
        const url = `${window.knovApiUrl}/api/v1/bsv_txs/${paymentId}/cancel`

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
            },
        })

        const data = await response.json()

        if (!response.ok) {
            return { success: false, error: data.error || 'Failed to cancel payment' }
        }

        return { success: true, pending: data.pending, message: data.message }
    } catch (error) {
        console.error('Error cancelling BSV payment:', error)
        return { success: false, error: 'Network error while cancelling payment' }
    }
}

async function retryBsvPayment(
    paymentId: string,
    deviceId?: string,
): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
        const url = `${window.knovApiUrl}/api/v1/bsv_txs/${paymentId}/retry`

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(deviceId ? { device_id: deviceId } : {}),
        })

        const data = await response.json()

        if (!response.ok) {
            return { success: false, error: data.error || 'Failed to retry payment' }
        }

        return { success: true, message: data.message }
    } catch (error) {
        console.error('Error retrying BSV payment:', error)
        return { success: false, error: 'Network error while retrying payment' }
    }
}

async function translateAnswer(answerId: string, targetLanguage: string = 'en') {
    const url = `${window.knovApiUrl}/api/v1/answers/${answerId}/translate`
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: { ...accessTokens(), ...commonHeaders() },
            body: JSON.stringify({ target_language: targetLanguage }),
        })
        if (!response.ok) {
            throw new Error(`Failed to translate answer to English: ${response.statusText}`)
        }

        const data = (await response.json()) as AnswerTranslationResponse
        const translatedAnswer = translatedAnswerFromResponse(cache.getCachedAnswer(answerId), data)
        if (translatedAnswer) cache.cacheAnswer(translatedAnswer)

        return data
    } catch (error) {
        console.error('Error translating answer to English:', error)
        throw error
    }
}

async function runRaffle(answerId: string) {
    const url = `${window.knovApiUrl}/api/v1/answers/${answerId}/run_raffle`
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: { ...accessTokens(), ...commonHeaders() },
        })
        if (!response.ok) {
            throw new Error(`Failed to run raffle: ${response.statusText}`)
        }

        return await response.json()
    } catch (error) {
        console.error('Error running raffle:', error)
        throw error
    }
}

async function finalizeRaffle(answerId: string) {
    const url = `${window.knovApiUrl}/api/v1/answers/${answerId}/finalize_raffle`
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: { ...accessTokens(), ...commonHeaders() },
        })
        const data = await response.json()
        return data
    } catch (error) {
        console.error('Error finalizing raffle:', error)
        throw error
    }
}

async function retryImageGeneration(answerId: string, payload: Record<string, unknown> = {}) {
    const url = `${window.knovApiUrl}/api/v1/answers/${answerId}/retry_image_generation`
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: { ...accessTokens(), ...commonHeaders() },
            body: JSON.stringify(payload),
        })
        if (!response.ok) {
            throw new Error(
                `Failed to retry image generation: ${response.status} ${response.statusText}`,
            )
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error('Error retrying image generation:', error)
        throw error
    }
}

async function clearImageGenerationError(answerId: string) {
    const url = `${window.knovApiUrl}/api/v1/answers/${answerId}/clear_image_generation_error`
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: { ...accessTokens(), ...commonHeaders() },
        })
        const data = await response.json()
        return data
    } catch (error) {
        console.error('Error clearing image generation error:', error)
        throw error
    }
}

// The post's owner or a knov_admin: true adds it to Music (Treechat Radio), false removes it.
async function setAnswerIsMusic(answerId: string, isMusic: boolean) {
    const response = await fetch(`${window.knovApiUrl}/api/v1/answers/${answerId}/music`, {
        method: 'POST',
        headers: { ...accessTokens(), ...commonHeaders() },
        body: JSON.stringify({ is_music: isMusic }),
    })
    if (!response.ok) throw new Error(`Failed to update music flag: ${response.status}`)
    return response.json()
}

async function followUser(userId) {
    const res = await fetch(`${gon.api.users}/${userId}/follow`, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })

    if (abort401(res)) return
    return await res.json()
}

export type OpenDirectMessageResult =
    | { ok: true; quest: Quest; created: boolean }
    | { ok: false; error: string; reason?: string }

// Opens the viewer's direct message with a user, creating it on first contact
// (POST /api/v1/direct_messages). The server decides who may be messaged.
async function openDirectMessage(userId: string): Promise<OpenDirectMessageResult | undefined> {
    const res = await fetch(`${window.knovApiUrl}/api/v1/direct_messages`, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
        body: JSON.stringify({ user_id: userId }),
    }).catch(() => null)

    if (!res) return { ok: false, error: "Couldn't open the conversation. Try again." }
    if (abort401(res)) return
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data?.quest) {
        return {
            ok: false,
            error: data?.error || "Couldn't open the conversation. Try again.",
            reason: data?.reason,
        }
    }
    return { ok: true, quest: data.quest, created: Boolean(data.created) }
}

// How many message requests wait on the viewer (GET /api/v1/direct_messages/requests).
async function getMessageRequestCount(): Promise<number> {
    const res = await fetch(`${window.knovApiUrl}/api/v1/direct_messages/requests`, {
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    }).catch(() => null)
    if (!res || abort401(res) || !res.ok) return 0
    const data = await res.json().catch(() => ({}))
    return Number(data?.count) || 0
}

export type AnswerMessageRequestResult = { ok: true; quest: Quest } | { ok: false; error: string }

// The recipient of a message request accepts or declines it
// (POST /api/v1/direct_messages/:id/accept|decline); returns the conversation.
async function answerMessageRequest(
    questId: string,
    answer: 'accept' | 'decline',
): Promise<AnswerMessageRequestResult | undefined> {
    const fallback = "Couldn't update the message request. Try again."
    const res = await fetch(`${window.knovApiUrl}/api/v1/direct_messages/${questId}/${answer}`, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    }).catch(() => null)

    if (!res) return { ok: false, error: fallback }
    if (abort401(res)) return
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data?.quest) return { ok: false, error: data?.error || fallback }
    return { ok: true, quest: data.quest }
}

async function unfollowUser(userId) {
    const res = await fetch(`${gon.api.users}/${userId}/unfollow`, {
        method: 'DELETE',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })

    if (abort401(res)) return
    const data = await res.json()
    if (res.ok) {
        void queryClient.invalidateQueries({ queryKey: ['quests'] })
    }
    return data
}

// Everything a user has earned, split Posts / Music / Sharing / Hosts / Ordinals.
async function getUserEarnings(userId) {
    const res = await fetch(`${gon.api.users}/${userId}/earnings`, {
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })
    if (!res.ok) throw new Error(`Earnings request failed: ${res.status}`)
    return (await res.json())?.earnings
}

async function getFollowers(userId) {
    const res = await fetch(`${gon.api.users}/${userId}/followers`, {
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })

    if (abort401(res)) return
    return await res.json()
}

async function getFollowing(userId) {
    const res = await fetch(`${gon.api.users}/${userId}/following`, {
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })

    if (abort401(res)) return
    return await res.json()
}

async function getSuggestedFollows() {
    const res = await fetch(`${gon.api.users}/suggested_follows`, {
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })

    if (abort401(res)) return
    return await res.json()
}

async function storeTwitterPKCE(codeVerifier: string, state: string) {
    const res = await fetch(`${window.knovApiUrl}/auth/twitter/pkce`, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
        // The API sets a cookie here that binds the state to this browser
        credentials: 'include',
        body: JSON.stringify({
            code_verifier: codeVerifier,
            state: state,
        }),
    })

    if (!res.ok) {
        throw new Error('Failed to store PKCE parameters')
    }

    const data = await res.json()
    return data
}

async function tweetQuest(providerId: string, questId: string, imageData: Blob | string | object) {
    const res = await fetch(`${window.knovApiUrl}/api/v1/quests/create_tweet`, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
        credentials: 'same-origin',
        body: JSON.stringify({
            provider_id: providerId,
            quest_id: questId,
            image_data: imageData,
        }),
    })

    if (abort401(res)) return
    return await res.json()
}

// Upload or update a dedicated preview image for a file attachment
async function updateFilePreview(attachmentId: string, preview: Blob, key: string = '1x1') {
    const url = `${window.knovApiUrl}/api/v1/attachments/${attachmentId}/preview`
    const form = new FormData()
    form.append('preview', preview)
    if (key) form.append('key', key)
    const res = await fetch(url, {
        method: 'POST',
        headers: { ...accessTokens() },
        body: form,
    })
    if (!res.ok) {
        const text = await res.text()
        throw new Error(`Failed to update file preview: ${text}`)
    }
    return await res.json()
}

async function getAnonymousShareLink(answerId: string): Promise<{
    url: string
    reward_cents: number
    reward_enabled: boolean
    daily_cap_cents: number
    remaining_daily_cents: number
} | null> {
    if (typeof gon === 'undefined' || gon?.anonymousShareRewardsEligible !== true) {
        return null
    }

    const response = await fetch(`${window.knovApiUrl}/api/v1/anonymous_share_links`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
        body: JSON.stringify({ answer_id: answerId }),
    })
    if (!response.ok) {
        throw buildApiError('Share reward link is unavailable', response.status)
    }

    return await response.json()
}

async function getAnswerByAnonymousShareToken(
    token: string,
    diagnostic?: OperationTrace | null,
): Promise<Answer | null> {
    if (!token) return null

    diagnostic?.mark('share_lookup_begin')
    const response = await fetch(
        `${window.knovApiUrl}/api/v1/anonymous_share_links/${encodeURIComponent(token)}`,
        {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
            },
        },
    )
    if (response.status === 401 || response.status === 403 || response.status === 404) return null
    if (!response.ok) {
        throw buildApiError('Tracked share link is unavailable', response.status)
    }

    const { answer_id: answerId } = await response.json()
    diagnostic?.mark('share_lookup_end')
    if (!answerId) return null

    diagnostic?.mark('post_fetch_begin')
    const answer = await getAnswer(answerId)
    diagnostic?.mark('post_fetch_end')
    if (!answer) return null

    const existingShareLinkIds = Array.isArray(answer.anonymous_share_link_ids)
        ? answer.anonymous_share_link_ids
        : []
    const answerWithShareToken = {
        ...answer,
        anonymous_share_link_ids: [...new Set([...existingShareLinkIds, token])],
    }
    diagnostic?.mark('share_cache_begin')
    cache.cacheAnswer(answerWithShareToken, false)
    diagnostic?.mark('share_cache_end')
    return answerWithShareToken
}

async function listOpenclawIntegrations() {
    const res = await fetch(`${window.knovApiUrl}/api/v1/openclaw/integrations`, {
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })
    if (abort401(res)) return
    return await res.json()
}

async function registerOpenclawIntegration(params: {
    agentAlias: string
    webhookUrl: string
    webhookSecret?: string
    writebackUrl?: string
    transportSecurityMode?: 'off' | 'preferred' | 'required'
    transportKeyId?: string
    streamId?: string
    streamIds?: string[]
    teamId?: string
    teamIds?: string[]
    enabled?: boolean
    metadata?: Record<string, unknown>
}) {
    const payload = {
        agent_alias: params.agentAlias,
        webhook_url: params.webhookUrl,
        webhook_secret: params.webhookSecret,
        writeback_url: params.writebackUrl,
        transport_security_mode: params.transportSecurityMode,
        transport_key_id: params.transportKeyId,
        stream_id: params.streamId,
        stream_ids: params.streamIds,
        team_id: params.teamId,
        team_ids: params.teamIds,
        enabled: params.enabled ?? true,
        metadata: params.metadata ?? {},
    }
    const res = await fetch(`${window.knovApiUrl}/api/v1/openclaw/integrations`, {
        method: 'POST',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
        body: JSON.stringify(payload),
    })
    if (abort401(res)) return
    return await res.json()
}

async function setOpenclawIntegrationEnabled(integrationId: string, enabled: boolean) {
    const res = await fetch(`${window.knovApiUrl}/api/v1/openclaw/integrations/${integrationId}`, {
        method: 'PATCH',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
        body: JSON.stringify({ enabled }),
    })
    if (abort401(res)) return
    return await res.json()
}

async function rotateOpenclawIntegrationSecret(integrationId: string) {
    const res = await fetch(
        `${window.knovApiUrl}/api/v1/openclaw/integrations/${integrationId}/rotate_secret`,
        {
            method: 'POST',
            headers: {
                ...accessTokens(),
                ...commonHeaders(),
            },
        },
    )
    if (abort401(res)) return
    return await res.json()
}

async function removeOpenclawIntegration(integrationId: string) {
    const res = await fetch(`${window.knovApiUrl}/api/v1/openclaw/integrations/${integrationId}`, {
        method: 'DELETE',
        headers: {
            ...accessTokens(),
            ...commonHeaders(),
        },
    })
    if (abort401(res)) return
    return await res.json()
}

async function removeAllOpenclawIntegrations() {
    const listResponse = await listOpenclawIntegrations()
    const integrations = Array.isArray(listResponse?.integrations) ? listResponse.integrations : []
    const removalResults = []

    for (const integration of integrations) {
        const integrationId = integration?.id
        if (!integrationId) continue
        const response = await removeOpenclawIntegration(integrationId)
        removalResults.push({
            integration_id: integrationId,
            response,
        })
    }

    return {
        success: true,
        removed_count: removalResults.length,
        removals: removalResults,
    }
}

// --- Admin: stream-host revenue-share program (platform admin only) ---

async function getHostGrants() {
    const res = await fetch(`${window.knovApiUrl}/api/v1/admin/hosts`, {
        headers: { ...accessTokens(), ...commonHeaders() },
    })
    if (abort401(res)) return
    if (!res.ok) throw new Error('Failed to load host grants')
    return await res.json()
}

async function getHostCandidates(query?: string) {
    const suffix = query ? `?q=${encodeURIComponent(query)}` : ''
    const res = await fetch(`${window.knovApiUrl}/api/v1/admin/hosts/candidates${suffix}`, {
        headers: { ...accessTokens(), ...commonHeaders() },
    })
    if (abort401(res)) return
    if (!res.ok) throw new Error('Failed to load candidates')
    return await res.json()
}

async function inviteHost(params: { user_id: string; share_bps?: number; note?: string }) {
    const res = await fetch(`${window.knovApiUrl}/api/v1/admin/hosts/invite`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...accessTokens(),
            ...commonHeaders(),
        },
        body: JSON.stringify(params),
    })
    if (abort401(res)) return
    const body = await res.json()
    if (!res.ok) throw new Error(body?.error || 'Failed to send invite')
    return body
}

async function ensureHostInviteArtifact(grantId: string) {
    const res = await fetch(`${window.knovApiUrl}/api/v1/admin/hosts/${grantId}/artifact`, {
        method: 'POST',
        headers: { ...accessTokens(), ...commonHeaders() },
    })
    if (abort401(res)) return
    const body = await res.json()
    if (!res.ok) throw new Error(body?.error || 'Failed to create invite link')
    return body
}

async function resendHostInvite(grantId: string) {
    const res = await fetch(`${window.knovApiUrl}/api/v1/admin/hosts/${grantId}/resend`, {
        method: 'POST',
        headers: { ...accessTokens(), ...commonHeaders() },
    })
    if (abort401(res)) return
    const body = await res.json()
    if (!res.ok) throw new Error(body?.error || 'Failed to resend invite')
    return body
}

async function revokeHostGrant(grantId: string, reason?: string) {
    const res = await fetch(`${window.knovApiUrl}/api/v1/admin/hosts/${grantId}`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            ...accessTokens(),
            ...commonHeaders(),
        },
        body: JSON.stringify({ reason }),
    })
    if (abort401(res)) return
    const body = await res.json()
    if (!res.ok) throw new Error(body?.error || 'Failed to revoke grant')
    return body
}

/** The caller's own host grant + wallet readiness; null grant for non-hosts. */
async function getMyHostGrant() {
    const res = await fetch(`${window.knovApiUrl}/api/v1/host_grants/mine`, {
        headers: { ...accessTokens(), ...commonHeaders() },
    })
    if (abort401(res)) return
    if (!res.ok) return null
    return await res.json()
}

async function acceptHostInvite() {
    const res = await fetch(`${window.knovApiUrl}/api/v1/host_grants/accept`, {
        method: 'POST',
        headers: { ...accessTokens(), ...commonHeaders() },
    })
    if (abort401(res)) return
    const body = await res.json()
    if (!res.ok) throw new Error(body?.error || 'Failed to accept host invite')
    return body
}

async function declineHostInvite() {
    const res = await fetch(`${window.knovApiUrl}/api/v1/host_grants/decline`, {
        method: 'POST',
        headers: { ...accessTokens(), ...commonHeaders() },
    })
    if (abort401(res)) return
    const body = await res.json()
    if (!res.ok) throw new Error(body?.error || 'Failed to decline host invite')
    return body
}

// --- Admin: platform ops console (platform admin only) ---

// `refresh` bypasses the server's short snapshot cache (the Refresh button).
async function getOpsJson(path: string, { refresh = false } = {}) {
    const query = refresh ? '?refresh=1' : ''
    const res = await fetch(`${window.knovApiUrl}/api/v1/admin/ops/${path}${query}`, {
        headers: { ...accessTokens(), ...commonHeaders() },
    })
    if (abort401(res)) return
    const body = await res.json().catch(() => null)
    if (!res.ok) throw new Error(body?.error || `Failed to load ${path.replace('_', ' ')}`)
    return body
}

async function getOpsWallets(options?: { refresh?: boolean }) {
    return getOpsJson('wallets', options)
}

async function getOpsAutofund(options?: { refresh?: boolean }) {
    return getOpsJson('autofund', options)
}

async function getOpsErrors(options?: { refresh?: boolean }) {
    return getOpsJson('errors', options)
}

async function getOpsApiTokens(options?: { refresh?: boolean }) {
    return getOpsJson('api_tokens', options)
}

const toExport = {
    getMyHostGrant,
    acceptHostInvite,
    declineHostInvite,
    getHostGrants,
    getHostCandidates,
    inviteHost,
    ensureHostInviteArtifact,
    resendHostInvite,
    revokeHostGrant,
    getOpsWallets,
    getOpsAutofund,
    getOpsErrors,
    getOpsApiTokens,
    // Api.
    createQuest,
    deleteQuest,
    duplicateQuest,
    starQuest,
    unstarQuest,
    postThread,
    transcribeAnswer,
    upvoteAnswer,
    votePoll,
    getQuests,
    getQuest,
    getQuestQuery,
    getQuestAiInputMediaTree,
    getUser,
    getUserByName,
    updateQuestPerms,
    getCommentQuest,
    getQuestQuotes,
    updateQuest,
    getAnswer,
    refreshAnswerPayloadMedia,
    getAnswerResult,
    getAnswers,
    getAnswersWithMeta,
    getReadableApiErrorMessage,
    createAnswer,
    deleteAnswer,
    updateAnswer,
    swapAnswer,
    attachVideo,
    deleteEmbed,
    getHistory,
    getPeopleChats,
    getStarredSidebarQuests,
    followUser,
    unfollowUser,
    openDirectMessage,
    answerMessageRequest,
    getMessageRequestCount,
    getUserEarnings,
    getFollowers,
    getFollowing,
    getSuggestedFollows,
    getNotificationsCount,
    getNotificationsCounts,
    getUsers,
    getSpace,
    getSpaces,
    getUpvaluesMetrics,
    getUpvalueLeaderboard,
    getPostsLeaderboard,
    getAiFeesLeaderboard,
    getMintersLeaderboard,
    getHostsLeaderboard,
    getShareRewardsLeaderboard,
    getAiUsageByTypeMetrics,
    getAiUsageByModelMetrics,
    getPostsMetrics,
    getSignupsMetrics,
    getAllMetrics,
    getBsvFeeCharges,
    getBsvPayments,
    cancelBsvPayment,
    retryBsvPayment,
    getFeeChargePnlMetrics,
    getAiModels,
    getActionDefinitions,
    // Util.
    commonHeaders,
    getLinkPreview,
    getQueryString,
    abortQuestApiCall,
    actOnNoti,
    mergeAnswers,
    // Actions.
    userViewsQuest,
    userViewsBranch,
    pinQuest,
    unpinQuest,
    updateUser,
    deleteAccount,
    userSearchAction,
    updateUserOptions,
    updateUserSpaceOptions,
    updateTeam,
    addToTeam,
    acceptTeamInvite,
    rejectTeamInvite,
    removeFromTeam,
    leaveTeam,
    getTeams,
    getPublicTeams,
    getPublicTeam,
    getTeam,
    getTeamCardStats,
    createSpaceInvitation,
    getSpaceInvitationByToken,
    acceptSpaceInvitation,
    deleteSpaceInvitation,
    removeFromSpace,
    updateSpace,
    getGon,
    getAgentModelConfig,
    clipLink,
    fetchRecentThreads,
    getBsvLockAmtForAnswer,
    getBsvLocks,
    lockAnswer,
    getBsvBalance,
    getBsvBalanceCached,
    getBsvFeeRate,
    getBsvAddress,
    postToBsv,
    sendBsv,
    createOrdinalWalletAction,
    getOrdinalWalletAction,
    getOrdinalWalletUsers,
    signOrdinalWalletAction,
    abortOrdinalWalletAction,
    sendTreechatFee,
    sendUpvalue,
    sendReaction,
    removeReaction,
    ensureBsvWallet,
    downloadBsvWallet,
    getBsvServerShare,
    getBsvUtxos,
    registerBsvServerShare,
    createBsvDeviceTransferSession,
    getBsvDeviceTransferSession,
    acceptBsvDeviceTransferSession,
    confirmBsvDeviceTransferSessionSas,
    uploadBsvDeviceTransferPayload,
    getBsvDeviceTransferPayload,
    activateBsvDeviceTransferSession,
    releaseBsvDeviceTransferSession,
    cancelBsvDeviceTransferSession,
    getSigningRequests,
    submitSignedTx,
    reportSigningFailure,
    getSigningRequestMarketContext,
    openSigningRequestOrdinalPrevoutScript,
    submitOrdinalSignatures,
    broadcastBsvTx,
    createLockTx,
    getUserByPandaPubkey,
    setPandaPubkey,
    muteUser,
    unmuteUser,
    muteQuest,
    unmuteQuest,
    muteStream,
    unmuteStream,
    getMutedStreams,
    deleteProvider,
    createFragmentWalletTx,
    getBlockHeight,
    getBsvExchangeRate,
    getBsvUpvalueHistory,
    getBsvUpvalueTotal,
    getWalletSeeds,
    getDailyEarningsToday,
    createDailyEarningsShare,
    ensureDailyEarningsBackingPost,
    promoteDailyEarningsPost,
    getDailyEarningsShare,
    getBsvTdp,
    getBsvTdpStats,
    getBsvTdpShareImage,
    createStripeAiBillingCheckoutSession,
    setStripeAiAutoReload,
    syncStripeAiBillingCheckoutSession,
    getStripeAiBillingStatus,
    setStripeAiPaymentMode,
    guardarianEstimate,
    guardarianCreateTransaction,
    guardarianGetTransaction,
    guardarianListTransactions,
    toggleUserFeature,
    getUserFeatures,
    postHelloMessage,
    getBsvAddressForUser,
    getAnswerPaymentReceipts,
    mintOrdinalTemporal,
    writeAttachmentOnchain,
    getOrdinalMintFeeQuote,
    getMintMediaInfo,
    getMintMediaContentChunk,
    sendOrdinalTemporal,
    getTemporalWorkflowStatus,
    listOrdinals,
    getWalletOrdinalsSnapshot,
    toggleOrdinalShowInProfile,
    toggleOrdinalHidden,
    ordinalsMediaStream,
    getOrdinalProfileOwners,
    getOrdinalMarketState,
    getOrdinalMarketStateFor,
    getOrdinalStates,
    getOrdinalOrders,
    createOrdinalOrder,
    cancelOrdinalOrder,
    withdrawOrdinalOrder,
    declineOrdinalOrder,
    settleOrdinalOrder,
    getMyOrdinalOrders,
    ensureOrdinalThread,
    createMintSource,
    getOrdinalProvenance,
    translateAnswer,
    runRaffle,
    finalizeRaffle,
    retryImageGeneration,
    clearImageGenerationError,
    setAnswerIsMusic,
    hideTeam,
    unhideTeam,
    pinTeam,
    unpinTeam,
    updateTeamSeedSelection,
    updateTeamBanner,
    getTeamInvitePreview,
    joinTeam,
    setDefaultHomeStream,
    storeTwitterPKCE,
    tweetQuest,
    resolveUrl,
    refreshTweetEmbedMedia,
    updateFilePreview,
    getAnonymousShareLink,
    getAnswerByAnonymousShareToken,
    listOpenclawIntegrations,
    registerOpenclawIntegration,
    removeOpenclawIntegration,
    removeAllOpenclawIntegrations,
    setOpenclawIntegrationEnabled,
    rotateOpenclawIntegrationSecret,
    preuploadAnswerMedia,
    preuploadAnswerImage,
    preuploadAnswerImages,
    purgePreuploadedBlob,
}

window.openclaw = {
    listIntegrations: listOpenclawIntegrations,
    registerIntegration: registerOpenclawIntegration,
    removeIntegration: removeOpenclawIntegration,
    removeAllIntegrations: removeAllOpenclawIntegrations,
    setIntegrationEnabled: setOpenclawIntegrationEnabled,
    rotateIntegrationSecret: rotateOpenclawIntegrationSecret,
}

const syncWindowApiExposure = (debugEnabled = DEBUG()) => {
    if (debugEnabled) {
        window.api = toExport
        return
    }

    delete window.api
}

window.addEventListener(DEBUG_MODE_CHANGED_EVENT, event => {
    const debugEnabled = Boolean(
        (event as CustomEvent<{ debugEnabled?: boolean }>).detail?.debugEnabled,
    )
    syncWindowApiExposure(debugEnabled)
})

syncWindowApiExposure()

export default toExport
